import { useEffect, useRef, useCallback, useState } from 'react'
import * as PIXI from 'pixi.js'
import { IconButton } from '@arcanecircle/ui/grimoire/icon-button'
import { useVTTStore, PING_TTL_MS } from '@/store'
import { shallow } from 'zustand/shallow'
import { gridToPixel, pixelToGrid, clampToMapBounds, hexToPixiColor } from '@/utils'
import { canUseBarrierTool, canUseFogTool } from '@/utils/barrierPermissions'
import { computeCenterOffset } from '@/utils/viewport'
import { isMovementBlocked, screenToWorld, marqueeHitTest, clientToScreen } from '@/utils/geometry'
import {
  classifyLeftPress,
  shouldPanForButton,
  shouldPlaceForButton,
  PING_LONG_PRESS_MS,
  PING_MOVE_TOLERANCE_PX,
} from '@/utils/pointerInput'
import { TokenTooltip } from '@/components/TokenTooltip'
import { TokenStatusPopover } from '@/components/TokenStatusPopover'
import { HpEditPopover } from '@/components/HpEditPopover'
import { RulerOverlay, rulerColorForUser } from '@/components/RulerOverlay'
import { Toolbar } from '@/components/Toolbar'
import { useRuler, snapToGridCenter, type RulerCoord } from '@/hooks/useRuler'
import { socketService } from '@/services/socket'
import type { Token } from '@/types'
import { resolveTokenDisclosure } from '@/lib/tokenVisibility'
import { normalizeStatuses, resolveStatus } from '@/lib/statusRegistry'
import type { VTTCharacterData, TokenClassification } from '@/services/characterService'
import {
  shouldShowHPBar,
  drawHPPill,
  createHPPillText,
  HP_PILL_HEIGHT,
  HP_PILL_WIDTH,
} from '@/render/drawHPBar'
import { drawStatusChips } from '@/render/drawStatusChips'
import { statusIconName, lightningGripTexture } from '@/render/statusIcons'
import { useCharacterDrag } from '@/hooks/useCharacterDrag'

/** Audited 2026-05-14 against existing handleKeyDown in VTTCanvas: R is unused
 *  (only Arrow keys, B for barrier, Escape, Backspace, Shift, Delete are bound).
 *  See spec section "Keybinding conflict". If a future handler claims R, change
 *  this to 'm' and update Toolbar.rulerKeyHint. */
const RULER_TOGGLE_KEY = 'r'

export interface VTTCanvasProps {
  /**
   * Optional callback when token is moved (for WebSocket sync)
   * If waypoints are provided, the token should animate through them sequentially
   * Phase 3c: Added instant flag for keyboard moves
   */
  onTokenMove?: (tokenId: string, x: number, y: number, waypoints?: Array<{ x: number; y: number }>, instant?: boolean) => void;
  /**
   * Optional callback when barrier is created (for WebSocket sync)
   */
  onBarrierCreate?: (mapId: string, points: Array<{ x: number; y: number }>, closed?: boolean) => void;
  /**
   * Optional callback when barrier is deleted (for WebSocket sync)
   */
  onBarrierDelete?: (barrierId: string) => void;
  /**
   * Optional callback when fog is revealed (for WebSocket sync)
   */
  onFogReveal?: (mapId: string, cells: Array<{ x: number; y: number }>) => void;
  /**
   * Optional callback when fog is hidden (for WebSocket sync)
   */
  onFogHide?: (mapId: string, cells: Array<{ x: number; y: number }>) => void;
  /**
   * Current user's ID (for ownership checks)
   */
  currentUserId?: string;
  /**
   * Current user's role (for permission checks)
   */
  userRole?: 'gm' | 'player' | 'observer';
  /**
   * Duration of token movement animation in seconds (default: 1)
   */
  animationDuration?: number;
  /**
   * Optional callback to get character data for a token
   * Used to display character stats in token tooltip
   */
  getCharacterData?: (characterId: string) => VTTCharacterData | null;
  /**
   * Hydrate a linked character whose HP isn't available yet (#161 follow-up).
   * Called from the token-draw effect when a token references a characterId
   * that getCharacterData can't resolve to usable HP — e.g. the initial session
   * roster fetch hasn't merged that character into state yet. App wires this to
   * the character hook's per-character refetch, which on resolve updates the
   * `characters` state and re-runs this effect so the HP pill renders WITHOUT
   * the player having to open the sheet. Mirrors the on-spawn refresh path
   * (onCharacterNeedsRefresh); the callee dedupes in-flight fetches.
   */
  onCharacterHydrate?: (characterId: string) => void;
  /**
   * Owner-or-GM: edit a linked character's current HP from the on-canvas HP
   * bar (issue #135). Same handler the selected-token HUD uses; `set` is
   * absolute, `delta` is relative ±.
   */
  onHpEdit?: (
    characterId: string,
    change: { set: number } | { delta: number },
    tokenId?: string,
  ) => void;
  /**
   * Optional callback to spawn a token from a party-roster drag-and-drop.
   * Fired with grid coordinates resolved from the drop position.
   */
  onSpawnToken?: (payload: {
    mapId: string;
    name: string;
    x: number;
    y: number;
    characterId?: string;
    imageUrl?: string;
    ownerUserId?: string;
  }) => void;
  /**
   * Optional callback to bulk-delete tokens (multi-select Delete /
   * Backspace on canvas). Emits a single `tokens_delete` socket
   * event under the hood; the server enforces the per-token
   * permission gate (GM may delete any, an owner
   * may delete tokens they own) and drops the IDs the caller isn't
   * allowed to remove. Falls back to a single `token_delete` emit
   * when only one token is selected.
   */
  onTokensDelete?: (tokenIds: string[]) => void;
  /**
   * Optional callback to disconnect a token from a shared mob character
   * (GM-only). Clones the character on the platform and repoints the token
   * to the clone so this instance is no longer shared. Wired through to
   * the right-click context menu's Disconnect button.
   */
  onTokenDisconnect?: (tokenId: string) => void;
  /**
   * Optional GM-only callback to re-classify a token's linked character
   * (player / friendly / enemy — VTT #163). Wired through to the right-click
   * context menu's segmented classification toggle. Writes the character on
   * the platform and broadcasts so the bank panel re-buckets and the enemy
   * outline updates for everyone.
   */
  onClassificationChange?: (
    characterId: string,
    classification: TokenClassification
  ) => void;
}

/**
 * Animation state for a token
 */
interface TokenAnimation {
  fromX: number
  fromY: number
  toX: number
  toY: number
  startTime: number
  duration: number // in milliseconds
  waypoints?: Array<{ x: number; y: number }> // Remaining waypoints to animate through
  currentWaypointIndex?: number // Index of current waypoint being animated to
}

/**
 * Main VTT Canvas Component
 * Renders the grid and tokens using PixiJS with full drag-and-drop support
 *
 * Phase 1-2 Features:
 * - Grid rendering with configurable size
 * - Token rendering as colored circles with labels
 * - Interactive token dragging with mouse/touch
 * - Ghost preview showing token position while dragging
 * - Grid snapping on token drop
 * - Selection highlights for active token
 * - Smooth 60fps rendering
 * - WebSocket sync support (Phase 2)
 *
 * Architecture:
 * - Uses PixiJS layers (grid, tokens, ghost, selection)
 * - Drag state tracked in refs for performance
 * - Store updated only on drag completion
 * - Optimistic UI updates during drag
 * - Optional WebSocket callback for network sync
 */

// Grimoire selection palette (PIXI needs numeric hex; values from the design
// tokens in packages/ui/src/styles/tokens.css):
//   --ac-gm-gold    43 70% 60%  -> 0xe0b852  (highlights / non-interactive borders)
//   --ac-mint-primary 172 85% 53% -> 0x21edd2 (hover / interactive affordances)
const SEL_GOLD = 0xe0b852 // selection ring (non-interactive highlight border)
const SEL_VIEWONLY = 0xc0563f // muted red-clay for view-only tokens
const HANDLE_DARK = 0x19103d // Figma dark chip behind a grip icon (#19103D)
//   --ac-danger     0 72% 58%   -> 0xe14747  (status red / destructive)
// Always-on outline ring drawn around tokens classified `enemy` (VTT #163).
const ENEMY_RED = 0xe14747 // Grimoire status red (--ac-danger), enemy token ring

export function VTTCanvas({
  onTokenMove,
  onBarrierCreate,
  onBarrierDelete,
  onFogReveal,
  onFogHide,
  currentUserId,
  userRole,
  animationDuration = 1, // default 1 second
  getCharacterData,
  onCharacterHydrate,
  onHpEdit,
  onSpawnToken,
  onTokensDelete,
  onTokenDisconnect,
  onClassificationChange,
}: VTTCanvasProps = {}) {
  // DOM and PixiJS references
  const canvasRef = useRef<HTMLDivElement>(null)
  const appRef = useRef<PIXI.Application | null>(null)
  const backgroundLayerRef = useRef<PIXI.Container | null>(null)
  const backgroundSpriteRef = useRef<PIXI.Sprite | null>(null) // Phase 6: background image (color, masked to grid)
  const backgroundGreyscaleRef = useRef<PIXI.Sprite | null>(null) // desaturated underlay shown beyond the grid
  const darkvisionGreyRef = useRef<PIXI.Sprite | null>(null) // desaturated map shown in darkvision-revealed unlit areas
  const darkvisionGreyMaskRef = useRef<PIXI.Graphics | null>(null) // mask = darkvision LoS minus lit regions
  const backgroundMaskRef = useRef<PIXI.Graphics | null>(null) // grid-rect mask clipping the color sprite
  const backgroundBorderRef = useRef<PIXI.Graphics | null>(null) // stroke at the play-area (grid) edge
  const gridLayerRef = useRef<PIXI.Graphics | null>(null) // grid lines, redrawn reactively on dimension/grid edits
  const tokensContainerRef = useRef<PIXI.Container | null>(null)
  const ghostLayerRef = useRef<PIXI.Graphics | null>(null)
  const selectionLayerRef = useRef<PIXI.Graphics | null>(null)
  // Interactive selection handles (rotate, resize) — a 'passive' container
  // whose children are eventMode='static' with their own pointer handlers,
  // mirroring the barrierLayer pattern. Kept separate from the non-interactive
  // selectionLayer halo (eventMode='none') because a single Graphics can't take
  // per-shape pointer events.
  const selectionHandlesRef = useRef<PIXI.Container | null>(null)
  // True while the rotate grip is being dragged — suppresses the grip's
  // hover-scale on handle rebuilds mid-drag (the effect re-runs on every
  // live rotation update).
  const rotatingRef = useRef(false)
  // True while a bottom-right resize-grip drag is in flight, so the grip's
  // hover-scale doesn't fight the live token rescale.
  const resizingRef = useRef(false)
  // Dashed rubber-band rectangle drawn over the canvas while the user
  // is mid-drag-selecting. Lives above the selection halos so the
  // rect outline isn't covered by them. Cleared on mouseup / Escape.
  const rubberBandLayerRef = useRef<PIXI.Graphics | null>(null)
  const fogLayerRef = useRef<PIXI.Graphics | null>(null) // Phase 4: manual fog
  const darknessLayerRef = useRef<PIXI.Graphics | null>(null) // Phase 3b: dynamic LOS darkness
  const dimLayerRef = useRef<PIXI.Graphics | null>(null) // Lighting v2: dim-band shading (mid alpha, above darkness)
  const lightTintLayerRef = useRef<PIXI.Graphics | null>(null) // Lighting v2: additive colour tint (below darkness)
  const lightSourceLayerRef = useRef<PIXI.Container | null>(null) // Dynamic lighting: GM-placed light source objects (interactive)
  // True between a light object's pointerdown and pointerup, so the stage
  // placement handler doesn't spawn a new light when you tap/drag an existing one.
  const lightPointerDownRef = useRef(false)
  // Touch double-tap detection for opening the token context menu (there's no
  // right-click on touch). Records the last token tap; a second tap on the same
  // token within the window opens its tooltip.
  const lastTokenTapRef = useRef<{ id: string; t: number; x: number; y: number } | null>(null)
  const brushPreviewLayerRef = useRef<PIXI.Graphics | null>(null) // Phase 4: brush cursor preview
  const pingLayerRef = useRef<PIXI.Graphics | null>(null) // Transient ping pulses, above tokens

  // Track when canvas is ready (state to trigger re-renders)
  const [canvasReady, setCanvasReady] = useState(false)

  // Token tooltip state
  const [tooltipState, setTooltipState] = useState<{
    token: Token
    position: { x: number; y: number }
  } | null>(null)

  // On-token status-picker popover state (lightning grip). Opened by tapping the
  // lightning control on a selected token; floats a TokenStatusPopover at the
  // grip's screen position. `tokenId` ties the open popover to its token so a
  // selection change can be detected and the popover dismissed.
  const [statusPopover, setStatusPopover] = useState<{
    tokenId: string
    position: { x: number; y: number }
  } | null>(null)

  // On-canvas HP editor state (issue #135). Opened by clicking a token's HP
  // pill; floats an input at the pill's screen position. `position` is in
  // canvas-viewport pixels (PIXI global), matching the viewport overlay box.
  const [hpEditState, setHpEditState] = useState<{
    characterId: string
    tokenId: string
    current: number
    max: number
    position: { x: number; y: number }
  } | null>(null)

  // Viewport state for pan and zoom
  const viewportRef = useRef({
    scale: 1,
    x: 0,
    y: 0,
  })

  // Touch state for multi-touch gestures
  const touchStateRef = useRef<{
    initialDistance: number | null
    initialScale: number | null
    initialMidpoint: { x: number; y: number } | null
    initialOffset: { x: number; y: number } | null
  }>({
    initialDistance: null,
    initialScale: null,
    initialMidpoint: null,
    initialOffset: null,
  })

  // Token animations (for smooth remote movements)
  const animationsRef = useRef<Map<string, TokenAnimation>>(new Map())

  // Track previous token positions to detect changes
  const previousPositionsRef = useRef<Map<string, { x: number; y: number }>>(new Map())

  // Track keyboard moves for fast animation
  const keyboardMoveRef = useRef<Set<string>>(new Set())

  // Map of tokenId to PIXI.Container for direct position updates
  const tokenContainersRef = useRef<Map<string, PIXI.Container>>(new Map())

  // HP pill containers indexed by tokenId (null = no pill for that token).
  // Shared between the token render loop (creates) and the selection/hover
  // handlers (toggle visibility).
  const tokenHpPillsRef = useRef<Map<string, PIXI.Container | null>>(new Map())

  // Tokens currently under the pointer — used to keep HP pills visible when
  // a token is hovered but not yet selected.
  const hoveredTokenIdsRef = useRef<Set<string>>(new Set())

  // Tokens whose HP pill stays visible without hover/selection — the viewer's
  // own tokens. The owner always sees their own HP bar (VTT #136), so the
  // hover/select gate doesn't apply to it. Rebuilt each render pass.
  const persistentHpPillIdsRef = useRef<Set<string>>(new Set())

  // characterIds we've already asked App to hydrate (HP missing at draw time,
  // #161 follow-up). Prevents re-firing a refetch every render pass for a
  // character that's still loading. Cleared when its data arrives via the
  // redraw, so a later cache-eviction can re-request.
  const hydrateRequestedRef = useRef<Set<string>>(new Set())

  // Bumped on every token-render pass. Async token-image loads capture the
  // generation they started in and bail on resolve if a newer pass has
  // superseded them, so a late texture never lands in a stale container.
  const tokenRenderGenRef = useRef(0)

  // Barrier placement state (Phase 3)
  const barrierVerticesRef = useRef<Array<{ x: number; y: number }>>([])
  const barrierLayerRef = useRef<PIXI.Graphics | null>(null)
  const barrierGraphicsRef = useRef<Map<string, PIXI.Graphics>>(new Map())

  // Shift+click waypoint queue: pending grid cells accumulated before commit (Bug 2).
  // Each shift+click on the stage appends a cell; a non-shift click or Enter commits
  // the path; Escape or clicking a token clears it.
  const pendingWaypointsRef = useRef<Array<{ x: number; y: number }>>([])

  // Phase 4: Fog brush state
  const fogBrushStateRef = useRef<{
    isDrawing: boolean
    lastGridX: number | null
    lastGridY: number | null
    accumulatedCells: Set<string> // Set of "x,y" strings
    lastUpdateTime: number
  }>({
    isDrawing: false,
    lastGridX: null,
    lastGridY: null,
    accumulatedCells: new Set(),
    lastUpdateTime: 0,
  })

  // Phase 3c: Last-seen token tracking (for player fog-of-war visibility)
  const lastSeenTokensRef = useRef<Map<string, {
    x: number
    y: number
    color: string
    name: string
    width: number
    height: number
  }>>(new Map())

  // Phase 3c: Track current visibility state (to detect transitions)
  const visibilityStateRef = useRef<Map<string, boolean>>(new Map())

  // Drag state (tracked in refs for performance)
  const dragStateRef = useRef<{
    isDragging: boolean
    tokenId: string
    token: Token
    offsetX: number // Offset from token origin to click point
    offsetY: number
    startX: number // Starting pointer position for drag threshold
    startY: number
    hasMoved: boolean // Whether drag threshold has been exceeded
    waypoints: Array<{ x: number; y: number }> // Grid coordinates of waypoints
    shiftPressed: boolean
    // When the dragged token is part of a multi-token selection, the
    // other selected tokens move alongside it by the same (dx, dy).
    // Snapshot of their starting grid positions, captured at
    // pointerdown so per-frame ghosts can compute the offset cheaply
    // and the drop step can compute final positions without re-
    // querying the store.
    bulkMembers: Array<{ id: string; startX: number; startY: number; width: number; height: number; canMove: boolean }>
  } | null>(null)

  // Rubber-band selection state. Set on pointerdown over an empty
  // canvas spot (no token under cursor, no tool mode that owns the
  // pointer), updated each pointermove, committed to the store on
  // pointerup or cleared on Escape. Coordinates are in PIXI canvas
  // pixels — converted to grid space at commit time.
  const rubberBandRef = useRef<{
    isActive: boolean
    startX: number
    startY: number
    currentX: number
    currentY: number
    shiftHeld: boolean // when held, union with prior selection
    baseSelection: string[] // selection at drag-start (for shift-union)
  } | null>(null)

  // Long-press ping tracking. Set on a left pointerdown over EMPTY canvas (in
  // select mode): a timer fires the ping after PING_LONG_PRESS_MS if the
  // pointer hasn't moved past tolerance and the press hasn't been released.
  // Movement past tolerance cancels the timer and the rubber-band takes over;
  // an early release cancels it and the press resolves as a normal click.
  // World coords are captured at press-down so the ping lands where the
  // cursor was, independent of any pan that happens mid-hold.
  const pingPressRef = useRef<{
    timer: ReturnType<typeof setTimeout> | null
    startScreenX: number
    startScreenY: number
    worldX: number
    worldY: number
    fired: boolean
  } | null>(null)

  // Get state and actions from store (Phase 6: use selectors with shallow comparison)
  const map = useVTTStore(state => state.maps.find(m => m.id === state.activeMapId) || null, shallow)
  const tokens = useVTTStore(state => state.tokens.filter(t => t.mapId === state.activeMapId), shallow)
  const barriers = useVTTStore(state => state.barriers.filter(b => b.mapId === state.activeMapId), shallow)
  const lightSources = useVTTStore(state => state.lightSources.filter(ls => ls.mapId === state.activeMapId), shallow)
  const selectedLightId = useVTTStore(state => state.selectedLightId)
  const setSelectedLightId = useVTTStore(state => state.setSelectedLightId)
  const updateLightSourceInStore = useVTTStore(state => state.updateLightSourceInStore)

  const { selectedTokenIds, moveToken, selectToken, toggleSelectedTokenId, tokenWaypoints, clearWaypoints, instantMoves, clearInstantMove, toolMode, addBarrier, previewingVisionFor, setPreviewingVisionFor, selectedBarrierId, selectBarrier, removeBarrier, fog, visionPolygon, setVisionPolygon, visionNeedsRecalculation, remoteRulers, clearRemoteRulers, clearPings, ownHpBarHidden } = useVTTStore()

  // "See as token": when previewingVisionFor is set, the GM views the board
  // from that one token's perspective — fog + dynamic line-of-sight rendered
  // exactly as a player standing at that token would see it. The viewer role
  // becomes 'player' and the active vision polygon is that single token's
  // raycasted vision (computed by the darkness effect below into
  // viewAsVisionPolygon). When not previewing, both fall back to the real
  // GM/player view.
  const [viewAsVisionPolygon, setViewAsVisionPolygon] = useState<
    Array<{ x: number; y: number }> | null
  >(null)
  const effectiveRole = previewingVisionFor ? 'player' : userRole
  const effectiveVisionPolygon = previewingVisionFor
    ? viewAsVisionPolygon
    : visionPolygon

  // Ruler tool (cross-role measurement). Hook is called unconditionally
  // so it's stable across map-null transitions.
  const ruler = useRuler()
  const measurementShape = useVTTStore((s) => s.measurementShape)

  // Mirror of viewportRef.current into state, polled via rAF while ruler is
  // active. Lets the SVG overlay re-position on pan/zoom without forcing the
  // rest of the canvas to re-render. Defaults match initial viewportRef.
  const [viewportSnapshot, setViewportSnapshot] = useState<{ scale: number; x: number; y: number }>({ scale: 1, x: 0, y: 0 })

  // Track canvas DOM element size for the absolutely-positioned overlay.
  const [canvasSize, setCanvasSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 })

  // Drop region for the custom character-drag system (see
  // useCharacterDrag). The ref is attached to the same div that wraps
  // the PIXI canvas; the registered handler hit-tests pointer-up coords
  // against the div's rect and, if inside, projects to grid coords via
  // the same screen→world transform the ruler uses.
  const dropRegionRef = useRef<HTMLDivElement | null>(null)
  const { registerDropTarget } = useCharacterDrag()
  useEffect(() => {
    if (!onSpawnToken || !map) return
    return registerDropTarget((clientX, clientY, payload) => {
      const el = dropRegionRef.current
      if (!el) return false
      const rect = el.getBoundingClientRect()
      if (
        clientX < rect.left ||
        clientX > rect.right ||
        clientY < rect.top ||
        clientY > rect.bottom
      ) {
        return false
      }
      const v = viewportRef.current
      // Project the drop point to world space. The viewport (v.x / v.scale)
      // lives in Pixi SCREEN space, so the pointer must first be mapped CSS px
      // → Pixi screen space the same way event.global is — otherwise on a
      // CSS-scaled (mobile) canvas the spawn lands in the wrong cell. Use the
      // canvas element for the scale; it fills the same box as the drop region.
      const a = appRef.current
      const canvas = a?.view as HTMLCanvasElement | undefined
      const screen =
        a && canvas
          ? clientToScreen(canvas, clientX, clientY, a.renderer.resolution)
          : { x: clientX - rect.left, y: clientY - rect.top }
      const worldX = (screen.x - v.x) / v.scale
      const worldY = (screen.y - v.y) / v.scale
      const grid = pixelToGrid(
        worldX - (map.gridOffsetX || 0),
        worldY - (map.gridOffsetY || 0),
        map.gridSize,
      )
      const clamped = clampToMapBounds(grid.x, grid.y, map.width, map.height)
      onSpawnToken({
        mapId: map.id,
        name: payload.name || 'Token',
        x: clamped.x,
        y: clamped.y,
        characterId: payload.characterId,
        imageUrl: payload.imageUrl ?? undefined,
        ownerUserId: payload.ownerUserId,
      })
      return true
    })
  }, [
    registerDropTarget,
    onSpawnToken,
    map,
  ])

  // Early return if no active map (Phase 6)
  if (!map) {
    return (
      <div ref={canvasRef} style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888' }}>
        No active map
      </div>
    )
  }

  /**
   * Check if current user can move a token
   */
  const canMoveToken = useCallback((token: Token): boolean => {
    // Phase 1 mode (no auth) - all tokens moveable
    if (!currentUserId || !userRole) return true
    // GM can move any token
    if (userRole === 'gm') return true
    // Players can only move tokens they own
    if (userRole === 'player' && token.ownerUserId === currentUserId) return true
    // Observers can't move anything
    return false
  }, [currentUserId, userRole])

  // GM toggle for whether players may see a token's HP bar, from the
  // right-click tooltip (the same control also lives in the bottom-left
  // HUD, wired in App). Optimistic store update, then emit token_update;
  // the server validates and broadcasts token_updated.
  const handleTokenHpBarVisibilityChange = useCallback(
    (tokenId: string, hpBarVisibleToPlayers: boolean) => {
      const { tokens: liveTokens, setTokens } = useVTTStore.getState()
      setTokens(
        liveTokens.map((t) =>
          t.id === tokenId ? { ...t, hpBarVisibleToPlayers } : t,
        ),
      )
      socketService.updateToken(tokenId, { hpBarVisibleToPlayers })
    },
    [],
  )

  // GM-only: set a token's player-disclosure — 'visible' (obeys fog/LOS) or
  // 'gm_only' (never shown to players). Optimistic local update, then
  // broadcast; the server gates this to the GM.
  // Plain function (not useCallback): this component already runs its hooks
  // after an early return, so adding another hook would compound that existing
  // lint debt — and the handler reads live store state via getState() anyway.
  const handleTokenVisibilityChange = (
    tokenId: string,
    visibility: 'visible' | 'gm_only',
  ) => {
    const { tokens: liveTokens, setTokens } = useVTTStore.getState()
    setTokens(
      liveTokens.map((t) => (t.id === tokenId ? { ...t, visibility } : t)),
    )
    socketService.updateToken(tokenId, { visibility })
  }

  // Resize a token (width/height in grid squares). Optimistic local update,
  // then broadcast; the server validates and gates the update (owner-or-GM).
  const handleTokenSizeChange = (
    tokenId: string,
    width: number,
    height: number,
  ) => {
    const { tokens: liveTokens, setTokens } = useVTTStore.getState()
    setTokens(
      liveTokens.map((t) => (t.id === tokenId ? { ...t, width, height } : t)),
    )
    socketService.updateToken(tokenId, { width, height })
  }

  // Rotate a token (degrees clockwise, 0-359). Optimistic local update, then
  // broadcast; the server validates and gates the update (owner-or-GM).
  const handleTokenRotationChange = (tokenId: string, rotation: number) => {
    const normalized = ((Math.round(rotation) % 360) + 360) % 360
    const { tokens: liveTokens, setTokens } = useVTTStore.getState()
    setTokens(
      liveTokens.map((t) => (t.id === tokenId ? { ...t, rotation: normalized } : t)),
    )
    socketService.updateToken(tokenId, { rotation: normalized })
  }

  // Dismiss the lightning status popover when its token stops being the sole
  // selection (the grip that anchors it is only drawn for a single selected,
  // editable token), so it never dangles over a deselected token.
  useEffect(() => {
    setStatusPopover((prev) => {
      if (!prev) return prev
      const stillSole =
        selectedTokenIds.length === 1 && selectedTokenIds[0] === prev.tokenId
      return stillSole ? prev : null
    })
  }, [selectedTokenIds])

  // Set a token's status/condition keys. Optimistic local update, then
  // broadcast; the server validates and gates the update (owner-or-GM).
  const handleTokenStatusesChange = (tokenId: string, statuses: string[]) => {
    const normalized = normalizeStatuses(statuses)
    const { tokens: liveTokens, setTokens } = useVTTStore.getState()
    setTokens(
      liveTokens.map((t) => (t.id === tokenId ? { ...t, statuses: normalized } : t)),
    )
    socketService.updateToken(tokenId, { statuses: normalized })
  }

  // Set the leader this token follows (or null to clear). Optimistic local
  // update, then broadcast; the server gates it (owner-or-GM) and rejects
  // self/cycle/cross-map links.
  const handleTokenFollowChange = (tokenId: string, followsTokenId: string | null) => {
    const { tokens: liveTokens, setTokens } = useVTTStore.getState()
    setTokens(
      liveTokens.map((t) => (t.id === tokenId ? { ...t, followsTokenId } : t)),
    )
    socketService.updateToken(tokenId, { followsTokenId })
  }

  // Update a token's light settings (lightRadius and/or torchActive).
  // Optimistic local update + broadcast. The server gates lightRadius changes
  // to GM; torchActive to owner-or-GM. Only the provided keys are updated.
  const handleTokenLightChange = (
    tokenId: string,
    updates: { lightRadius?: number | null; torchActive?: boolean },
  ) => {
    const { tokens: liveTokens, setTokens } = useVTTStore.getState()
    setTokens(liveTokens.map((t) => (t.id === tokenId ? { ...t, ...updates } : t)))
    socketService.updateToken(tokenId, updates)
  }

  // Set a token's vision-type flags (lowLightVision and/or darkvision).
  // Optimistic local update + broadcast. The server gates these to GM. Only the
  // provided keys are updated. Mirrors handleTokenLightChange.
  const handleTokenVisionTypeChange = (
    tokenId: string,
    updates: { lowLightVision?: boolean; darkvision?: boolean },
  ) => {
    const { tokens: liveTokens, setTokens } = useVTTStore.getState()
    setTokens(liveTokens.map((t) => (t.id === tokenId ? { ...t, ...updates } : t)))
    socketService.updateToken(tokenId, updates)
  }

  /**
   * Phase 3c: Check if a token is visible to the current player
   * GMs always see all tokens
   * Players see tokens based on fog/LOS rules, BUT they always see
   * the tokens they own — otherwise a player whose own token sits in
   * fog can't click or drag it back out.
   */
  const isTokenVisible = useCallback((token: Token): boolean => {
    // "See as token": always render the token we're viewing as (it IS the
    // viewpoint). Every other token is then judged from a player's
    // perspective using that token's own vision — userId null so there's no
    // owner bypass: this is a single token's view, not its player's whole
    // party.
    if (previewingVisionFor && token.id === previewingVisionFor) return true

    // Ownership / GM-disclosure decision first. Resolves GM (sees all),
    // owned tokens (always seen), and GM-only tokens (never seen by players).
    // Every other token — a normal 'visible' token — falls through to the
    // fog/LOS geometry test below, so it is occluded by walls and unexplored
    // fog exactly like the map's vision rules dictate (ac-vtt#131/#132).
    const disclosure = resolveTokenDisclosure(token, {
      role: effectiveRole,
      userId: previewingVisionFor ? null : currentUserId,
    })
    if (disclosure === 'shown') return true
    if (disclosure === 'hidden') return false

    // Vision layers STACK. Fog-of-war is always active; dynamic line-of-sight
    // is an additional layer applied only when losEnabled. A token is visible
    // only if EVERY active layer would show it (hidden by either = hidden).
    const losEnabled = map.losEnabled === true

    // Fog layer: token visible if ALL occupied cells are revealed.
    const fogShows = (() => {
      const cells: Array<{ x: number; y: number }> = []
      for (let dy = 0; dy < token.height; dy++) {
        for (let dx = 0; dx < token.width; dx++) {
          cells.push({ x: token.x + dx, y: token.y + dy })
        }
      }
      return cells.every((cell) => fog.get(`${cell.x},${cell.y}`) === true)
    })()
    if (!fogShows) return false

    // LoS layer (only when enabled): token visible if ANY occupied cell is
    // inside the computed vision polygon (the party's, or — in see-as-token
    // mode — the single viewed token's).
    if (losEnabled) {
      if (!effectiveVisionPolygon || effectiveVisionPolygon.length === 0) {
        // LoS on but no polygon calculated yet — hide everything.
        return false
      }

      const cellsInPixels: Array<{ x: number; y: number }> = []
      for (let dy = 0; dy < token.height; dy++) {
        for (let dx = 0; dx < token.width; dx++) {
          const pixelX = (token.x + dx) * map.gridSize + map.gridSize / 2
          const pixelY = (token.y + dy) * map.gridSize + map.gridSize / 2
          cellsInPixels.push({ x: pixelX, y: pixelY })
        }
      }

      const losShows = cellsInPixels.some((point) => {
        // Ray-casting point-in-polygon.
        let inside = false
        const x = point.x
        const y = point.y

        for (let i = 0, j = effectiveVisionPolygon.length - 1; i < effectiveVisionPolygon.length; j = i++) {
          const xi = effectiveVisionPolygon[i].x
          const yi = effectiveVisionPolygon[i].y
          const xj = effectiveVisionPolygon[j].x
          const yj = effectiveVisionPolygon[j].y

          const intersect =
            yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi

          if (intersect) inside = !inside
        }

        return inside
      })
      if (!losShows) return false
    }

    // Survived every active layer.
    return true
  }, [effectiveRole, previewingVisionFor, currentUserId, map.losEnabled, map.gridSize, fog, effectiveVisionPolygon])

  // Keep refs to latest values for use in PixiJS event handlers
  const mapRef = useRef(map)
  // Current user id in a ref so the once-on-mount pointer handlers (e.g. the
  // long-press ping timer) read the live value without re-binding.
  const currentUserIdRef = useRef(currentUserId)
  currentUserIdRef.current = currentUserId
  const moveTokenRef = useRef(moveToken)
  const onTokenMoveRef = useRef(onTokenMove)
  // Bulk-delete callback used by the Delete/Backspace handler. Lives
  // in a ref because the handler is wired inside the once-on-mount
  // PixiJS init effect; the ref-update effect below keeps it fresh.
  const onTokensDeleteRef = useRef(onTokensDelete)
  const onBarrierCreateRef = useRef(onBarrierCreate)
  const onBarrierDeleteRef = useRef(onBarrierDelete)
  const onFogRevealRef = useRef(onFogReveal)
  const onFogHideRef = useRef(onFogHide)
  const renderGhostRef = useRef<typeof renderGhost | null>(null)
  const canMoveTokenRef = useRef(canMoveToken)
  // Token-visibility predicate used by the rubber-band hit test. Lives
  // in a ref so the once-on-mount handler closure can read the latest
  // role / fog state instead of the snapshot from first render.
  const isTokenVisibleRef = useRef(isTokenVisible)
  const toolModeRef = useRef(toolMode)
  const renderBarrierPreviewRef = useRef<typeof renderBarrierPreview | null>(null)

  /**
   * Render barrier placement preview (Phase 3)
   */
  const renderBarrierPreview = useCallback(() => {
    const barrierLayer = barrierLayerRef.current
    if (!barrierLayer) return

    barrierLayer.clear()

    const vertices = barrierVerticesRef.current
    if (vertices.length === 0) return

    // Draw connecting lines
    if (vertices.length > 1) {
      barrierLayer.lineStyle({ width: 4, color: 0xff4444, alpha: 0.8 })

      // Walls snap to grid corners, so vertices render at the corner (no offset).
      const firstPixel = gridToPixel(vertices[0].x, vertices[0].y, map.gridSize)
      barrierLayer.moveTo(firstPixel.x, firstPixel.y)

      for (let i = 1; i < vertices.length; i++) {
        const pixel = gridToPixel(vertices[i].x, vertices[i].y, map.gridSize)
        barrierLayer.lineTo(pixel.x, pixel.y)
      }
    }

    // Draw vertices as red circles
    vertices.forEach((vertex, index) => {
      const pixel = gridToPixel(vertex.x, vertex.y, map.gridSize)
      const centerX = pixel.x
      const centerY = pixel.y

      // Filled circle
      barrierLayer.beginFill(0xff4444, 0.8)
      barrierLayer.drawCircle(centerX, centerY, 6)
      barrierLayer.endFill()

      // White border
      barrierLayer.lineStyle({ width: 2, color: 0xffffff, alpha: 1 })
      barrierLayer.drawCircle(centerX, centerY, 6)

      // Draw vertex number
      const text = new PIXI.Text((index + 1).toString(), {
        fontSize: 10,
        fill: 0xffffff,
        fontWeight: 'bold',
      })
      text.anchor.set(0.5)
      text.x = centerX
      text.y = centerY
      barrierLayer.addChild(text)
    })
  }, [map.gridSize])

  /**
   * Render ghost preview of dragged token with waypoints
   */
  const renderGhost = useCallback(
    (ghostLayer: PIXI.Graphics, token: Token, x: number, y: number, waypoints: Array<{ x: number; y: number }> = []) => {
      ghostLayer.clear()

      const tokenSize = map.gridSize * token.width - 4
      const tokenStartPixel = gridToPixel(token.x, token.y, map.gridSize)
      const centerOffsetX = (map.gridSize * token.width) / 2
      const centerOffsetY = (map.gridSize * token.height) / 2

      // Draw connecting lines and waypoint ghosts
      if (waypoints.length > 0) {
        // Draw line from token start to first waypoint
        ghostLayer.lineStyle({ width: 3, color: 0x3498db, alpha: 0.6 }) // Blue line
        ghostLayer.moveTo(tokenStartPixel.x + centerOffsetX, tokenStartPixel.y + centerOffsetY)

        const firstWaypointPixel = gridToPixel(waypoints[0].x, waypoints[0].y, map.gridSize)
        ghostLayer.lineTo(firstWaypointPixel.x + centerOffsetX, firstWaypointPixel.y + centerOffsetY)

        // Draw waypoint ghosts and connecting lines
        waypoints.forEach((waypoint, index) => {
          const waypointPixel = gridToPixel(waypoint.x, waypoint.y, map.gridSize)

          // Draw blue ghost circle for waypoint
          ghostLayer.beginFill(hexToPixiColor(token.color), 0.3)
          ghostLayer.drawCircle(
            waypointPixel.x + centerOffsetX,
            waypointPixel.y + centerOffsetY,
            tokenSize / 2
          )
          ghostLayer.endFill()

          // Draw blue outline
          ghostLayer.lineStyle({ width: 3, color: 0x3498db, alpha: 0.8 }) // Blue outline
          ghostLayer.drawCircle(
            waypointPixel.x + centerOffsetX,
            waypointPixel.y + centerOffsetY,
            tokenSize / 2
          )

          // Draw line to next waypoint or to cursor
          if (index < waypoints.length - 1) {
            const nextWaypointPixel = gridToPixel(waypoints[index + 1].x, waypoints[index + 1].y, map.gridSize)
            ghostLayer.lineStyle({ width: 3, color: 0x3498db, alpha: 0.6 })
            ghostLayer.moveTo(waypointPixel.x + centerOffsetX, waypointPixel.y + centerOffsetY)
            ghostLayer.lineTo(nextWaypointPixel.x + centerOffsetX, nextWaypointPixel.y + centerOffsetY)
          } else {
            // Draw line from last waypoint to cursor
            ghostLayer.lineStyle({ width: 3, color: 0x3498db, alpha: 0.6 })
            ghostLayer.moveTo(waypointPixel.x + centerOffsetX, waypointPixel.y + centerOffsetY)
            ghostLayer.lineTo(x + centerOffsetX, y + centerOffsetY)
          }
        })
      } else {
        // No waypoints - draw line from token to cursor
        ghostLayer.lineStyle({ width: 2, color: 0xffffff, alpha: 0.3 })
        ghostLayer.moveTo(tokenStartPixel.x + centerOffsetX, tokenStartPixel.y + centerOffsetY)
        ghostLayer.lineTo(x + centerOffsetX, y + centerOffsetY)
      }

      // Draw semi-transparent ghost circle at cursor
      ghostLayer.beginFill(hexToPixiColor(token.color), 0.4)
      ghostLayer.drawCircle(
        x + centerOffsetX,
        y + centerOffsetY,
        tokenSize / 2
      )
      ghostLayer.endFill()

      // Draw border to show it's a ghost
      ghostLayer.lineStyle({ width: 2, color: 0xffffff, alpha: 0.6 })
      ghostLayer.drawCircle(
        x + centerOffsetX,
        y + centerOffsetY,
        tokenSize / 2
      )

      // Add grid snap preview (show which grid square it will snap to)
      const gridPos = pixelToGrid(
        x + centerOffsetX,
        y + centerOffsetY,
        map.gridSize
      )
      const snappedPixel = gridToPixel(gridPos.x, gridPos.y, map.gridSize)

      // Draw snap preview rectangle
      ghostLayer.lineStyle({ width: 2, color: 0xffffff, alpha: 0.3, alignment: 1 })
      ghostLayer.drawRect(
        snappedPixel.x,
        snappedPixel.y,
        map.gridSize * token.width,
        map.gridSize * token.height
      )
    },
    [map.gridSize]
  )

  /**
   * Render selection highlight for selected token
   */
  // Draws one selection halo onto the passed layer. The caller is
  // responsible for clearing the layer before the first call in a
  // render pass — that way the multi-select effect can draw N halos
  // by looping without each call wiping the previous halo.
  const renderSelection = useCallback(
    (selectionLayer: PIXI.Graphics, token: Token) => {
      const pixelPos = gridToPixel(token.x, token.y, map.gridSize)
      const tokenSize = map.gridSize * token.width - 4

      const hCx = pixelPos.x + (map.gridSize * token.width) / 2
      const hCy = pixelPos.y + (map.gridSize * token.height) / 2
      const hRadius = tokenSize / 2

      // Check if user can move this token
      const canMove = canMoveToken(token)

      // Selection ring — a non-interactive highlight border, so Grimoire GOLD
      // when editable (gold for highlights/non-interactive borders), muted red
      // when view-only. The interactive grips (rotate/resize) are mint and live
      // on selectionHandles, drawn separately.
      const ringColor = canMove ? SEL_GOLD : SEL_VIEWONLY
      selectionLayer.lineStyle({ width: 3, color: ringColor, alpha: 0.85 })
      selectionLayer.drawCircle(hCx, hCy, hRadius + 4)
    },
    [map.gridSize, canMoveToken]
  )

  /**
   * Update refs with latest values for PixiJS handlers
   */
  useEffect(() => {
    mapRef.current = map
    moveTokenRef.current = moveToken
    onTokenMoveRef.current = onTokenMove
    onTokensDeleteRef.current = onTokensDelete
    onBarrierCreateRef.current = onBarrierCreate
    onBarrierDeleteRef.current = onBarrierDelete
    onFogRevealRef.current = onFogReveal
    onFogHideRef.current = onFogHide
    renderGhostRef.current = renderGhost
    canMoveTokenRef.current = canMoveToken
    isTokenVisibleRef.current = isTokenVisible
    toolModeRef.current = toolMode
    renderBarrierPreviewRef.current = renderBarrierPreview
  }, [map, moveToken, onTokenMove, onTokensDelete, onBarrierCreate, onBarrierDelete, onFogReveal, onFogHide, renderGhost, canMoveToken, isTokenVisible, toolMode, renderBarrierPreview])

  // Refs to hold drag handler functions (so we can add/remove window listeners)
  const windowHandlersRef = useRef<{
    handleMove: ((event: PointerEvent) => void) | null
    handleUp: ((event: PointerEvent) => void) | null
  }>({ handleMove: null, handleUp: null })

  /**
   * Handle pointer down - start dragging a token
   */
  const handlePointerDown = useCallback(
    (token: Token, event: PIXI.FederatedPointerEvent) => {
      // Ignore right-clicks (they're for the tooltip)
      if (event.button === 2) {
        return
      }

      // In barrier mode, don't allow token dragging
      if (toolMode === 'barrier') {
        console.log('[VTTCanvas] Barrier mode active - token dragging disabled')
        return
      }

      // Close any open tooltip when clicking on a token
      setTooltipState(null)

      // Clear any pending shift+click waypoints when the player clicks a token.
      // Clicking any token (selected or not) signals a change of intent — abort
      // the queued path rather than leaving ghost graphics dangling.
      if (pendingWaypointsRef.current.length > 0) {
        pendingWaypointsRef.current = []
        const ghostLayer = ghostLayerRef.current
        if (ghostLayer) ghostLayer.clear()
      }

      // Selection update.
      //   - shift-click toggles the token in/out of the multi-select
      //     set, and does NOT start a drag (the user is curating the
      //     set, not moving anything).
      //   - plain click on a token already in the selection keeps the
      //     selection intact (so a bulk drag stays bulk).
      //   - plain click on an unselected token replaces the selection
      //     with just that token.
      const priorSelection = useVTTStore.getState().selectedTokenIds
      const wasSelected = priorSelection.includes(token.id)
      if (event.shiftKey) {
        toggleSelectedTokenId(token.id)
        return
      }
      if (!wasSelected) {
        selectToken(token.id)
      }

      // Check if user can move this token
      if (!canMoveToken(token)) {
        // Can't drag this token - just select it
        return
      }

      const pixelPos = gridToPixel(token.x, token.y, map.gridSize)
      const globalPos = event.global
      // The grab offset is the distance between the cursor and the
      // token's top-left corner, measured in world space. `event.global`
      // is screen space, so we apply the inverse viewport transform first
      // — otherwise the offset is only correct at scale 1 / zero pan, and
      // the token jumps away from the cursor at any other zoom (see the
      // screenToWorld conversions in handleMove/handleUp).
      const worldPos = screenToWorld(
        { x: globalPos.x, y: globalPos.y },
        viewportRef.current
      )

      // Snapshot the bulk-drag membership at pointerdown. If the
      // clicked token was already part of a multi-token selection,
      // every selected token moves alongside it by the same (dx, dy)
      // when the drag completes. If the click replaced the selection
      // (single-token case), bulkMembers is empty and the existing
      // single-token drag path runs unchanged.
      const liveSelection = useVTTStore.getState().selectedTokenIds
      const liveTokens = useVTTStore.getState().tokens
      const bulkMembers: Array<{
        id: string
        startX: number
        startY: number
        width: number
        height: number
        canMove: boolean
      }> = []
      if (wasSelected && liveSelection.length > 1) {
        liveSelection.forEach((id) => {
          if (id === token.id) return
          const t = liveTokens.find((x) => x.id === id)
          if (!t || t.mapId !== token.mapId) return
          bulkMembers.push({
            id: t.id,
            startX: t.x,
            startY: t.y,
            width: t.width,
            height: t.height,
            canMove: canMoveToken(t),
          })
        })
      }

      // Store drag state
      dragStateRef.current = {
        isDragging: true,
        tokenId: token.id,
        token,
        offsetX: worldPos.x - pixelPos.x,
        offsetY: worldPos.y - pixelPos.y,
        startX: globalPos.x,
        startY: globalPos.y,
        hasMoved: false,
        waypoints: [],
        shiftPressed: false,
        bulkMembers,
      }

      // Enable dragging mode on stage
      if (appRef.current) {
        appRef.current.stage.eventMode = 'static'
      }

      // Attach window listeners to handle fast cursor movements outside canvas
      if (windowHandlersRef.current.handleMove && windowHandlersRef.current.handleUp) {
        window.addEventListener('pointermove', windowHandlersRef.current.handleMove)
        window.addEventListener('pointerup', windowHandlersRef.current.handleUp)
      }
    },
    [map.gridSize, selectToken, toggleSelectedTokenId, canMoveToken, toolMode]
  )

  /**
   * Initialize PixiJS application and layers - ONCE on mount
   */
  useEffect(() => {
    if (!canvasRef.current) return
    if (!map) return // Phase 6: guard against null map

    console.log('[VTTCanvas] Initializing PixiJS (should only happen ONCE)');

    // CONTAINER-AS-VIEWPORT: the renderer surface is sized to the CONTAINER,
    // never to the map. The map's pixel dimensions (width*gridSize) drive only
    // the world content + pan/zoom bounds, not the canvas element size. We seed
    // the renderer with the container's measured size here; a ResizeObserver
    // (below) keeps it in sync as the container/window changes. Fall back to
    // the map size only if the container hasn't laid out yet (clientWidth 0).
    const initialW = canvasRef.current.clientWidth || map.width * map.gridSize
    const initialH = canvasRef.current.clientHeight || map.height * map.gridSize

    // Initialize PixiJS application
    const app = new PIXI.Application({
      width: initialW,
      height: initialH,
      backgroundColor: hexToPixiColor(map.backgroundColor),
      antialias: true,
      resolution: window.devicePixelRatio || 1,
      autoDensity: true,
    })

    canvasRef.current.appendChild(app.view as HTMLCanvasElement)
    appRef.current = app

    // Center the world content within the viewport on first paint (the map is
    // typically smaller than the container). This mirrors resetViewport.
    {
      const offset = computeCenterOffset(
        initialW,
        initialH,
        map.width * map.gridSize,
        map.height * map.gridSize,
        1,
      )
      viewportRef.current = { scale: 1, x: offset.x, y: offset.y }
      app.stage.scale.set(1)
      app.stage.position.set(offset.x, offset.y)
    }

    // Enable stage interactivity for click events.
    // Always-covering hit-area: the viewport pan/zoom is applied to the STAGE
    // transform, so a fixed `app.screen` rect (in stage-local space) gets
    // dragged off-screen with the stage — leaving most of the canvas dead to
    // taps once panned/zoomed. An always-true hit-area makes the stage catch
    // every pointer in the canvas regardless of transform; interactive children
    // (tokens, grips) are still hit-tested first, so this only governs the
    // empty-space fallback. (Fixes the mobile "only top-left responds" dead-zone.)
    app.stage.eventMode = 'static'
    app.stage.hitArea = { contains: () => true }

    // Create layers (order matters - rendered bottom to top)
    const backgroundLayer = new PIXI.Container() // Phase 6: background image
    const gridLayer = new PIXI.Graphics()
    const tokensContainer = new PIXI.Container()
    const darknessLayer = new PIXI.Graphics() // Phase 3b: darkness/fog layer
    const dimLayer = new PIXI.Graphics() // Lighting v2: dim-band mid shading
    const lightTintLayer = new PIXI.Graphics() // Lighting v2: additive colour tint
    lightTintLayer.blendMode = PIXI.BLEND_MODES.ADD
    const selectionLayer = new PIXI.Graphics()
    const selectionHandles = new PIXI.Container() // interactive rotate/resize grips
    const rubberBandLayer = new PIXI.Graphics() // Multi-select drag rect
    const ghostLayer = new PIXI.Graphics()
    const barrierLayer = new PIXI.Graphics() // Phase 3: barrier placement preview
    const fogLayer = new PIXI.Graphics() // Phase 4: fog of war overlay
    const brushPreviewLayer = new PIXI.Graphics() // Phase 4: brush cursor preview
    const pingLayer = new PIXI.Graphics() // Transient ping pulses (above tokens)
    const lightSourceLayer = new PIXI.Container() // Dynamic lighting: GM-placed light source objects (interactive)

    // Visual-only overlays sit above the tokensContainer in z-order so
    // they paint over tokens, but they must never intercept pointer
    // events — otherwise a click on a fogged cell never reaches the
    // token underneath. Setting eventMode = 'none' is explicit about
    // this; default 'auto' should also skip listenerless Graphics, but
    // we don't want to rely on that.
    darknessLayer.eventMode = 'none'
    dimLayer.eventMode = 'none'
    lightTintLayer.eventMode = 'none'
    selectionLayer.eventMode = 'none'
    // 'passive': the container isn't a hit target, but its children (the rotate
    // / resize grips) set eventMode='static' + hitArea + handlers and must stay
    // interactive — same contract as barrierLayer.
    selectionHandles.eventMode = 'passive'
    rubberBandLayer.eventMode = 'none'
    // 'passive', not 'none': the layer itself isn't a hit target (its drawn
    // placement-preview content shouldn't intercept clicks), but its CHILDREN
    // — the rendered barrier graphics, which set eventMode 'static' + hitArea +
    // a click→selectBarrier handler — must stay interactive. 'none' skips the
    // whole subtree, which is why barriers couldn't be selected or hover-cursor.
    barrierLayer.eventMode = 'passive'
    fogLayer.eventMode = 'none'
    brushPreviewLayer.eventMode = 'none'
    pingLayer.eventMode = 'none'
    ghostLayer.eventMode = 'none'
    backgroundLayer.eventMode = 'none'
    gridLayer.eventMode = 'none'
    // 'passive': the container isn't a hit target, but its children — the light
    // objects, which set eventMode='static' + handlers — must stay interactive
    // (select/drag), same contract as barrierLayer / selectionHandles.
    lightSourceLayer.eventMode = 'passive'

    app.stage.addChild(backgroundLayer) // Phase 6: Background image at the bottom
    app.stage.addChild(gridLayer)
    // Selection halo sits BELOW tokens so a token's nameplate/HP pill (drawn
    // inside tokensContainer) renders on top of the ring instead of being
    // covered by it. The interactive grips stay above tokens (see below).
    app.stage.addChild(selectionLayer)
    app.stage.addChild(tokensContainer)
    app.stage.addChild(lightTintLayer) // Lighting v2: colour tint above tokens, below darkness
    app.stage.addChild(darknessLayer) // Phase 3b: Darkness above tokens (for dynamic_los mode)
    app.stage.addChild(dimLayer) // Lighting v2: dim-band shading above the darkness holes
    app.stage.addChild(selectionHandles) // interactive grips above tokens + halo
    app.stage.addChild(barrierLayer) // Barriers above darkness
    app.stage.addChild(fogLayer) // Phase 4: Fog covers everything (for manual_fog mode)
    app.stage.addChild(brushPreviewLayer) // Phase 4: Brush preview on top of fog
    app.stage.addChild(rubberBandLayer) // Multi-select rect above fog so it's always visible
    app.stage.addChild(pingLayer) // Ping pulses above tokens/fog so they're always visible
    app.stage.addChild(lightSourceLayer) // Light source indicators above fog/darkness (GM only)
    app.stage.addChild(ghostLayer) // Ghost on top of everything

    backgroundLayerRef.current = backgroundLayer
    gridLayerRef.current = gridLayer
    tokensContainerRef.current = tokensContainer
    ghostLayerRef.current = ghostLayer
    selectionLayerRef.current = selectionLayer
    selectionHandlesRef.current = selectionHandles
    rubberBandLayerRef.current = rubberBandLayer
    barrierLayerRef.current = barrierLayer
    fogLayerRef.current = fogLayer
    darknessLayerRef.current = darknessLayer
    dimLayerRef.current = dimLayer
    lightTintLayerRef.current = lightTintLayer
    brushPreviewLayerRef.current = brushPreviewLayer
    pingLayerRef.current = pingLayer
    lightSourceLayerRef.current = lightSourceLayer

    console.log('[VTTCanvas] PixiJS initialized, tokensContainer ready');

    // Background image is loaded by a separate, reactive effect (see below)
    // so it repaints when the URL is set after mount, on map switch, and on
    // session_state rehydration — not just at canvas-init time.

    // Draw grid (Phase 6: with grid configuration)
    if (map.showGrid) {
      drawGrid(
        gridLayer,
        map.width,
        map.height,
        map.gridSize,
        map.gridColor,
        map.gridOpacity,
        map.gridOffsetX,
        map.gridOffsetY
      )
    }

    // Render transient ping pulses. Each ping is an expanding ring that fades
    // out over PING_TTL_MS, drawn in WORLD coords (the layer is under the
    // viewport transform, like tokens). Expired pings are pruned from the
    // store. Runs every frame; cheap (a handful of rings at most).
    const renderPings = () => {
      const layer = pingLayerRef.current
      if (!layer) return
      const pings = useVTTStore.getState().activePings
      const now = performance.now()
      // Prune first so the layer never draws an expired ping for a frame.
      useVTTStore.getState().prunePings(now)
      layer.clear()
      if (pings.size === 0) return
      const gridSize = mapRef.current.gridSize
      const activeMapId = mapRef.current.id
      pings.forEach((ping) => {
        if (ping.mapId !== activeMapId) return
        const age = now - ping.createdAt
        const t = Math.min(age / PING_TTL_MS, 1) // 0..1 progress
        if (t >= 1) return
        // Expanding ring: grows from ~0.3 to ~1.2 grid cells; fades out.
        const maxR = gridSize * 1.2
        const radius = maxR * (0.25 + 0.75 * t)
        const alpha = 1 - t
        const color = 0xffcc33 // warm "attention" yellow
        layer.lineStyle({ width: 3, color, alpha })
        layer.drawCircle(ping.x, ping.y, radius)
        // Solid inner dot so the origin point is unmistakable.
        layer.beginFill(color, alpha)
        layer.drawCircle(ping.x, ping.y, gridSize * 0.12)
        layer.endFill()
      })
    }

    // Add animation ticker to update token positions during animations
    const animationTicker = () => {
      renderPings()
      if (animationsRef.current.size === 0) return

      const now = performance.now()
      const gridSize = mapRef.current.gridSize

      animationsRef.current.forEach((animation, tokenId) => {
        const container = tokenContainersRef.current.get(tokenId)
        if (!container) return

        const elapsed = now - animation.startTime
        const progress = Math.min(elapsed / animation.duration, 1)

        if (progress >= 1) {
          // Current segment complete
          const finalPixel = gridToPixel(animation.toX, animation.toY, gridSize)
          container.x = finalPixel.x
          container.y = finalPixel.y

          // Check if there are more waypoints to animate through
          if (animation.waypoints && animation.waypoints.length > 0) {
            const nextWaypoint = animation.waypoints.shift()!
            const newAnimation: TokenAnimation = {
              fromX: animation.toX,
              fromY: animation.toY,
              toX: nextWaypoint.x,
              toY: nextWaypoint.y,
              startTime: now,
              duration: animation.duration,
              waypoints: animation.waypoints,
            }
            animationsRef.current.set(tokenId, newAnimation)
            console.log(`[VTTCanvas] Starting next waypoint animation for ${tokenId} to (${nextWaypoint.x}, ${nextWaypoint.y})`)
          } else {
            // All waypoints complete
            animationsRef.current.delete(tokenId)
            console.log(`[VTTCanvas] Animation complete for ${tokenId}`)
          }
        } else {
          // Interpolate position with easing
          const easedProgress = 1 - Math.pow(1 - progress, 3) // ease-out cubic
          const x = animation.fromX + (animation.toX - animation.fromX) * easedProgress
          const y = animation.fromY + (animation.toY - animation.fromY) * easedProgress
          const pixel = gridToPixel(x, y, gridSize)
          container.x = pixel.x
          container.y = pixel.y
        }
      })
    }
    app.ticker.add(animationTicker)

    // Mark canvas as ready (triggers token rendering)
    setCanvasReady(true)

    // Minimum drag distance in pixels to start showing ghost/movement (prevents accidental moves)
    const DRAG_THRESHOLD = 5

    // Add global pointer handlers for dragging
    // Note: We attach these to both the stage AND the window to handle fast cursor movements
    const handleMove = (event: PIXI.FederatedPointerEvent | PointerEvent) => {
      // Handle fog brush movement (Phase 4)
      handleFogBrushMove(event)

      // Long-press ping: if the pointer travels past tolerance before the
      // hold threshold elapses, this is a marquee drag, not a ping — cancel
      // the pending ping timer. (The rubber-band itself keeps running below.)
      const pp = pingPressRef.current
      if (pp && pp.timer) {
        const a = appRef.current
        const canvas = a?.view as HTMLCanvasElement | undefined
        let sx: number, sy: number
        if ('global' in event) {
          sx = event.global.x
          sy = event.global.y
        } else if (canvas && a) {
          // Native fallback: map CSS px → Pixi screen space so the move
          // tolerance is measured in the same units as the stored start coord.
          const s = clientToScreen(canvas, event.clientX, event.clientY, a.renderer.resolution)
          sx = s.x
          sy = s.y
        } else {
          sx = event.clientX
          sy = event.clientY
        }
        const moved = Math.hypot(sx - pp.startScreenX, sy - pp.startScreenY)
        if (moved > PING_MOVE_TOLERANCE_PX) {
          clearTimeout(pp.timer)
          pingPressRef.current = null
        }
      }

      // Rubber-band selection update. Done before the token-drag
      // branch so a drag-select in progress doesn't compete with a
      // (nonexistent) token drag — they're mutually exclusive.
      const rb = rubberBandRef.current
      if (rb && rb.isActive && appRef.current) {
        const a = appRef.current
        const canvas = a.view as HTMLCanvasElement
        // Resolve the pointer to Pixi screen space, then to world space — the
        // marquee is stored and drawn in world coords (see pointerdown). The
        // native fallback is scaled CSS→render so it matches event.global.
        let screenX: number, screenY: number
        if ('global' in event) {
          screenX = event.global.x
          screenY = event.global.y
        } else {
          const s = clientToScreen(canvas, event.clientX, event.clientY, a.renderer.resolution)
          screenX = s.x
          screenY = s.y
        }
        const world = screenToWorld({ x: screenX, y: screenY }, viewportRef.current)
        rb.currentX = world.x
        rb.currentY = world.y
        drawRubberBand()
        return
      }

      const dragState = dragStateRef.current
      if (!dragState || !dragState.isDragging) return

      const ghostLayer = ghostLayerRef.current
      if (!ghostLayer || !renderGhostRef.current || !appRef.current) return

      // Get canvas + renderer for coordinate conversion
      const a = appRef.current
      const canvas = a.view as HTMLCanvasElement

      // Calculate current pointer position in Pixi screen space
      let currentX: number, currentY: number
      if ('global' in event) {
        // PixiJS event - use global coordinates directly
        const globalPos = event.global
        currentX = globalPos.x
        currentY = globalPos.y
      } else {
        // Native PointerEvent from window - convert CSS px → Pixi screen space
        // (scaled by the CSS→render factor so it matches event.global on mobile).
        const s = clientToScreen(canvas, event.clientX, event.clientY, a.renderer.resolution)
        currentX = s.x
        currentY = s.y
      }

      // Check if we've exceeded the drag threshold
      if (!dragState.hasMoved) {
        const distance = Math.hypot(
          currentX - dragState.startX,
          currentY - dragState.startY
        )
        if (distance < DRAG_THRESHOLD) {
          // Haven't moved enough yet, don't show ghost
          return
        }
        // Threshold exceeded, mark as moved
        dragState.hasMoved = true
      }

      // Convert the screen-space pointer to world space, then subtract the
      // (world-space) grab offset. This keeps the ghost glued to the cursor
      // at any zoom/pan, matching gridToPixel/pixelToGrid which work in
      // world coordinates.
      const worldPos = screenToWorld(
        { x: currentX, y: currentY },
        viewportRef.current
      )
      const x = worldPos.x - dragState.offsetX
      const y = worldPos.y - dragState.offsetY

      // Check if we should create a waypoint (Shift pressed during drag)
      if (dragState.shiftPressed) {
        const currentMap = mapRef.current
        const centerX = x + (currentMap.gridSize * dragState.token.width) / 2
        const centerY = y + (currentMap.gridSize * dragState.token.height) / 2
        let gridPos = pixelToGrid(centerX, centerY, currentMap.gridSize)
        gridPos = clampToMapBounds(gridPos.x, gridPos.y, currentMap.width, currentMap.height)

        // Check if this waypoint is different from the last one
        const lastWaypoint = dragState.waypoints[dragState.waypoints.length - 1]
        const isDifferent = !lastWaypoint || lastWaypoint.x !== gridPos.x || lastWaypoint.y !== gridPos.y

        // Also check if it's different from the token's starting position
        const isDifferentFromStart = gridPos.x !== dragState.token.x || gridPos.y !== dragState.token.y

        if (isDifferent && isDifferentFromStart) {
          dragState.waypoints.push({ x: gridPos.x, y: gridPos.y })
          console.log(`[VTTCanvas] Waypoint added at (${gridPos.x}, ${gridPos.y})`)

          // Reset shift pressed so we need to release and press again for next waypoint
          dragState.shiftPressed = false
        }
      }

      renderGhostRef.current(ghostLayer, dragState.token, x, y, dragState.waypoints)

      // Bulk-drag preview: outline each other selected member at its
      // start position + delta. dx/dy is taken from the primary
      // token's pixel position, snapped to grid, so the rest of the
      // selection moves in lock-step on the grid. Drawn inline (no
      // useCallback helper) to keep this component's hook count
      // stable across the early `if (!map) return` branch.
      if (dragState.bulkMembers.length > 0) {
        const currentMap = mapRef.current
        const centerX = x + (currentMap.gridSize * dragState.token.width) / 2
        const centerY = y + (currentMap.gridSize * dragState.token.height) / 2
        const previewGrid = pixelToGrid(centerX, centerY, currentMap.gridSize)
        const dxGrid = previewGrid.x - dragState.token.x
        const dyGrid = previewGrid.y - dragState.token.y
        dragState.bulkMembers.forEach((m) => {
          const targetGridX = m.startX + dxGrid
          const targetGridY = m.startY + dyGrid
          const pixel = gridToPixel(targetGridX, targetGridY, currentMap.gridSize)
          // Red when this member can't move (permission gate) — the
          // primary token's drop will silently skip them on commit,
          // so the colour gives the user a heads-up.
          const color = m.canMove ? 0xffffff : 0xe74c3c
          const alpha = m.canMove ? 0.55 : 0.5
          ghostLayer.lineStyle({ width: 2, color, alpha })
          ghostLayer.drawRect(
            pixel.x,
            pixel.y,
            currentMap.gridSize * m.width,
            currentMap.gridSize * m.height,
          )
        })
      }
    }

    const handleUp = (event: PIXI.FederatedPointerEvent | PointerEvent) => {
      // Handle fog brush end (Phase 4)
      handleFogBrushUp()

      // Long-press ping: a release before the hold threshold means this was a
      // quick click, not a ping — cancel the pending timer. (If the timer had
      // already fired, `fired` is true and pingPressRef was cleared then.)
      const pp = pingPressRef.current
      if (pp) {
        if (pp.timer) clearTimeout(pp.timer)
        pingPressRef.current = null
      }

      // Rubber-band commit. Runs before the token-drag branch for the
      // same reason as handleMove: the two states are mutually
      // exclusive and rubber-band is the simpler exit.
      if (rubberBandRef.current?.isActive) {
        finishRubberBand()
        window.removeEventListener('pointermove', handleMove as EventListener)
        window.removeEventListener('pointerup', handleUp as EventListener)
        return
      }

      const dragState = dragStateRef.current
      if (!dragState || !dragState.isDragging) return

      const ghostLayer = ghostLayerRef.current
      if (ghostLayer) {
        ghostLayer.clear()
      }

      // If we haven't exceeded the drag threshold, just cancel the drag
      if (!dragState.hasMoved) {
        dragStateRef.current = null
        if (appRef.current) {
          appRef.current.stage.eventMode = 'static'
        }
        // Remove window event listeners
        window.removeEventListener('pointermove', handleMove as EventListener)
        window.removeEventListener('pointerup', handleUp as EventListener)
        return
      }

      if (!appRef.current) return

      // Get canvas + renderer for coordinate conversion
      const a = appRef.current
      const canvas = a.view as HTMLCanvasElement

      // Resolve the pointer to Pixi screen space, then to world space before
      // subtracting the (world-space) grab offset — same transform as
      // handleMove so the drop lands exactly under the cursor at any zoom.
      let screenX: number, screenY: number
      if ('global' in event) {
        // PixiJS event
        screenX = event.global.x
        screenY = event.global.y
      } else {
        // Native PointerEvent from window - CSS px → Pixi screen space.
        const s = clientToScreen(canvas, event.clientX, event.clientY, a.renderer.resolution)
        screenX = s.x
        screenY = s.y
      }
      const worldPos = screenToWorld({ x: screenX, y: screenY }, viewportRef.current)
      const x = worldPos.x - dragState.offsetX
      const y = worldPos.y - dragState.offsetY

      const currentMap = mapRef.current
      const centerX = x + (currentMap.gridSize * dragState.token.width) / 2
      const centerY = y + (currentMap.gridSize * dragState.token.height) / 2
      let gridPos = pixelToGrid(centerX, centerY, currentMap.gridSize)
      gridPos = clampToMapBounds(gridPos.x, gridPos.y, currentMap.width, currentMap.height)

      // Check if movement is blocked by barriers (GMs can bypass)
      if (userRole !== 'gm') {
        const barriersRef = useVTTStore.getState().barriers
        if (isMovementBlocked(
          { x: dragState.token.x, y: dragState.token.y },
          { x: gridPos.x, y: gridPos.y },
          barriersRef,
          currentMap.gridSize
        )) {
          // Movement blocked - cancel drag and return to original position
          dragStateRef.current = null
          return
        }
      }

      // Bulk drag commit. When the user dragged a member of a multi-
      // token selection, every other member moves by the same grid
      // delta the primary token did. Waypoints are skipped for the
      // followers — bulk move is the simple case; per-token waypoint
      // chains aren't expressible from one drag of one cursor.
      const isBulkDrag = dragState.bulkMembers.length > 0
      const dxGrid = gridPos.x - dragState.token.x
      const dyGrid = gridPos.y - dragState.token.y

      // If there are waypoints, create animation chain BEFORE updating store
      if (dragState.waypoints.length > 0) {
        console.log(`[VTTCanvas] Creating waypoint animation chain with ${dragState.waypoints.length} waypoints`)

        // Build animation path: current position → waypoint1 → waypoint2 → ... → final position
        const allWaypoints = [...dragState.waypoints, { x: gridPos.x, y: gridPos.y }]
        const firstWaypoint = allWaypoints.shift()!

        const animation: TokenAnimation = {
          fromX: dragState.token.x,
          fromY: dragState.token.y,
          toX: firstWaypoint.x,
          toY: firstWaypoint.y,
          startTime: performance.now(),
          duration: animationDuration * 1000,
          waypoints: allWaypoints, // Remaining waypoints
        }
        animationsRef.current.set(dragState.tokenId, animation)

        // Pass the waypoints to the store so the position-change effect animates
        // the full path; without them it builds a straight-line tween to the end.
        moveTokenRef.current(dragState.tokenId, gridPos.x, gridPos.y, dragState.waypoints)
      } else {
        // No waypoints - just update store immediately
        moveTokenRef.current(dragState.tokenId, gridPos.x, gridPos.y)
      }

      if (onTokenMoveRef.current) {
        // Send waypoints if they exist
        onTokenMoveRef.current(
          dragState.tokenId,
          gridPos.x,
          gridPos.y,
          dragState.waypoints.length > 0 ? dragState.waypoints : undefined
        )
      }

      // Commit the followers. We skip members the caller isn't
      // allowed to move (mirrors the per-token permission check the
      // server enforces — sending the emit anyway would just earn an
      // error broadcast back). We also skip members whose final
      // position would equal their start position (the drag landed
      // them where they already were) and clamp each follower to
      // map bounds independently.
      if (isBulkDrag && (dxGrid !== 0 || dyGrid !== 0)) {
        dragState.bulkMembers.forEach((m) => {
          if (!m.canMove) return
          const targetX = clampToMapBounds(
            m.startX + dxGrid,
            m.startY + dyGrid,
            currentMap.width,
            currentMap.height,
          ).x
          const targetY = clampToMapBounds(
            m.startX + dxGrid,
            m.startY + dyGrid,
            currentMap.width,
            currentMap.height,
          ).y
          if (targetX === m.startX && targetY === m.startY) return

          // Barrier block check per follower (GM bypasses). A blocked
          // follower is silently skipped — its halo stays at the
          // original position so the user can see who didn't move.
          if (userRole !== 'gm') {
            const barriersRef = useVTTStore.getState().barriers
            if (
              isMovementBlocked(
                { x: m.startX, y: m.startY },
                { x: targetX, y: targetY },
                barriersRef,
                currentMap.gridSize,
              )
            ) {
              return
            }
          }

          moveTokenRef.current(m.id, targetX, targetY)
          if (onTokenMoveRef.current) {
            onTokenMoveRef.current(m.id, targetX, targetY)
          }
        })
      }

      dragStateRef.current = null

      // Keep stage.eventMode as 'static' to ensure fog brush and other tools continue to work
      // Previously this was set to 'auto' which would stop pointermove events from firing
      if (appRef.current) {
        appRef.current.stage.eventMode = 'static'
      }

      // Remove window event listeners
      window.removeEventListener('pointermove', handleMove as EventListener)
      window.removeEventListener('pointerup', handleUp as EventListener)
    }

    const handleUpOutside = () => {
      // Handle fog brush end (Phase 4)
      handleFogBrushUp()

      // Rubber-band cancel — pointer left the stage. Commit what's
      // currently inside the rect rather than discarding the gesture
      // outright; mirrors `handleUp` so a slightly-overshooting drag
      // doesn't feel broken.
      if (rubberBandRef.current?.isActive) {
        finishRubberBand()
      }

      const ghostLayer = ghostLayerRef.current
      if (ghostLayer) {
        ghostLayer.clear()
      }
      dragStateRef.current = null

      // Keep stage.eventMode as 'static' to ensure fog brush and other tools continue to work
      // Previously this was set to 'auto' which would stop pointermove events from firing
      if (appRef.current) {
        appRef.current.stage.eventMode = 'static'
      }

      // Remove window event listeners
      window.removeEventListener('pointermove', handleMove as EventListener)
      window.removeEventListener('pointerup', handleUp as EventListener)
    }

    // Keyboard event handlers for waypoints and barriers
    const handleKeyDown = (event: KeyboardEvent) => {
      // Escape cancels an in-progress rubber-band. Runs first so it
      // takes precedence over barrier-mode Escape (which would also
      // own Escape in that mode, but rubber-band is select-mode only
      // so they don't actually overlap).
      if (event.key === 'Escape' && rubberBandRef.current?.isActive) {
        rubberBandRef.current = null
        const layer = rubberBandLayerRef.current
        if (layer) layer.clear()
        window.removeEventListener('pointermove', handleMove as EventListener)
        window.removeEventListener('pointerup', handleUp as EventListener)
        event.preventDefault()
        return
      }

      // Arrow keys: Move selected token(s) by one grid square. With a
      // multi-token selection, every member that the caller can move
      // shifts by the same (dx, dy); blocked or unmovable members are
      // skipped silently (mirrors the bulk-drag commit).
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
        const store = useVTTStore.getState()
        const ids = store.selectedTokenIds
        if (ids.length === 0) return

        const selectedTokensArr = ids
          .map((id) => store.tokens.find((t) => t.id === id))
          .filter((t): t is Token => !!t && canMoveTokenRef.current(t))

        if (selectedTokensArr.length === 0) return

        event.preventDefault() // Prevent page scrolling

        const currentMap = mapRef.current
        let dx = 0
        let dy = 0
        if (event.key === 'ArrowUp') dy = -1
        if (event.key === 'ArrowDown') dy = 1
        if (event.key === 'ArrowLeft') dx = -1
        if (event.key === 'ArrowRight') dx = 1

        selectedTokensArr.forEach((selectedToken) => {
          let newX = selectedToken.x + dx
          let newY = selectedToken.y + dy

          // Clamp to map bounds
          newX = Math.max(0, Math.min(currentMap.width - selectedToken.width, newX))
          newY = Math.max(0, Math.min(currentMap.height - selectedToken.height, newY))

          // Only move if position changed
          if (newX === selectedToken.x && newY === selectedToken.y) return

          // Check if movement is blocked by barriers (GMs can bypass)
          if (userRole !== 'gm') {
            const barriers = useVTTStore.getState().barriers
            if (
              isMovementBlocked(
                { x: selectedToken.x, y: selectedToken.y },
                { x: newX, y: newY },
                barriers,
                currentMap.gridSize,
              )
            ) {
              return
            }
          }

          // Mark this as a keyboard move for fast animation
          keyboardMoveRef.current.add(selectedToken.id)

          moveTokenRef.current(selectedToken.id, newX, newY)

          // Send to server via WebSocket (Phase 3c: mark as instant for keyboard moves)
          if (onTokenMoveRef.current) {
            onTokenMoveRef.current(selectedToken.id, newX, newY, undefined, true)
          }
        })

        console.log(`[VTTCanvas] Arrow key moved ${selectedTokensArr.length} token(s) by (${dx}, ${dy})`)
        return
      }

      // Toggle barrier mode with 'B' key
      if (event.key === 'b' || event.key === 'B') {
        // Barrier tool is GM-only. The server already rejects non-GM
        // barrier_create; gate here so non-GM players can't even enter
        // barrier mode via the shortcut (client-side UX leak fix).
        if (!canUseBarrierTool(userRole)) {
          return
        }

        const currentToolMode = toolModeRef.current
        const newToolMode = currentToolMode === 'barrier' ? 'select' : 'barrier'

        // Barriers are always available now (no vision-mode gate); the GM
        // client-gate (canUseBarrierTool, checked above) is the only guard.

        const setToolMode = useVTTStore.getState().setToolMode
        setToolMode(newToolMode)
        console.log(`[VTTCanvas] Toggled tool mode to: ${newToolMode}`)
        return
      }

      // Barrier mode: ESC cancels the whole in-progress wall.
      if (toolModeRef.current === 'barrier' && event.key === 'Escape') {
        console.log('[VTTCanvas] Wall placement cancelled')
        barrierVerticesRef.current = []
        if (renderBarrierPreviewRef.current) {
          renderBarrierPreviewRef.current()
        }
        return
      }

      // Barrier mode: Backspace removes the last placed vertex.
      if (toolModeRef.current === 'barrier' && event.key === 'Backspace') {
        if (barrierVerticesRef.current.length > 0) {
          barrierVerticesRef.current.pop()
          if (renderBarrierPreviewRef.current) {
            renderBarrierPreviewRef.current()
          }
        }
        return
      }

      // Select mode: Delete / Backspace. Order of precedence:
      //   1. If any token is selected and the user has permission to
      //      delete it (GM, or owner of the token), bulk-delete the
      //      whole selection via `onTokensDelete`. The server filters
      //      out IDs the caller isn't allowed to delete, so it's safe
      //      to ship the full list — including non-owned tokens —
      //      and let the server reject what it should.
      //   2. Otherwise fall through to the barrier-delete branch.
      // We also guard on focus: when the user is typing in chat or
      // any other text input, Delete / Backspace must edit text, not
      // mutate the canvas.
      if (toolModeRef.current === 'select' && (event.key === 'Delete' || event.key === 'Backspace')) {
        // Focus guard — `isContentEditable` catches rich editors that
        // aren't INPUT/TEXTAREA (and Selecto-like libs that mount a
        // contenteditable div).
        const active = document.activeElement as HTMLElement | null
        const tag = active?.tagName
        const isTextInput =
          tag === 'INPUT' || tag === 'TEXTAREA' || (active?.isContentEditable ?? false)

        if (!isTextInput) {
          const store = useVTTStore.getState()
          const tokenIds = store.selectedTokenIds
          if (tokenIds.length > 0) {
            // Filter to IDs the caller can plausibly delete (GM may
            // delete any; players may only delete tokens they own).
            // Server enforces the same rule authoritatively — this
            // is just to avoid a no-op emit when, e.g., a player has
            // selected only NPC tokens.
            const deletable = tokenIds.filter((id) => {
              const t = store.tokens.find((tk) => tk.id === id)
              if (!t) return false
              if (userRole === 'gm') return true
              return (
                !!currentUserId &&
                t.ownerUserId != null &&
                t.ownerUserId === currentUserId
              )
            })

            if (deletable.length > 0) {
              event.preventDefault()
              console.log(`[VTTCanvas] Bulk deleting ${deletable.length} token(s)`)
              if (onTokensDeleteRef.current) {
                onTokensDeleteRef.current(deletable)
              }
              // Clear selection optimistically; the server broadcast
              // (`tokens_deleted`) will also clear it once it arrives.
              useVTTStore.getState().clearSelectedTokens()
              return
            }
          }
        }

        // No tokens selected (or none deletable) — fall through to
        // the barrier-delete branch.
        const selectedId = useVTTStore.getState().selectedBarrierId
        if (selectedId) {
          console.log(`[VTTCanvas] Deleting barrier ${selectedId}`)
          removeBarrier(selectedId)
          selectBarrier(null)

          // Send to server via WebSocket
          if (onBarrierDeleteRef.current) {
            onBarrierDeleteRef.current(selectedId)
          }
          return
        }
      }

      // Shift+click waypoint queue: Enter commits, Escape cancels (Bug 2).
      // These run even when no drag is in progress. The queue belongs
      // to a single token's path; with multi-select active we skip
      // (the user is curating a selection, not plotting a route).
      if (toolModeRef.current === 'select' && pendingWaypointsRef.current.length > 0) {
        if (event.key === 'Enter') {
          const store = useVTTStore.getState()
          const selectedId =
            store.selectedTokenIds.length === 1 ? store.selectedTokenIds[0] : null
          if (selectedId) {
            const pWaypoints = pendingWaypointsRef.current
            const finalWp = pWaypoints[pWaypoints.length - 1]
            const intermediates = pWaypoints.length > 1 ? pWaypoints.slice(0, -1) : undefined
            moveTokenRef.current(selectedId, finalWp.x, finalWp.y, intermediates)
            if (onTokenMoveRef.current) {
              onTokenMoveRef.current(selectedId, finalWp.x, finalWp.y, intermediates)
            }
            pendingWaypointsRef.current = []
            const ghostLayer = ghostLayerRef.current
            if (ghostLayer) ghostLayer.clear()
            event.preventDefault()
            console.log(`[VTTCanvas] Shift+click path committed via Enter: final (${finalWp.x}, ${finalWp.y})`)
            return
          }
        }
        if (event.key === 'Escape') {
          pendingWaypointsRef.current = []
          const ghostLayer = ghostLayerRef.current
          if (ghostLayer) ghostLayer.clear()
          event.preventDefault()
          console.log('[VTTCanvas] Shift+click waypoint queue cleared via Escape')
          return
        }
      }

      // Waypoint handling (only in select mode during drag)
      const dragState = dragStateRef.current
      if (!dragState || !dragState.isDragging) return

      if (event.key === 'Shift' && !dragState.shiftPressed) {
        // Create waypoint at current cursor position
        dragState.shiftPressed = true

        // Get current cursor position from ghost layer or last known position
        // We'll capture this in handleMove, but for now mark shift as pressed
        console.log('[VTTCanvas] Shift pressed - waypoint mode active')
      } else if (event.key === 'Escape') {
        // Clear all waypoints
        dragState.waypoints = []
        console.log('[VTTCanvas] Waypoints cleared')

        // Re-render ghost
        const ghostLayer = ghostLayerRef.current
        if (ghostLayer && renderGhostRef.current) {
          ghostLayer.clear()
        }
      } else if (event.key === 'Backspace') {
        // Remove last waypoint
        if (dragState.waypoints.length > 0) {
          dragState.waypoints.pop()
          console.log('[VTTCanvas] Last waypoint removed')

          // Re-render ghost
          const ghostLayer = ghostLayerRef.current
          if (ghostLayer && renderGhostRef.current) {
            ghostLayer.clear()
          }
        }
      }
    }

    const handleKeyUp = (event: KeyboardEvent) => {
      const dragState = dragStateRef.current
      if (!dragState || !dragState.isDragging) return

      if (event.key === 'Shift') {
        dragState.shiftPressed = false
        console.log('[VTTCanvas] Shift released - waypoint mode inactive')
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('keyup', handleKeyUp)

    // Touch gesture handlers for pan and zoom
    const handleTouchStart = (event: TouchEvent) => {
      if (event.touches.length === 2) {
        // Two-finger gesture - prepare for pinch zoom
        const touch1 = event.touches[0]
        const touch2 = event.touches[1]

        const distance = Math.hypot(
          touch2.clientX - touch1.clientX,
          touch2.clientY - touch1.clientY
        )

        // Canvas-relative midpoint, in the same space as the stage offset, so
        // the zoom can anchor on it (matches the wheel handler's offsetX/Y).
        const rect = canvas.getBoundingClientRect()
        const midpointX = (touch1.clientX + touch2.clientX) / 2 - rect.left
        const midpointY = (touch1.clientY + touch2.clientY) / 2 - rect.top

        touchStateRef.current = {
          initialDistance: distance,
          initialScale: viewportRef.current.scale,
          initialMidpoint: { x: midpointX, y: midpointY },
          initialOffset: { x: viewportRef.current.x, y: viewportRef.current.y },
        }

        // Prevent default to avoid page scroll
        event.preventDefault()
      }
    }

    const handleTouchMove = (event: TouchEvent) => {
      if (event.touches.length === 2 && touchStateRef.current.initialDistance) {
        const touch1 = event.touches[0]
        const touch2 = event.touches[1]

        const distance = Math.hypot(
          touch2.clientX - touch1.clientX,
          touch2.clientY - touch1.clientY
        )

        const rect = canvas.getBoundingClientRect()
        const midpointX = (touch1.clientX + touch2.clientX) / 2 - rect.left
        const midpointY = (touch1.clientY + touch2.clientY) / 2 - rect.top

        const { initialDistance, initialScale, initialMidpoint, initialOffset } = touchStateRef.current

        if (initialDistance && initialScale && initialMidpoint && initialOffset) {
          // Calculate new scale
          const scaleChange = distance / initialDistance
          let newScale = initialScale * scaleChange

          // Clamp scale between 0.5 and 3
          newScale = Math.max(0.5, Math.min(3, newScale))

          // Keep the world point that was under the initial pinch midpoint
          // pinned under the current midpoint. This anchors the zoom on the
          // fingers (not the corner) AND folds in two-finger pan in one step.
          const ratio = newScale / initialScale
          const newX = midpointX - (initialMidpoint.x - initialOffset.x) * ratio
          const newY = midpointY - (initialMidpoint.y - initialOffset.y) * ratio

          // Update viewport
          viewportRef.current = {
            scale: newScale,
            x: newX,
            y: newY,
          }

          // Apply transform to stage
          if (appRef.current) {
            appRef.current.stage.scale.set(newScale)
            appRef.current.stage.position.set(newX, newY)
          }
        }

        event.preventDefault()
      }
    }

    const handleTouchEnd = (event: TouchEvent) => {
      if (event.touches.length < 2) {
        // Reset touch state when fingers are lifted
        touchStateRef.current = {
          initialDistance: null,
          initialScale: null,
          initialMidpoint: null,
          initialOffset: null,
        }
      }
    }

    // Prevent default context menu on canvas (blocks all right-click menus)
    const handleContextMenu = (event: MouseEvent) => {
      console.log('[VTTCanvas] Context menu event:', {
        target: event.target,
        clientX: event.clientX,
        clientY: event.clientY,
        type: event.type
      })
      event.preventDefault()
      event.stopPropagation()
      return false
    }

    // Attach touch event listeners to the canvas
    const canvas = app.view as HTMLCanvasElement
    console.log('[VTTCanvas] Attaching contextmenu listener to canvas:', canvas)
    canvas.addEventListener('contextmenu', handleContextMenu, { capture: true })
    canvas.addEventListener('touchstart', handleTouchStart, { passive: false })
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false })
    canvas.addEventListener('touchend', handleTouchEnd)

    // ── Mouse wheel zoom + middle-mouse drag pan ───────────────────────
    // Pointer/mouse analogues of the touch pinch/pan gestures above. Both
    // update viewportRef + the PIXI stage transform and push a
    // viewportSnapshot so the SVG ruler overlay stays aligned on zoom/pan.
    const applyViewport = (scale: number, x: number, y: number) => {
      viewportRef.current = { scale, x, y }
      if (appRef.current) {
        appRef.current.stage.scale.set(scale)
        appRef.current.stage.position.set(x, y)
      }
      setViewportSnapshot({ scale, x, y })
    }

    const handleWheel = (event: WheelEvent) => {
      // Block page scroll; zoom the canvas instead.
      event.preventDefault()
      const v = viewportRef.current
      // Normalize delta across deltaMode (pixels / lines / pages) for a
      // consistent feel, then map to an exponential factor so every notch
      // scales by a constant ratio (smooth in and out).
      const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? 100 : 1
      const delta = event.deltaY * unit
      const factor = Math.exp(-delta * 0.0015)
      const newScale = Math.max(0.5, Math.min(3, v.scale * factor))
      if (newScale === v.scale) return
      const ratio = newScale / v.scale
      // Keep the world point under the cursor fixed while zooming.
      const cx = event.offsetX
      const cy = event.offsetY
      const newX = cx - (cx - v.x) * ratio
      const newY = cy - (cy - v.y) * ratio
      applyViewport(newScale, newX, newY)
    }

    // Middle-mouse drag panning. Tracked with a plain object (not a React
    // ref) since it only lives for the duration of this effect.
    const panState = { active: false, startX: 0, startY: 0, originX: 0, originY: 0 }
    const handlePanMove = (event: PointerEvent) => {
      if (!panState.active) return
      const v = viewportRef.current
      const newX = panState.originX + (event.clientX - panState.startX)
      const newY = panState.originY + (event.clientY - panState.startY)
      applyViewport(v.scale, newX, newY)
    }
    const handlePanUp = () => {
      if (!panState.active) return
      panState.active = false
      canvas.style.cursor = ''
      window.removeEventListener('pointermove', handlePanMove)
      window.removeEventListener('pointerup', handlePanUp)
    }
    // Hit-test whether a screen point (canvas-local px) lands on a token.
    // Used to disambiguate right-press: right-drag on EMPTY canvas pans, but
    // right-press ON a token is reserved (tooltip today, context menu later)
    // and must NOT start a pan. Uses PIXI's event boundary to find the top
    // interactive object under the point, then checks it lives inside the
    // tokens container. Middle-button pan ignores this (always pans).
    const isPointerOnToken = (clientX: number, clientY: number): boolean => {
      const a = appRef.current
      const tokensContainer = tokensContainerRef.current
      if (!a || !tokensContainer) return false
      // hitTest works in Pixi SCREEN space (the same space as event.global),
      // NOT raw CSS px. Convert the client coord through the CSS→render scale
      // so the gate is accurate when the canvas is CSS-scaled (mobile). On
      // desktop the scale is 1, so this is identical to the old math.
      const local = clientToScreen(canvas, clientX, clientY, a.renderer.resolution)
      let hit: PIXI.DisplayObject | null = null
      try {
        hit = a.renderer.events.rootBoundary.hitTest(local.x, local.y)
      } catch {
        return false
      }
      // Walk up the parent chain — a token is a Container with sprite/label
      // children, so the hit may be a descendant of the token container.
      let node: PIXI.Container | null = hit as PIXI.Container | null
      while (node) {
        if (node === tokensContainer) return true
        node = node.parent
      }
      return false
    }

    const handlePanDown = (event: PointerEvent) => {
      // Middle (1) always pans; right (2) pans only on empty canvas (a right
      // press on a token is reserved for the tooltip). Left never pans.
      if (!shouldPanForButton(event.button, isPointerOnToken(event.clientX, event.clientY))) return
      // Capture phase: stop PIXI's federated pointerdown (rubber-band /
      // token drag) from firing for this button, and block the OS
      // autoscroll / context-menu affordances. The contextmenu suppression
      // (handleContextMenu + the document listener) keeps the OS menu from
      // popping during a right-drag.
      event.preventDefault()
      event.stopPropagation()
      const v = viewportRef.current
      panState.active = true
      panState.startX = event.clientX
      panState.startY = event.clientY
      panState.originX = v.x
      panState.originY = v.y
      canvas.style.cursor = 'grabbing'
      window.addEventListener('pointermove', handlePanMove)
      window.addEventListener('pointerup', handlePanUp)
    }
    const handleAuxClick = (event: MouseEvent) => {
      // Suppress the middle-click autoscroll widget on browsers that show it.
      if (event.button === 1) event.preventDefault()
    }

    canvas.addEventListener('wheel', handleWheel, { passive: false })
    canvas.addEventListener('pointerdown', handlePanDown, { capture: true })
    canvas.addEventListener('auxclick', handleAuxClick)

    // Also try adding to document as a backup
    document.addEventListener('contextmenu', (e) => {
      const target = e.target as HTMLElement
      console.log('[VTTCanvas] Document contextmenu event:', {
        target,
        isCanvas: target === canvas,
        tagName: target.tagName
      })
      if (target === canvas) {
        console.log('[VTTCanvas] Blocking contextmenu on canvas via document listener')
        e.preventDefault()
        e.stopPropagation()
        return false
      }
    }, { capture: true })

    // Store handlers in ref for window listener management
    windowHandlersRef.current = {
      handleMove: handleMove as EventListener as ((event: PointerEvent) => void),
      handleUp: handleUp as EventListener as ((event: PointerEvent) => void),
    }

    // Helper function to calculate cells in circular radius
    // brushSize 1 = single cell, brushSize 2 = 3x3, brushSize 3 = 5x5, etc.
    const getCellsInRadius = (centerX: number, centerY: number, brushSize: number): Array<{ x: number; y: number }> => {
      const cells: Array<{ x: number; y: number }> = []
      // Convert brush size to actual radius: size 1 = radius 0, size 2 = radius 1, etc.
      const radius = brushSize - 1
      const radiusSquared = radius * radius

      for (let dy = -radius; dy <= radius; dy++) {
        for (let dx = -radius; dx <= radius; dx++) {
          const distSquared = dx * dx + dy * dy
          if (distSquared <= radiusSquared) {
            const gridX = centerX + dx
            const gridY = centerY + dy
            // Check bounds
            if (gridX >= 0 && gridX < mapRef.current.width && gridY >= 0 && gridY < mapRef.current.height) {
              cells.push({ x: gridX, y: gridY })
            }
          }
        }
      }

      return cells
    }

    // Fog brush pointer down handler (Phase 4)
    const handleFogBrushDown = (event: PIXI.FederatedPointerEvent) => {
      const mode = toolModeRef.current
      if (mode !== 'fog') return
      // Fog is GM-only (server enforces it too); refuse non-GM brush entry so
      // a direct store poke into 'fog' mode can't paint locally. Mirrors the
      // barrier tool gate.
      if (!canUseFogTool(userRole)) return
      const fogMode = useVTTStore.getState().fogMode

      // event.global is screen space; pixelToGrid works in world space, so
      // apply the inverse viewport transform first. Without this the brush
      // paints the wrong cells at any zoom/pan (same fix as token drag #92).
      const worldPos = screenToWorld(
        { x: event.global.x, y: event.global.y },
        viewportRef.current
      )

      const currentMap = mapRef.current
      const gridPos = pixelToGrid(worldPos.x, worldPos.y, currentMap.gridSize)

      fogBrushStateRef.current.isDrawing = true
      fogBrushStateRef.current.lastGridX = gridPos.x
      fogBrushStateRef.current.lastGridY = gridPos.y
      fogBrushStateRef.current.accumulatedCells = new Set()
      fogBrushStateRef.current.lastUpdateTime = 0

      // Process initial cells
      const brushSize = useVTTStore.getState().brushSize
      const cells = getCellsInRadius(gridPos.x, gridPos.y, brushSize)

      cells.forEach((cell) => {
        const key = `${cell.x},${cell.y}`
        fogBrushStateRef.current.accumulatedCells.add(key)
      })

      // Immediately update locally and send to server
      if (fogMode === 'reveal') {
        useVTTStore.getState().revealFogCells(cells)
        if (onFogRevealRef.current) {
          onFogRevealRef.current(currentMap.id, cells)
        }
      } else {
        useVTTStore.getState().hideFogCells(cells)
        if (onFogHideRef.current) {
          onFogHideRef.current(currentMap.id, cells)
        }
      }

      fogBrushStateRef.current.lastUpdateTime = performance.now()

      console.log(`[VTTCanvas] Fog brush started: ${fogMode}, ${cells.length} cells`)
    }

    // Fog brush pointer move handler (Phase 4)
    const handleFogBrushMove = (event: PIXI.FederatedPointerEvent | PointerEvent) => {
      const fogState = fogBrushStateRef.current
      if (!fogState.isDrawing) return

      const mode = toolModeRef.current
      if (mode !== 'fog') return
      const fogMode = useVTTStore.getState().fogMode

      // Resolve the pointer to screen space (canvas-relative), then to world
      // space before mapping to grid cells — same transform as handleFogBrushDown
      // so the brush stays glued to the cursor at any zoom/pan.
      let screenX: number, screenY: number
      if ('global' in event) {
        screenX = event.global.x
        screenY = event.global.y
      } else {
        // For native PointerEvent, map CSS px → Pixi screen space (scaled by
        // the CSS→render factor so it matches event.global on mobile).
        const a = appRef.current
        const canvas = a?.view as HTMLCanvasElement
        if (!canvas || !a) return
        const s = clientToScreen(canvas, event.clientX, event.clientY, a.renderer.resolution)
        screenX = s.x
        screenY = s.y
      }

      const worldPos = screenToWorld({ x: screenX, y: screenY }, viewportRef.current)
      const currentMap = mapRef.current
      const gridPos = pixelToGrid(worldPos.x, worldPos.y, currentMap.gridSize)

      // Skip if same cell
      if (gridPos.x === fogState.lastGridX && gridPos.y === fogState.lastGridY) {
        return
      }

      fogState.lastGridX = gridPos.x
      fogState.lastGridY = gridPos.y

      // Calculate affected cells
      const brushSize = useVTTStore.getState().brushSize
      const cells = getCellsInRadius(gridPos.x, gridPos.y, brushSize)

      // Add to accumulated cells
      const newCells: Array<{ x: number; y: number }> = []
      cells.forEach((cell) => {
        const key = `${cell.x},${cell.y}`
        if (!fogState.accumulatedCells.has(key)) {
          fogState.accumulatedCells.add(key)
          newCells.push(cell)
        }
      })

      if (newCells.length === 0) return

      // Update locally immediately for responsiveness
      if (fogMode === 'reveal') {
        useVTTStore.getState().revealFogCells(newCells)
      } else {
        useVTTStore.getState().hideFogCells(newCells)
      }

      // Throttle server updates to 100ms
      const now = performance.now()
      if (now - fogState.lastUpdateTime >= 100) {
        if (fogMode === 'reveal' && onFogRevealRef.current) {
          onFogRevealRef.current(currentMap.id, newCells)
        } else if (fogMode === 'hide' && onFogHideRef.current) {
          onFogHideRef.current(currentMap.id, newCells)
        }
        fogState.lastUpdateTime = now
      }
    }

    // Fog brush pointer up handler (Phase 4)
    const handleFogBrushUp = () => {
      const fogState = fogBrushStateRef.current
      if (!fogState.isDrawing) return

      fogState.isDrawing = false

      // Send any remaining accumulated cells that weren't sent due to throttling
      if (fogState.accumulatedCells.size > 0) {
        const mode = toolModeRef.current
        const fogMode = useVTTStore.getState().fogMode
        const currentMap = mapRef.current

        // Get all cells from accumulated set
        const allCells: Array<{ x: number; y: number }> = []
        fogState.accumulatedCells.forEach((key) => {
          const [xStr, yStr] = key.split(',')
          allCells.push({ x: parseInt(xStr, 10), y: parseInt(yStr, 10) })
        })

        // Send final update (only if still in fog mode)
        if (mode === 'fog' && fogMode === 'reveal' && onFogRevealRef.current) {
          onFogRevealRef.current(currentMap.id, allCells)
        } else if (mode === 'fog' && fogMode === 'hide' && onFogHideRef.current) {
          onFogHideRef.current(currentMap.id, allCells)
        }
      }

      fogState.accumulatedCells.clear()
      fogState.lastGridX = null
      fogState.lastGridY = null

      console.log('[VTTCanvas] Fog brush ended')
    }

    // Stage click handler: barrier placement (Phase 3) + shift+click waypoint queue (Bug 2)
    const handleStageClick = (event: PIXI.FederatedPointerEvent) => {
      console.log('[VTTCanvas] Stage clicked! toolMode:', toolModeRef.current)

      // Close any open tooltip when clicking on stage
      setTooltipState(null)

      // A click that reaches the stage (rather than a light object, which stops
      // propagation) is empty space → deselect any selected light.
      if (!lightPointerDownRef.current) setSelectedLightId(null)

      // ── Shift+click waypoint queuing (select mode, ruler inactive) ──────────
      // Each shift+click in select mode appends a grid cell to the pending path
      // for the selected token. The path is visualised on the ghost layer using
      // the same blue-line + ghost-circle style as drag waypoints.
      // A non-shift click commits the path with all queued cells as intermediate
      // stops and the click position as the final destination.
      if (
        event.shiftKey &&
        toolModeRef.current === 'select' &&
        !rulerKeyRef.current.isActive
      ) {
        // Waypoint queue belongs to a single token. With multi-select
        // active, shift+click on the canvas does nothing — the
        // rubber-band-with-shift path (handleStagePointerDown) owns
        // the shift+drag gesture in that case.
        const store = useVTTStore.getState()
        const selectedId =
          store.selectedTokenIds.length === 1 ? store.selectedTokenIds[0] : null
        if (selectedId) {
          const token = store.tokens.find(t => t.id === selectedId)
          if (token && canMoveTokenRef.current(token)) {
            const globalPos = event.global
            const currentMap = mapRef.current
            let gridPos = pixelToGrid(globalPos.x, globalPos.y, currentMap.gridSize)
            gridPos = clampToMapBounds(gridPos.x, gridPos.y, currentMap.width, currentMap.height)

            // Deduplicate against last queued waypoint and token origin
            const last = pendingWaypointsRef.current[pendingWaypointsRef.current.length - 1]
            const isDiff = !last || last.x !== gridPos.x || last.y !== gridPos.y
            const notOrigin = gridPos.x !== token.x || gridPos.y !== token.y
            if (isDiff && notOrigin) {
              pendingWaypointsRef.current.push(gridPos)
              console.log(`[VTTCanvas] Shift+click waypoint queued at (${gridPos.x}, ${gridPos.y}); total: ${pendingWaypointsRef.current.length}`)

              // Render the accumulated path on the ghost layer. The last queued
              // cell is passed as the "cursor" position (ghost circle target);
              // all preceding cells are intermediate waypoints.
              const ghostLayer = ghostLayerRef.current
              if (ghostLayer && renderGhostRef.current) {
                const pWaypoints = pendingWaypointsRef.current
                const lastWp = pWaypoints[pWaypoints.length - 1]
                const lastPixel = gridToPixel(lastWp.x, lastWp.y, currentMap.gridSize)
                const intermediates = pWaypoints.slice(0, -1)
                renderGhostRef.current(ghostLayer, token, lastPixel.x, lastPixel.y, intermediates)
              }
            }
          }
        }
        return
      }

      // ── Non-shift click: commit pending waypoint path ────────────────────────
      // The click position becomes the final destination; all queued cells are
      // intermediate stops sent via the existing onTokenMove socket path.
      if (
        !event.shiftKey &&
        toolModeRef.current === 'select' &&
        pendingWaypointsRef.current.length > 0
      ) {
        const store = useVTTStore.getState()
        const selectedId =
          store.selectedTokenIds.length === 1 ? store.selectedTokenIds[0] : null
        if (selectedId) {
          const globalPos = event.global
          const currentMap = mapRef.current
          let gridPos = pixelToGrid(globalPos.x, globalPos.y, currentMap.gridSize)
          gridPos = clampToMapBounds(gridPos.x, gridPos.y, currentMap.width, currentMap.height)

          const intermediates = pendingWaypointsRef.current
          console.log(`[VTTCanvas] Committing shift+click path: ${intermediates.length} waypoint(s) → final (${gridPos.x}, ${gridPos.y})`)
          moveTokenRef.current(selectedId, gridPos.x, gridPos.y, intermediates)
          if (onTokenMoveRef.current) {
            onTokenMoveRef.current(selectedId, gridPos.x, gridPos.y, intermediates)
          }

          pendingWaypointsRef.current = []
          const ghostLayer = ghostLayerRef.current
          if (ghostLayer) ghostLayer.clear()
        }
        return
      }

      // ── Light source placement (GM only, 'light' tool mode) ─────────────────
      if (toolModeRef.current === 'light') {
        if (userRole !== 'gm') return
        // LEFT button only — middle/right are pan/context and must never drop a
        // light (#554). Touch taps report button 0, so touch placement still works.
        if (!shouldPlaceForButton(event.button)) return
        // Tapping an existing light selects/drags it (handled on the light object,
        // which set this flag) — don't also spawn a new one underneath.
        if (lightPointerDownRef.current) return
        const worldPos = screenToWorld({ x: event.global.x, y: event.global.y }, viewportRef.current)
        const currentMap = mapRef.current
        // Place at fractional grid coords (center of nearest cell)
        const gridX = Math.floor(worldPos.x / currentMap.gridSize)
        const gridY = Math.floor(worldPos.y / currentMap.gridSize)
        const clampedX = Math.max(0, Math.min(currentMap.width - 1, gridX))
        const clampedY = Math.max(0, Math.min(currentMap.height - 1, gridY))
        socketService.createLightSource(currentMap.id, clampedX, clampedY)
        console.log('[VTTCanvas] Light source placed at', clampedX, clampedY)
        return
      }

      // ── Barrier placement (existing behavior) ────────────────────────────────
      if (toolModeRef.current !== 'barrier') {
        console.log('[VTTCanvas] Not in barrier mode, ignoring click')
        return
      }

      // Barrier tool is GM-only (server also enforces). Guard the placement
      // path in case a non-GM reached barrier mode via direct store access.
      if (!canUseBarrierTool(userRole)) {
        return
      }

      console.log('[VTTCanvas] In barrier mode, processing click...')

      // event.global is SCREEN space. Convert through the viewport transform
      // (scale + pan + the #101 container-centering offset) to world pixels
      // before snapping, so vertices land under the cursor at any zoom and on
      // scaled/resized maps. This mirrors the fog-brush and token-drag paths;
      // barrier placement was the one tool still reading event.global raw.
      const worldPos = screenToWorld(
        { x: event.global.x, y: event.global.y },
        viewportRef.current,
      )

      console.log('[VTTCanvas] Click at world position:', worldPos)

      // Snap to the nearest grid corner (intersection), not the cell it falls in.
      const currentMap = mapRef.current
      const cornerX = Math.round(worldPos.x / currentMap.gridSize)
      const cornerY = Math.round(worldPos.y / currentMap.gridSize)
      const clampedPos = clampToMapBounds(cornerX, cornerY, currentMap.width, currentMap.height)

      // Add vertex
      barrierVerticesRef.current.push(clampedPos)
      console.log(`[VTTCanvas] Barrier vertex added at (${clampedPos.x}, ${clampedPos.y})`)
      console.log('[VTTCanvas] Total vertices:', barrierVerticesRef.current.length)

      // Render preview
      if (renderBarrierPreviewRef.current) {
        console.log('[VTTCanvas] Rendering barrier preview...')
        renderBarrierPreviewRef.current()
      } else {
        console.log('[VTTCanvas] No renderBarrierPreviewRef!')
      }
    }

    // Helper function to complete barrier
    const completeBarrier = () => {
      // Barrier tool is GM-only (server also enforces).
      if (!canUseBarrierTool(userRole)) {
        return
      }
      if (barrierVerticesRef.current.length < 2) {
        console.log('[VTTCanvas] Need at least 2 vertices to complete a wall')
        return
      }

      console.log(`[VTTCanvas] Barrier completed with ${barrierVerticesRef.current.length} vertices`)

      // Create barrier object
      const barrier = {
        id: `barrier_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        mapId: mapRef.current.id,
        points: [...barrierVerticesRef.current],
        closed: false, // new walls are open polylines (no closing edge)
      }

      // Save to store
      addBarrier(barrier)
      console.log('[VTTCanvas] Barrier saved:', barrier)

      // Send to server via WebSocket
      if (onBarrierCreateRef.current) {
        onBarrierCreateRef.current(barrier.mapId, barrier.points, barrier.closed)
      }

      // Clear preview
      barrierVerticesRef.current = []
      if (renderBarrierPreviewRef.current) {
        renderBarrierPreviewRef.current()
      }
    }

    // Stage pointer down handler. Forwards to the fog brush when in
    // fog modes, and starts a rubber-band drag-select when in select
    // mode on empty canvas. Token containers consume their own
    // pointerdown before this fires for them, so `event.target ===
    // stage` is a reliable proxy for "no token under the cursor".
    const handleStagePointerDown = (event: PIXI.FederatedPointerEvent) => {
      handleFogBrushDown(event)

      if (event.button === 2) return // right-click reserved for tooltip / barrier complete
      if (toolModeRef.current !== 'select') return
      if (rulerKeyRef.current.isActive) return
      if (dragStateRef.current?.isDragging) return
      if (!appRef.current) return
      if (event.target !== appRef.current.stage) return

      // Pending shift+click path? Don't hijack — the user is committing
      // a route, not starting a selection. The stage `click` handler
      // owns that branch.
      if (pendingWaypointsRef.current.length > 0) return

      // event.global is screen space; the rubber-band layer is a child of
      // the viewport-transformed stage and tokens live in world space, so
      // store the marquee in WORLD space (inverse viewport transform).
      const startWorld = screenToWorld(
        { x: event.global.x, y: event.global.y },
        viewportRef.current,
      )
      const baseSelection = useVTTStore.getState().selectedTokenIds

      rubberBandRef.current = {
        isActive: true,
        startX: startWorld.x,
        startY: startWorld.y,
        currentX: startWorld.x,
        currentY: startWorld.y,
        shiftHeld: event.shiftKey,
        baseSelection: event.shiftKey ? baseSelection.slice() : [],
      }

      // Long-press ping: arm a timer alongside the rubber-band. If the
      // pointer stays still (handleMove cancels on movement past tolerance)
      // and the press isn't released early (handleUp cancels), the timer
      // fires a ping at the press-down world position and tears down the
      // (unused) rubber-band so no stray selection rect is committed. A
      // shift-press is a deliberate additive-marquee gesture, never a ping.
      if (!event.shiftKey) {
        pingPressRef.current = {
          timer: null,
          startScreenX: event.global.x,
          startScreenY: event.global.y,
          worldX: startWorld.x,
          worldY: startWorld.y,
          fired: false,
        }
        pingPressRef.current.timer = setTimeout(() => {
          const pp = pingPressRef.current
          if (!pp) return
          // Decision encoded by classifyLeftPress: held past threshold, on
          // empty canvas, with no movement (movement would have cleared this
          // timer already) → 'ping'.
          const outcome = classifyLeftPress({
            heldMs: PING_LONG_PRESS_MS,
            movedPx: 0,
            onToken: false,
          })
          pp.fired = true
          pp.timer = null
          if (outcome !== 'ping') {
            pingPressRef.current = null
            return
          }
          // Cancel the rubber-band — this gesture is a ping, not a select.
          rubberBandRef.current = null
          if (rubberBandLayerRef.current) rubberBandLayerRef.current.clear()
          window.removeEventListener('pointermove', windowHandlersRef.current.handleMove as EventListener)
          window.removeEventListener('pointerup', windowHandlersRef.current.handleUp as EventListener)

          const currentMap = mapRef.current
          const id = `${currentUserIdRef.current ?? 'me'}:${Date.now()}`
          // Add locally for instant feedback, then relay. The server echoes
          // ping_placed to the whole room but the client skips its own echo.
          useVTTStore.getState().addPing({
            id,
            userId: currentUserIdRef.current ?? 'me',
            mapId: currentMap.id,
            x: pp.worldX,
            y: pp.worldY,
            createdAt: performance.now(),
          })
          socketService.emitPingPlace({ mapId: currentMap.id, x: pp.worldX, y: pp.worldY })
          pingPressRef.current = null
        }, PING_LONG_PRESS_MS)
      }

      // Attach window listeners so the drag follows cursor outside
      // the canvas too. Cleaned up in handleRubberBandUp and on
      // Escape via the keyboard handler.
      if (windowHandlersRef.current.handleMove && windowHandlersRef.current.handleUp) {
        window.addEventListener('pointermove', windowHandlersRef.current.handleMove)
        window.addEventListener('pointerup', windowHandlersRef.current.handleUp)
      }
    }

    /**
     * Repaint the rubber-band rectangle. Drawn as a series of short
     * line segments to approximate a dashed border — PIXI.Graphics
     * doesn't ship a native dashed stroke. Filled with a faint blue
     * tint so the selected region is visible without obscuring the
     * tokens under it.
     */
    const drawRubberBand = () => {
      const layer = rubberBandLayerRef.current
      if (!layer) return
      layer.clear()
      const rb = rubberBandRef.current
      if (!rb || !rb.isActive) return

      const x = Math.min(rb.startX, rb.currentX)
      const y = Math.min(rb.startY, rb.currentY)
      const w = Math.abs(rb.currentX - rb.startX)
      const h = Math.abs(rb.currentY - rb.startY)

      // Tint fill
      layer.beginFill(0x3498db, 0.08)
      layer.drawRect(x, y, w, h)
      layer.endFill()

      // Dashed border. 8-pixel dashes with 4-pixel gaps.
      const DASH = 8
      const GAP = 4
      const drawDashedSegment = (
        x1: number,
        y1: number,
        x2: number,
        y2: number,
      ) => {
        const dx = x2 - x1
        const dy = y2 - y1
        const len = Math.hypot(dx, dy)
        if (len === 0) return
        const ux = dx / len
        const uy = dy / len
        let drawn = 0
        layer.lineStyle({ width: 1.5, color: 0x3498db, alpha: 0.9 })
        while (drawn < len) {
          const segLen = Math.min(DASH, len - drawn)
          layer.moveTo(x1 + ux * drawn, y1 + uy * drawn)
          layer.lineTo(x1 + ux * (drawn + segLen), y1 + uy * (drawn + segLen))
          drawn += segLen + GAP
        }
      }

      drawDashedSegment(x, y, x + w, y)
      drawDashedSegment(x + w, y, x + w, y + h)
      drawDashedSegment(x + w, y + h, x, y + h)
      drawDashedSegment(x, y + h, x, y)
    }

    /**
     * Resolve which tokens fall inside the rubber-band rect and
     * commit the selection. Empty drag (no movement past a small
     * threshold) is treated as a click-on-empty and clears the
     * selection. Shift-held drag unions with the prior selection.
     */
    const finishRubberBand = () => {
      const rb = rubberBandRef.current
      if (!rb || !rb.isActive) return
      rubberBandRef.current = null

      const layer = rubberBandLayerRef.current
      if (layer) layer.clear()

      const dragDistance = Math.hypot(rb.currentX - rb.startX, rb.currentY - rb.startY)
      // Click-on-empty (no real drag): clear selection unless shift
      // was held (shift+click on empty is a no-op). Clears both tokens
      // and any selected barrier — clicking off a barrier deselects it.
      if (dragDistance < DRAG_THRESHOLD) {
        if (!rb.shiftHeld) {
          const store = useVTTStore.getState()
          store.clearSelectedTokens()
          if (store.selectedBarrierId) store.selectBarrier(null)
          // Clicking off in select mode also drops a selected light so its HUD
          // goes away. This is the authoritative empty-click path on both mouse
          // and touch (PIXI 'click' is mouse-only); the separate handleStageClick
          // clear only covers placement-tool taps.
          if (store.selectedLightId) store.setSelectedLightId(null)
        }
        return
      }

      const currentMap = mapRef.current
      const activeMapId = currentMap.id
      const store = useVTTStore.getState()
      // Hit-test in WORLD space: rb coords were converted via the inverse
      // viewport transform on pointerdown/move, and token positions are
      // world-space (grid * gridSize). Tokens the caller can't see are
      // skipped — the user shouldn't be able to select fog-hidden tokens.
      const candidates = store.tokens.filter(
        (t) => t.mapId === activeMapId && isTokenVisibleRef.current(t),
      )
      const hits = marqueeHitTest(
        { x1: rb.startX, y1: rb.startY, x2: rb.currentX, y2: rb.currentY },
        candidates,
        currentMap.gridSize,
      )

      if (rb.shiftHeld) {
        // Union with the snapshot taken at drag-start. Use a Set to
        // dedupe — order from the base wins so the user's prior
        // selection isn't reshuffled.
        const merged = rb.baseSelection.slice()
        const seen = new Set(merged)
        hits.forEach((id) => {
          if (!seen.has(id)) {
            merged.push(id)
            seen.add(id)
          }
        })
        useVTTStore.getState().setSelectedTokenIds(merged)
      } else {
        useVTTStore.getState().setSelectedTokenIds(hits)
      }
    }

    // Right-click handler to complete barrier (Phase 3)
    const handleStageRightClick = (event: PIXI.FederatedPointerEvent) => {
      console.log('[VTTCanvas] Stage right-clicked')
      // Always prevent default context menu
      event.preventDefault()

      // Close any open tooltip when right-clicking on stage
      setTooltipState(null)

      if (toolModeRef.current !== 'barrier') return

      completeBarrier()
    }

    // Brush preview handler (Phase 4)
    const handleBrushPreview = (event: PIXI.FederatedPointerEvent) => {
      const brushPreviewLayer = brushPreviewLayerRef.current
      if (!brushPreviewLayer) return

      brushPreviewLayer.clear()

      // Only show brush preview in fog mode
      const mode = toolModeRef.current
      if (mode !== 'fog') return
      const fogMode = useVTTStore.getState().fogMode

      // Convert screen-space pointer to world space so the preview lines up
      // with the cells the brush will actually paint at any zoom/pan.
      const worldPos = screenToWorld(
        { x: event.global.x, y: event.global.y },
        viewportRef.current
      )

      const currentMap = mapRef.current
      const gridPos = pixelToGrid(worldPos.x, worldPos.y, currentMap.gridSize)

      // Get brush size and calculate cells
      const brushSize = useVTTStore.getState().brushSize
      const cells = getCellsInRadius(gridPos.x, gridPos.y, brushSize)

      // Draw preview circles for each cell
      const previewColor = fogMode === 'reveal' ? 0x00ff00 : 0xff0000 // Green for reveal, red for hide
      cells.forEach((cell) => {
        const pixelX = cell.x * currentMap.gridSize
        const pixelY = cell.y * currentMap.gridSize
        brushPreviewLayer.lineStyle({ width: 2, color: previewColor, alpha: 0.5 })
        brushPreviewLayer.drawRect(pixelX, pixelY, currentMap.gridSize, currentMap.gridSize)
      })
    }

    // Debug: log all click events on the stage
    app.stage.on('pointerdown', (event) => {
      console.log('[VTTCanvas] Stage pointerdown:', {
        target: event.target,
        eventMode: (event.target as any)?.eventMode,
        toolMode: toolModeRef.current
      })
    })

    // Attach stage listeners (for when pointer is over canvas)
    app.stage.on('pointerdown', handleStagePointerDown) // Phase 4: fog brush
    app.stage.on('pointermove', (event) => {
      handleMove(event)
      handleBrushPreview(event) // Phase 4: update brush preview
    })
    app.stage.on('pointerup', handleUp)
    app.stage.on('pointerupoutside', handleUpOutside)
    app.stage.on('click', handleStageClick) // Phase 3: barrier placement
    // Touch placement: PIXI 'click' is mouse-only, so barrier/light placement
    // was dead on touch. Route placement through 'pointertap' (fires on tap),
    // but ONLY in a placement tool mode — select-mode taps are left untouched
    // so token select/drag aren't perturbed (the earlier regression).
    app.stage.on('pointertap', (event: PIXI.FederatedPointerEvent) => {
      const tm = toolModeRef.current
      if (tm === 'barrier' || tm === 'light') handleStageClick(event)
    })
    app.stage.on('rightclick', handleStageRightClick) // Phase 3b: complete barrier with right-click

    // Debug: log when click fires
    console.log('[VTTCanvas] Stage click listener attached')

    // Debug: Add native DOM click listener to see if clicks reach canvas
    const canvasElement = app.view as HTMLCanvasElement
    const debugClickHandler = (event: MouseEvent) => {
      console.log('[VTTCanvas] Native canvas click detected!', {
        x: event.clientX,
        y: event.clientY,
        target: event.target,
        toolMode: toolModeRef.current
      })
    }
    canvasElement.addEventListener('click', debugClickHandler)
    console.log('[VTTCanvas] Native canvas click listener attached')

    // CONTAINER-DRIVEN RENDERER SIZE. The renderer surface tracks the
    // container, not the map — so the visible "tabletop" never resizes itself
    // when the map's pixel dimensions change (facet #3). When the container
    // changes size (sidebar toggle, window resize, layout reflow) we resize the
    // renderer to match and keep the world content centered. We re-center
    // (rather than preserve pan) on resize because the offset that centered the
    // old viewport is wrong for the new one; preserving pan is a possible
    // future refinement but recentering is the predictable default.
    const container = canvasRef.current
    let lastW = container?.clientWidth ?? 0
    let lastH = container?.clientHeight ?? 0
    const resizeToContainer = (w: number, h: number) => {
      if (w <= 0 || h <= 0) return
      // #494: ALWAYS re-sync the stage hit-area, even when w/h are unchanged.
      // The dedup guard below only skips the expensive renderer.resize + recenter
      // — it must never strand a stale/short hit-area. (Belt-and-suspenders: the
      // always-true hit-area is transform-independent, but keeping the re-sync
      // outside the early-return guarantees the interactive region is whole after
      // any mount-race that seeded a short canvas.)
      app.stage.hitArea = { contains: () => true }
      if (w === lastW && h === lastH) return
      lastW = w
      lastH = h
      app.renderer.resize(w, h)
      // Re-center the world content for the new viewport size, preserving the
      // current zoom level.
      const m = mapRef.current
      const v = viewportRef.current
      const offset = computeCenterOffset(w, h, m.width * m.gridSize, m.height * m.gridSize, v.scale)
      viewportRef.current = { scale: v.scale, x: offset.x, y: offset.y }
      app.stage.position.set(offset.x, offset.y)
      setViewportSnapshot({ scale: v.scale, x: offset.x, y: offset.y })
      setCanvasSize({ width: w, height: h })
    }
    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (!entry) return
      // #494: prefer borderBoxSize when the UA reports it — it tracks the box the
      // canvas actually fills and rounds more predictably than contentRect on
      // sub-integer settles, which is what left a short bottom strip. Fall back to
      // contentRect where borderBoxSize is unavailable.
      const box = entry.borderBoxSize?.[0]
      const w = box ? box.inlineSize : entry.contentRect.width
      const h = box ? box.blockSize : entry.contentRect.height
      resizeToContainer(Math.round(w), Math.round(h))
    })
    if (container) resizeObserver.observe(container)
    // Window resize is covered by ResizeObserver on the container, but keep a
    // window listener as a belt-and-suspenders fallback for environments where
    // the container's box doesn't change but the device pixel ratio / layout
    // does.
    const handleWindowResize = () => {
      if (container) resizeToContainer(container.clientWidth, container.clientHeight)
    }
    window.addEventListener('resize', handleWindowResize)
    // #494: one-shot deferred re-measure after mount. A layout race can seed the
    // renderer/canvas a few rows short before the ResizeObserver's first callback
    // settles; re-measuring on the next frame from the real laid-out container
    // corrects the short size (and, via clientToScreen using canvas.width, the
    // bottom-row pointer→world mapping) before the first press lands.
    requestAnimationFrame(() => {
      const c = canvasRef.current
      if (c) resizeToContainer(c.clientWidth, c.clientHeight)
    })
    // Seed canvasSize from the measured container so overlays (ruler) have a
    // real viewport size immediately, not the map fallback.
    setCanvasSize({ width: initialW, height: initialH })

    // Cleanup - ONLY on unmount
    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', handleWindowResize)
      console.log('[VTTCanvas] Cleaning up PixiJS (component unmounting)');
      setCanvasReady(false)

      // Remove any lingering window listeners
      if (windowHandlersRef.current.handleMove && windowHandlersRef.current.handleUp) {
        window.removeEventListener('pointermove', windowHandlersRef.current.handleMove)
        window.removeEventListener('pointerup', windowHandlersRef.current.handleUp)
      }

      // Remove keyboard listeners
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', handleKeyUp)

      // Remove touch listeners
      const canvas = app.view as HTMLCanvasElement
      canvas.removeEventListener('contextmenu', handleContextMenu)
      canvas.removeEventListener('touchstart', handleTouchStart)
      canvas.removeEventListener('touchmove', handleTouchMove)
      canvas.removeEventListener('touchend', handleTouchEnd)
      canvas.removeEventListener('wheel', handleWheel)
      canvas.removeEventListener('pointerdown', handlePanDown, { capture: true } as EventListenerOptions)
      canvas.removeEventListener('auxclick', handleAuxClick)
      window.removeEventListener('pointermove', handlePanMove)
      window.removeEventListener('pointerup', handlePanUp)

      // Remove debug click listener
      canvas.removeEventListener('click', debugClickHandler)

      app.destroy(true, { children: true })
    }
  }, []) // Empty deps - only run on mount/unmount

  /**
   * Reactively (re)load the map background image.
   *
   * The init effect above runs once, so it can't render a background that is
   * set after the canvas mounts (uploaded mid-session), that arrives via
   * session_state hydration after init, or that changes on a map switch.
   * Keyed on backgroundUrl + map identity/size so the sprite repaints whenever
   * any of those change. backgroundUrl is persisted on the Map row and rehydrated
   * on rejoin, so this also restores the background when the map is reopened later.
   */
  useEffect(() => {
    if (!canvasReady) return
    const layer = backgroundLayerRef.current
    if (!layer) return

    // Clear any previous background sprite before (re)painting.
    layer.removeChildren().forEach((child) => child.destroy())

    backgroundSpriteRef.current = null
    backgroundGreyscaleRef.current = null
    backgroundMaskRef.current = null
    backgroundBorderRef.current = null
    darkvisionGreyRef.current = null
    darkvisionGreyMaskRef.current = null

    if (!map?.backgroundUrl) return

    let cancelled = false
    const url = map.backgroundUrl
    const currentMap = map

    PIXI.Assets.load(url)
      .then((texture) => {
        // Bail if the map changed or the canvas was torn down mid-load.
        if (cancelled || backgroundLayerRef.current !== layer) return

        // Two stacked copies of the background so off-grid areas read as
        // out-of-bounds: a desaturated underlay covering the whole image, and
        // a full-color copy on top masked to the grid rect. Anything beyond
        // the grid falls through to the greyscale underlay. A border stroke at
        // the grid edge marks where the play area ends.
        //
        // GM-only: the greyscale underlay is the "gray overflow" beyond the
        // grid. Players should see ONLY the in-grid image (the masked color
        // sprite), so the underlay is hidden for non-GM viewers. The color
        // sprite stays masked to the grid for everyone, so off-grid simply
        // reads as void for players. A live effect below keeps this in sync if
        // userRole changes. (Tokens/fog/barriers are separate layers and are
        // intentionally left unmasked — only this background underlay is gated.)
        const greyscale = new PIXI.Sprite(texture)
        const desaturate = new PIXI.ColorMatrixFilter()
        desaturate.desaturate()
        greyscale.filters = [desaturate]
        greyscale.visible = userRole === 'gm'
        applyBackgroundTransform(greyscale, currentMap)

        const color = new PIXI.Sprite(texture)
        applyBackgroundTransform(color, currentMap)
        const mask = new PIXI.Graphics()
        const border = new PIXI.Graphics()
        paintGridBounds(mask, border, currentMap.width * currentMap.gridSize, currentMap.height * currentMap.gridSize)
        color.mask = mask

        backgroundGreyscaleRef.current = greyscale
        backgroundSpriteRef.current = color
        backgroundMaskRef.current = mask
        backgroundBorderRef.current = border

        layer.addChild(greyscale)
        layer.addChild(color)
        layer.addChild(mask) // mask must live in the display tree to clip
        layer.addChild(border)

        // Darkvision reveal: a third desaturated copy, on TOP of the colour
        // sprite, masked each render to (darkvision LoS minus lit regions). It
        // shows the map in greyscale where a darkvision token sees into the
        // dark, while lit areas fall through to the colour sprite below.
        const dvGrey = new PIXI.Sprite(texture)
        const dvDesaturate = new PIXI.ColorMatrixFilter()
        dvDesaturate.desaturate()
        dvGrey.filters = [dvDesaturate]
        dvGrey.visible = false
        applyBackgroundTransform(dvGrey, currentMap)
        const dvMask = new PIXI.Graphics()
        dvGrey.mask = dvMask
        darkvisionGreyRef.current = dvGrey
        darkvisionGreyMaskRef.current = dvMask
        layer.addChild(dvGrey)
        layer.addChild(dvMask) // mask must live in the display tree to clip
      })
      .catch((error) => {
        console.error('[VTTCanvas] Failed to load background image:', error)
      })

    return () => {
      cancelled = true
    }
  }, [canvasReady, map?.backgroundUrl, map?.id])

  // Live-apply offset/scale to the existing background sprites without
  // reloading, and repaint the grid-bounds mask + border (their size tracks
  // map width/height/gridSize).
  useEffect(() => {
    const color = backgroundSpriteRef.current
    if (!color || !map) return
    applyBackgroundTransform(color, map)
    const greyscale = backgroundGreyscaleRef.current
    if (greyscale) applyBackgroundTransform(greyscale, map)
    const dvGrey = darkvisionGreyRef.current
    if (dvGrey) applyBackgroundTransform(dvGrey, map)
    const mask = backgroundMaskRef.current
    const border = backgroundBorderRef.current
    if (mask && border) {
      paintGridBounds(mask, border, map.width * map.gridSize, map.height * map.gridSize)
    }
  }, [map?.width, map?.height, map?.gridSize, map?.backgroundOffsetX, map?.backgroundOffsetY, map?.backgroundScaleX, map?.backgroundScaleY, map?.backgroundFit])

  // Gate the greyscale off-grid underlay on role: GM sees the gray overflow,
  // players see only the in-grid (masked color) image. Kept as its own effect
  // so a role flip doesn't force a texture reload (the heavy load effect is
  // keyed on backgroundUrl/map.id, not userRole).
  useEffect(() => {
    const greyscale = backgroundGreyscaleRef.current
    if (greyscale) greyscale.visible = userRole === 'gm'
  }, [userRole, map?.backgroundUrl, map?.id])

  // Live-react to grid resolution + map size changes.
  //
  // The init effect runs once with empty deps, so it paints the grid a single
  // time. When the GM edits gridSize / width / height (or any grid-appearance
  // field) from the Map config panel, the map_updated broadcast updates the
  // store, but the grid lines don't repaint on their own. This effect redraws
  // (or clears) the grid layer so the change is visible immediately.
  //
  // RECONCILED WITH PR #93: #93 originally called
  //   app.renderer.resize(map.width*gridSize, map.height*gridSize)
  // here, which made the visible canvas/tabletop grow and shrink with the map
  // (facet #3 — confusing self-resize). The renderer size is now owned by the
  // container ResizeObserver in the init effect; the renderer sizes to the
  // CONTAINER, never to the map. A map-dimension change must NOT resize the
  // canvas element — it only changes the world content + pan/zoom bounds. So
  // the renderer.resize call is intentionally removed here. We do re-center the
  // world content for the new map size at the current zoom (the world's extent
  // changed, so the centering offset changed), preserving the container-as-
  // viewport contract.
  useEffect(() => {
    if (!canvasReady || !map) return
    const app = appRef.current
    const gridLayer = gridLayerRef.current
    if (!app || !gridLayer) return

    // Re-center world content for the (possibly new) map extent without
    // touching the renderer size. app.screen is the logical (CSS-pixel)
    // viewport — the same space the stage position/scale operate in (use this,
    // not renderer.width/height, which are device pixels under autoDensity).
    const v = viewportRef.current
    const offset = computeCenterOffset(
      app.screen.width,
      app.screen.height,
      map.width * map.gridSize,
      map.height * map.gridSize,
      v.scale,
    )
    viewportRef.current = { scale: v.scale, x: offset.x, y: offset.y }
    app.stage.position.set(offset.x, offset.y)
    setViewportSnapshot({ scale: v.scale, x: offset.x, y: offset.y })

    gridLayer.clear()
    if (map.showGrid) {
      drawGrid(
        gridLayer,
        map.width,
        map.height,
        map.gridSize,
        map.gridColor,
        map.gridOpacity,
        map.gridOffsetX,
        map.gridOffsetY
      )
    }
  }, [
    canvasReady,
    map?.width,
    map?.height,
    map?.gridSize,
    map?.showGrid,
    map?.gridColor,
    map?.gridOpacity,
    map?.gridOffsetX,
    map?.gridOffsetY,
  ])

  /**
   * Detect token position changes and start animations
   */
  useEffect(() => {
    tokens.forEach((token) => {
      const previous = previousPositionsRef.current.get(token.id)

      if (previous && (previous.x !== token.x || previous.y !== token.y)) {
        // Position changed - check if we should animate
        const isDragging = dragStateRef.current?.tokenId === token.id

        if (!isDragging) {
          // Not currently being dragged by this client - animate the movement
          // If already animating, cancel the old animation and start a new one (interrupt)
          const alreadyAnimating = animationsRef.current.has(token.id)
          if (alreadyAnimating) {
            // Get current animation position as the starting point for the new animation
            const currentAnimation = animationsRef.current.get(token.id)!
            const elapsed = performance.now() - currentAnimation.startTime
            const progress = Math.min(elapsed / currentAnimation.duration, 1)
            const currentX = currentAnimation.fromX + (currentAnimation.toX - currentAnimation.fromX) * progress
            const currentY = currentAnimation.fromY + (currentAnimation.toY - currentAnimation.fromY) * progress
            // Update previous position to current animation position
            previousPositionsRef.current.set(token.id, { x: currentX, y: currentY })
          }

          // Check if there are waypoints for this token from network
          const waypoints = tokenWaypoints.get(token.id)

          // Check if this is an instant move (keyboard or server instant)
          // Phase 3c: Check both local keyboard moves AND instant moves from server
          const isKeyboardMove = keyboardMoveRef.current.has(token.id)
          const isInstantMove = instantMoves.has(token.id)
          const isInstant = isKeyboardMove || isInstantMove
          const moveDuration = isInstant ? animationDuration * 100 : animationDuration * 1000 // 0.1s for instant, 1s for drag

          // Get the actual starting position (either previous or current animation position)
          const startPos = previousPositionsRef.current.get(token.id) || previous

          if (waypoints && waypoints.length > 0) {
            // Animate through waypoints
            console.log(`[VTTCanvas] Starting waypoint animation for token ${token.id} with ${waypoints.length} waypoints`)

            // Build animation path: current position → waypoint1 → waypoint2 → ... → final position
            const allWaypoints = [...waypoints, { x: token.x, y: token.y }]
            const firstWaypoint = allWaypoints.shift()!

            const animation: TokenAnimation = {
              fromX: startPos.x,
              fromY: startPos.y,
              toX: firstWaypoint.x,
              toY: firstWaypoint.y,
              startTime: performance.now(),
              duration: moveDuration,
              waypoints: allWaypoints, // Remaining waypoints
            }
            animationsRef.current.set(token.id, animation)

            // Clear waypoints from store
            clearWaypoints(token.id)
          } else {
            // Simple animation without waypoints
            const animation: TokenAnimation = {
              fromX: startPos.x,
              fromY: startPos.y,
              toX: token.x,
              toY: token.y,
              startTime: performance.now(),
              duration: moveDuration, // 0.1s for keyboard, 1s for drag
            }
            animationsRef.current.set(token.id, animation)
            console.log(`[VTTCanvas] Starting ${isInstant ? 'instant' : 'normal'} animation for token ${token.id} from (${startPos.x}, ${startPos.y}) to (${token.x}, ${token.y})`)
          }

          // Clear instant move flags
          if (isKeyboardMove) {
            keyboardMoveRef.current.delete(token.id)
          }
          if (isInstantMove) {
            clearInstantMove(token.id)
          }
        }
      }

      // Update previous positions
      previousPositionsRef.current.set(token.id, { x: token.x, y: token.y })
    })

    // Clean up removed tokens
    const currentTokenIds = new Set(tokens.map(t => t.id))
    Array.from(previousPositionsRef.current.keys()).forEach(tokenId => {
      if (!currentTokenIds.has(tokenId)) {
        previousPositionsRef.current.delete(tokenId)
        animationsRef.current.delete(tokenId)
        tokenContainersRef.current.delete(tokenId)
      }
    })
  }, [tokens, animationDuration, tokenWaypoints, clearWaypoints, instantMoves, clearInstantMove])

  /**
   * Render tokens when they change
   */
  useEffect(() => {
    console.log('[VTTCanvas] Token render effect triggered!', {
      tokenCount: tokens.length,
      canvasReady,
      hasContainer: !!tokensContainerRef.current,
      tokens: tokens.map(t => ({ id: t.id, name: t.name, x: t.x, y: t.y }))
    });

    if (!tokensContainerRef.current) {
      console.warn('[VTTCanvas] tokensContainerRef not ready yet, skipping render');
      return;
    }

    console.log('[VTTCanvas] Actually rendering', tokens.length, 'tokens to canvas');

    const container = tokensContainerRef.current
    container.removeChildren()
    tokenContainersRef.current.clear()
    // Rebuild per-token HP pill and hover maps so stale refs don't leak.
    tokenHpPillsRef.current.clear()
    hoveredTokenIdsRef.current.clear()
    persistentHpPillIdsRef.current.clear()

    // New render pass — invalidate any in-flight token-image loads from a
    // previous pass so their textures don't get added to detached containers.
    const renderGeneration = ++tokenRenderGenRef.current

    // Phase 3c: Render both visible tokens and last-seen tokens
    const renderedTokenIds = new Set<string>()

    tokens.forEach((token) => {
      const tokenSize = map.gridSize * token.width - 4 // 2px padding on each side
      const visible = isTokenVisible(token)

      // Phase 3c: Track visibility state changes
      const wasVisibleLastFrame = visibilityStateRef.current.get(token.id) ?? false

      // Detect visibility transitions
      if (visible && !wasVisibleLastFrame) {
        // Phase 3c: Clear any stale animations when token comes back into vision
        // This prevents showing old movement that happened while out of vision
        animationsRef.current.delete(token.id)
      } else if (!visible && wasVisibleLastFrame) {
        // Phase 3c: Clear animations when token goes out of vision
        animationsRef.current.delete(token.id)
      }

      // Update visibility state for next frame
      visibilityStateRef.current.set(token.id, visible)

      // Only update last-seen when token IS visible (not when hidden)
      // This preserves the position where it was last seen when it goes out of vision
      if (visible) {
        // Token is visible - update last-seen to current position
        lastSeenTokensRef.current.set(token.id, {
          x: token.x,
          y: token.y,
          color: token.color,
          name: token.name,
          width: token.width,
          height: token.height,
        })
      }

      // Phase 3c: Decide what to render
      let tokenToRender: Token | null = null
      let isStale = false

      if (visible) {
        // Token is currently visible - render normally at CURRENT position
        tokenToRender = token
        isStale = false
      } else if (lastSeenTokensRef.current.has(token.id)) {
        // Token not visible but we have last-seen data - render as stale at LAST-SEEN position
        const lastSeen = lastSeenTokensRef.current.get(token.id)!
        tokenToRender = {
          ...token,
          x: lastSeen.x,
          y: lastSeen.y,
          color: lastSeen.color,
          name: lastSeen.name,
          width: lastSeen.width,
          height: lastSeen.height,
        }
        isStale = true
      } else {
        // Token not visible and never seen - don't render
        return
      }

      renderedTokenIds.add(token.id)

      // Common geometry used by name pill, HP pill, and status chips.
      // tokenSize already computed at top of forEach.
      const cx = (map.gridSize * tokenToRender.width) / 2
      const cy = (map.gridSize * tokenToRender.height) / 2
      const portraitRadius = tokenSize / 2

      // Create container for token (circle + label)
      const tokenContainer = new PIXI.Container()

      // Check if there's an active animation for this token (only for visible tokens)
      const animation = !isStale ? animationsRef.current.get(token.id) : null
      let pixelPos: { x: number; y: number }

      if (animation) {
        // Use current animated position
        const now = performance.now()
        const elapsed = now - animation.startTime
        const progress = Math.min(elapsed / animation.duration, 1)
        const easedProgress = 1 - Math.pow(1 - progress, 3) // ease-out cubic
        const x = animation.fromX + (animation.toX - animation.fromX) * easedProgress
        const y = animation.fromY + (animation.toY - animation.fromY) * easedProgress
        pixelPos = gridToPixel(x, y, map.gridSize)
      } else {
        // Use logical position from store (or last-seen position for stale tokens)
        pixelPos = gridToPixel(tokenToRender.x, tokenToRender.y, map.gridSize)
      }

      tokenContainer.x = pixelPos.x
      tokenContainer.y = pixelPos.y

      // Store container in ref for animation ticker (only for visible tokens)
      if (!isStale) {
        tokenContainersRef.current.set(token.id, tokenContainer)
      }

      // GM-only tokens (ones players never see) render faded with a soft
      // black drop shadow in the GM's own view, so the GM can tell at a
      // glance which tokens are hidden from the table. Players never receive
      // these tokens at all, so this only ever applies for the GM — we gate
      // on role and reuse the canonical disclosure resolver (a player viewer
      // resolving to 'hidden' is exactly "hidden from the table").
      const hiddenFromPlayers =
        userRole === 'gm' &&
        resolveTokenDisclosure(token, { role: 'player', userId: null }) ===
          'hidden'
      if (hiddenFromPlayers) {
        const cx = (map.gridSize * tokenToRender.width) / 2
        const cy = (map.gridSize * tokenToRender.height) / 2
        const shadow = new PIXI.Graphics()
        shadow.beginFill(0x000000, 0.6)
        shadow.drawCircle(cx + 3, cy + 3, tokenSize / 2)
        shadow.endFill()
        shadow.filters = [new PIXI.BlurFilter(4)]
        // Added first so it sits behind the circle/sprite/label.
        tokenContainer.addChild(shadow)
        tokenContainer.alpha = 0.5
      }

      // Draw token circle
      const circle = new PIXI.Graphics()
      const tokenColor = hexToPixiColor(tokenToRender.color)

      // Phase 3c: Stale tokens are rendered grayed and semi-transparent
      if (isStale) {
        // Desaturated/grayed color (convert to grayscale-ish)
        circle.beginFill(0x808080, 0.5) // Gray with 50% opacity
      } else {
        circle.beginFill(tokenColor)
      }

      circle.drawCircle(
        (map.gridSize * tokenToRender.width) / 2,
        (map.gridSize * tokenToRender.height) / 2,
        tokenSize / 2
      )
      circle.endFill()

      // Add border - dotted for stale, solid for visible
      if (isStale) {
        circle.lineStyle({ width: 2, color: 0x808080, alpha: 0.6 })
      } else {
        circle.lineStyle({ width: 2, color: 0x000000, alpha: 0.3 })
      }
      circle.drawCircle(
        (map.gridSize * tokenToRender.width) / 2,
        (map.gridSize * tokenToRender.height) / 2,
        tokenSize / 2
      )

      tokenContainer.addChild(circle)

      // Always-on enemy outline (VTT #163). Tokens whose linked character is
      // classified `enemy` get a Grimoire status-red (--ac-danger → ENEMY_RED)
      // ring drawn at the token edge — unconditionally, not gated on hover or
      // selection (those rings are drawn separately on the selection layer).
      // Drawn after the base circle so it sits on the rim; the async portrait
      // inserts at index 1 and pushes this above it, so it stays visible over
      // the image. Stale (out-of-vision) tokens keep the grayed treatment.
      const tokenClassification = tokenToRender.characterId
        ? getCharacterData?.(tokenToRender.characterId)?.tokenClassification
        : undefined
      if (!isStale && tokenClassification === 'enemy') {
        const enemyRing = new PIXI.Graphics()
        enemyRing.lineStyle({ width: 3, color: ENEMY_RED, alpha: 1 })
        enemyRing.drawCircle(cx, cy, portraitRadius)
        tokenContainer.addChild(enemyRing)
      }

      // Token portrait. The colored circle above acts as the placeholder/
      // backdrop; when the token carries an imageUrl we load it and lay a
      // circle-masked sprite over the fill. The load is async, so capture the
      // render generation and bail on resolve if a newer pass has superseded
      // this one (or the container was torn down). PIXI.Assets caches by URL,
      // so repeated passes reuse the decoded texture rather than re-fetching.
      if (tokenToRender.imageUrl) {
        const imageUrl = tokenToRender.imageUrl
        const cx = (map.gridSize * tokenToRender.width) / 2
        const cy = (map.gridSize * tokenToRender.height) / 2
        const radius = tokenSize / 2
        const staleNow = isStale
        const rotationDeg = tokenToRender.rotation ?? 0
        const targetContainer = tokenContainer

        PIXI.Assets.load(imageUrl)
          .then((texture) => {
            if (
              renderGeneration !== tokenRenderGenRef.current ||
              targetContainer.destroyed
            ) {
              return
            }

            const portraitGroup = new PIXI.Container()

            const sprite = new PIXI.Sprite(texture)
            sprite.width = radius * 2
            sprite.height = radius * 2
            // Anchor at center so a rotation spins the image around the token's
            // middle. The circular mask is symmetric, so only the portrait turns;
            // the name label and HP bar are separate container children and stay
            // upright.
            sprite.anchor.set(0.5)
            sprite.x = cx
            sprite.y = cy
            if (rotationDeg) {
              sprite.rotation = (rotationDeg * Math.PI) / 180
            }
            if (staleNow) {
              // Match the grayed/translucent treatment of the stale circle.
              sprite.tint = 0x808080
              sprite.alpha = 0.5
            }

            const mask = new PIXI.Graphics()
            mask.beginFill(0xffffff)
            mask.drawCircle(cx, cy, radius)
            mask.endFill()
            sprite.mask = mask
            portraitGroup.addChild(sprite)
            portraitGroup.addChild(mask)

            // Clean edge ring on top of the portrait, mirroring the circle's border.
            const ring = new PIXI.Graphics()
            ring.lineStyle({
              width: 2,
              color: staleNow ? 0x808080 : 0x000000,
              alpha: staleNow ? 0.6 : 0.3,
            })
            ring.drawCircle(cx, cy, radius)
            portraitGroup.addChild(ring)

            // Insert just above the base circle (index 0) so the name label and
            // HP bar added synchronously below stay on top of the portrait.
            targetContainer.addChildAt(portraitGroup, 1)
          })
          .catch((error) => {
            console.error('[VTTCanvas] Failed to load token image:', error)
          })
      }

      // -----------------------------------------------------------------------
      // Name pill — dark rounded pill overlapping the portrait's bottom edge.
      // Design: rgba(0,0,0,0.75), h=16, corner-r=4, centered horizontally.
      // Pill top sits 6px above portrait bottom so it overlaps by that much.
      // -----------------------------------------------------------------------
      const namePillH = 16
      const namePillR = 4
      // Portrait bottom in token-local coords (cy and portraitRadius computed above)
      const pillTopY = cy + portraitRadius - 6

      // Measure text first so we can size the pill to fit.
      const nameText = new PIXI.Text(tokenToRender.name, {
        fontFamily: "'DM Sans', sans-serif",
        fontSize: 10,
        fill: isStale ? 0xaaaaaa : 0xffffff,
        align: 'center',
      })
      nameText.anchor.set(0.5)
      if (isStale) nameText.alpha = 0.6

      const namePadX = 8 // 4 px each side
      const namePillW = Math.max(50, nameText.width + namePadX * 2)

      const nameBackdrop = new PIXI.Graphics()
      nameBackdrop.beginFill(0x000000, isStale ? 0.45 : 0.75)
      nameBackdrop.drawRoundedRect(
        cx - namePillW / 2,
        pillTopY,
        namePillW,
        namePillH,
        namePillR
      )
      nameBackdrop.endFill()
      if (isStale) nameBackdrop.alpha = 0.6

      nameText.x = cx
      nameText.y = pillTopY + namePillH / 2

      tokenContainer.addChild(nameBackdrop)
      tokenContainer.addChild(nameText)

      // -----------------------------------------------------------------------
      // Status chips — 14 px dark circles clustered against the LEFT edge of
      // the portrait, each holding an 8 px mint line-icon (Figma 3242-425).
      // Chips pack into alternating tall/short columns nested against the curve.
      // -----------------------------------------------------------------------
      const statusKeys = normalizeStatuses(tokenToRender.statuses)
      drawStatusChips(tokenContainer, {
        statusKeys,
        resolveIconName: (key) => statusIconName(key),
        resolveIconUrl: (key) => resolveStatus(key).iconUrl,
        portraitLeftX: cx - portraitRadius,
        portraitTopY: cy - portraitRadius,
        isStale,
      })

      // -----------------------------------------------------------------------
      // HP pill — green rounded pill ABOVE the portrait.
      // shouldShowHPBar decides whether the pill is created for this viewer
      // (GM always; owner always; other players per the per-token toggle).
      // Once created, a hover/select gate decides whether it's shown — EXCEPT
      // the owner's own pill, which stays visible without hover (VTT #136).
      //
      // Stale tokens skip the pill — stale HP data is unreliable.
      // -----------------------------------------------------------------------
      let hpPillContainer: PIXI.Container | null = null

      if (!isStale && tokenToRender.characterId && getCharacterData) {
        const character = getCharacterData(tokenToRender.characterId)
        const hp = character?.stats?.hp
        const hpMax = hp?.max
        const hpCurrent = hp?.current

        const hpUsable =
          !!hp &&
          typeof hpMax === 'number' &&
          Number.isFinite(hpMax) &&
          hpMax > 0 &&
          typeof hpCurrent === 'number'

        // Bug #161/2: HP isn't available for this linked character yet. This is
        // the "hidden until the sheet is opened" case — the session roster
        // fetch hadn't merged this character when the token first drew, and
        // nothing re-requested it. Ask App to hydrate it (once); on resolve the
        // characters state updates, this effect re-runs, and the pill renders
        // without the player ever opening the sheet. Mirrors the on-spawn
        // refresh path. We only request when there IS a linked character to
        // fetch but its HP is missing — a real not-yet-loaded miss, not an
        // NPC/tokenless case.
        if (!hpUsable && onCharacterHydrate) {
          const charId = tokenToRender.characterId
          if (!hydrateRequestedRef.current.has(charId)) {
            hydrateRequestedRef.current.add(charId)
            onCharacterHydrate(charId)
          }
        } else if (hpUsable) {
          // Data arrived — clear the guard so a future eviction can re-request.
          hydrateRequestedRef.current.delete(tokenToRender.characterId)
        }

        if (hpUsable && hp) {
          // Re-bind to bare numbers — `hpUsable` is a boolean flag, so TS won't
          // narrow hpMax/hpCurrent through it. The hpUsable guard already
          // proved both are finite numbers (hpMax > 0).
          const hpCur = hp.current as number
          const hpMx = hp.max as number
          const isGM = userRole === 'gm'
          // The viewer owns this token when its ownerUserId matches them —
          // same ownership test as canMoveToken. Owners always see their own
          // HP bar (VTT #136), independent of the GM's player-visibility toggle.
          const isOwner =
            !!currentUserId && tokenToRender.ownerUserId === currentUserId
          // #526: owner's private opt-out of seeing their OWN token's HP bar.
          const ownerHidSelf = isOwner && ownHpBarHidden.has(tokenToRender.id)
          // GM-vs-owner-vs-player visibility rule (+ owner self opt-out).
          const hpBarShown = shouldShowHPBar(
            isGM,
            isOwner,
            tokenToRender.hpBarVisibleToPlayers,
            ownerHidSelf
          )

          if (hpBarShown) {
            // Pill sits above the portrait: bottom of pill is 5 px above
            // portrait top; pill is HP_PILL_HEIGHT (16 px) tall.
            const pillX = cx - HP_PILL_WIDTH / 2
            const pillY = cy - portraitRadius - HP_PILL_HEIGHT - 5

            const hpPill = new PIXI.Container()
            hpPill.x = pillX
            hpPill.y = pillY

            const hpPillGraphics = new PIXI.Graphics()
            const hpPillText = createHPPillText()
            drawHPPill(hpPillGraphics, hpPillText, {
              current: hpCur,
              max: hpMx,
            })

            hpPill.addChild(hpPillGraphics)
            hpPill.addChild(hpPillText)

            // Persistent (always-visible, hover/select gate bypassed) when:
            //  - it's the viewer's OWN token (owner always sees own HP, #136), OR
            //  - the GM has turned "Show HP" ON for this token and the viewer is
            //    a player (#161). The toggle is meant to surface party HP to
            //    players persistently — not just on hover — so without this a
            //    GM enabling "Show HP" produced no visible change for players
            //    (the pill existed but stayed hover-gated). The GM's own view
            //    keeps the hover/select gate so the board isn't cluttered.
            const showHpToPlayers = tokenToRender.hpBarVisibleToPlayers !== false
            const persistentForPlayer = !isGM && showHpToPlayers
            if (isOwner || persistentForPlayer) {
              persistentHpPillIdsRef.current.add(token.id)
            }
            const isSelectedNow = useVTTStore
              .getState()
              .selectedTokenIds.includes(token.id)
            hpPill.visible = isOwner || persistentForPlayer || isSelectedNow

            // Click-to-edit HP (issue #135): for a viewer who may edit this
            // token (GM or owner — same gate as canMoveToken / the HUD editor),
            // the pill becomes a button that opens the on-canvas HP popover.
            // pointerdown is swallowed so clicking the bar doesn't start a token
            // drag; pointertap opens the editor at the pill's screen position.
            if (onHpEdit && tokenToRender.characterId && canMoveToken(token)) {
              const charId = tokenToRender.characterId
              const curHp = hpCur
              const maxHp = hpMx
              hpPill.eventMode = 'static'
              hpPill.cursor = 'pointer'
              hpPill.hitArea = new PIXI.Rectangle(
                0,
                0,
                HP_PILL_WIDTH,
                HP_PILL_HEIGHT,
              )
              hpPill.on('pointerdown', (e) => e.stopPropagation())
              hpPill.on('pointertap', (e) => {
                e.stopPropagation()
                setHpEditState({
                  characterId: charId,
                  tokenId: token.id,
                  current: curHp,
                  max: maxHp,
                  position: { x: e.global.x, y: e.global.y },
                })
              })
            }

            tokenContainer.addChild(hpPill)
            hpPillContainer = hpPill
          }
        }
      }

      // Register pill (or null when not applicable) for hover/selection effects.
      tokenHpPillsRef.current.set(token.id, hpPillContainer)

      // Phase 3c: Stale tokens are not interactive
      // Make interactive only in select mode (not in barrier or fog modes) and only if not stale
      if (toolMode === 'select' && !isStale) {
        tokenContainer.eventMode = 'static'
        // Set cursor based on whether user can move this token
        tokenContainer.cursor = canMoveToken(token) ? 'grab' : 'pointer'

        // Add hover effect
        const canMove = canMoveToken(token)
        const hoverTokenId = token.id
        tokenContainer.on('pointerover', () => {
          if (canMove) {
            tokenContainer.cursor = 'grab'
          }
          circle.alpha = 0.9
          // Show HP pill on hover
          hoveredTokenIdsRef.current.add(hoverTokenId)
          const pill = tokenHpPillsRef.current.get(hoverTokenId)
          if (pill) pill.visible = true
        })
        tokenContainer.on('pointerout', () => {
          circle.alpha = 1.0
          // Hide HP pill on pointer-out unless the token is currently selected
          // or it's the viewer's own token (owner bars stay visible).
          hoveredTokenIdsRef.current.delete(hoverTokenId)
          const isSelected = useVTTStore
            .getState()
            .selectedTokenIds.includes(hoverTokenId)
          const isPersistent =
            persistentHpPillIdsRef.current.has(hoverTokenId)
          const pill = tokenHpPillsRef.current.get(hoverTokenId)
          if (pill && !isSelected && !isPersistent) pill.visible = false
        })

        // Drag handler
        tokenContainer.on('pointerdown', (event) => {
          if (canMove) {
            tokenContainer.cursor = 'grabbing'
          }
          handlePointerDown(token, event)
        })

        // Right-click handler for tooltip
        tokenContainer.on('rightclick', (event) => {
          console.log('[VTTCanvas] Token right-clicked:', {
            tokenId: token.id,
            tokenName: token.name,
            target: event.target,
            button: event.button,
            nativeEvent: event.nativeEvent,
            eventType: event.type
          })

          // Prevent context menu on both PixiJS and native levels
          event.preventDefault()
          event.stopPropagation()

          // Also prevent on native event if available
          if (event.nativeEvent && 'preventDefault' in event.nativeEvent) {
            event.nativeEvent.preventDefault()
            event.nativeEvent.stopPropagation()
          }

          const globalPos = event.global
          // Close any existing tooltip and open new one for this token
          // This allows switching between tokens by right-clicking
          setTooltipState({
            token,
            position: { x: globalPos.x - 220, y: globalPos.y + 10 }
          })
        })

        // Touch double-tap → open the context menu (tooltip). There's no
        // right-click on touch, so two quick taps on the same token surface the
        // same menu. Mouse is ignored here (it has the right-click path above).
        tokenContainer.on('pointertap', (event) => {
          const pe = event.nativeEvent as PointerEvent | undefined
          if (pe && pe.pointerType === 'mouse') return
          const now = performance.now()
          const g = event.global
          const last = lastTokenTapRef.current
          const DBL_MS = 320
          const DBL_DIST = 28
          if (
            last &&
            last.id === token.id &&
            now - last.t < DBL_MS &&
            Math.hypot(g.x - last.x, g.y - last.y) < DBL_DIST
          ) {
            lastTokenTapRef.current = null
            // Clamp so the menu stays on-screen on narrow mobile viewports.
            const TT_W = 230
            const x = Math.max(8, Math.min(window.innerWidth - TT_W, g.x - TT_W / 2))
            const y = Math.max(8, Math.min(window.innerHeight - 40, g.y + 12))
            setTooltipState({ token, position: { x, y } })
          } else {
            lastTokenTapRef.current = { id: token.id, t: now, x: g.x, y: g.y }
          }
        })
      } else {
        // In barrier/fog modes or stale tokens, make non-interactive so clicks pass through
        tokenContainer.eventMode = 'none'
      }

      container.addChild(tokenContainer)
    })
  }, [tokens, map?.id, map?.gridSize, handlePointerDown, canvasReady, canMoveToken, toolMode, isTokenVisible, getCharacterData, onCharacterHydrate, userRole, currentUserId, onHpEdit, ownHpBarHidden])

  /**
   * Render selection highlight when selected token changes.
   * Also syncs HP pill visibility: selected tokens show the pill; deselected
   * ones hide it (unless still hovered, tracked in hoveredTokenIdsRef).
   */
  useEffect(() => {
    const selectionLayer = selectionLayerRef.current
    if (!selectionLayer) return

    selectionLayer.clear()

    // Build a quick lookup for the active map's tokens, then draw a
    // halo per selected id. Halos render in the order the user added
    // tokens to the selection — z-order doesn't matter since the
    // selection layer sits above the token sprites already.
    const selectedSet = new Set(selectedTokenIds)
    tokens.forEach((token) => {
      if (selectedSet.has(token.id)) {
        renderSelection(selectionLayer, token)
      }
    })

    // Interactive grips. Rebuild every run; only shown for a SINGLE selected,
    // editable token (rotating/scaling a whole multi-selection is out of scope).
    const handles = selectionHandlesRef.current
    if (handles) {
      handles.removeChildren().forEach((c) => c.destroy({ children: true }))
      const soleId = selectedTokenIds.length === 1 ? selectedTokenIds[0] : null
      const soleToken = soleId ? tokens.find((t) => t.id === soleId) : null
      if (soleToken && canMoveToken(soleToken)) {
        const px = gridToPixel(soleToken.x, soleToken.y, map.gridSize)
        const cx = px.x + (map.gridSize * soleToken.width) / 2
        const cy = px.y + (map.gridSize * soleToken.height) / 2
        const r = (map.gridSize * soleToken.width - 4) / 2

        // --- Rotate grip (top-right): plain dark disc + white CW glyph, no
        // border, fades in on appear. Matches Figma 3242-425 (dark #19103D
        // chip, white rotate icon — no mint ring). Grab-and-drag rotates the
        // token CONTINUOUSLY to face the pointer (free rotation, no 45° steps):
        // pointermove sets rotation live; pointerup persists.
        const rot = new PIXI.Container()
        rot.x = cx + r + 1
        rot.y = cy - r - 1
        rot.eventMode = 'static'
        rot.cursor = 'pointer'
        rot.hitArea = new PIXI.Circle(0, 0, 11)
        const disc = new PIXI.Graphics()
        disc.beginFill(HANDLE_DARK, 0.95)
        disc.drawCircle(0, 0, 8)
        disc.endFill()
        const arrow = new PIXI.Text('↻', {
          fontSize: 12,
          fill: 0xffffff,
          fontWeight: '700',
        })
        arrow.anchor.set(0.5)
        rot.addChild(disc)
        rot.addChild(arrow)
        // Fade in over ~140ms rather than popping in (Figma reveals the
        // controls). rAF lerp; bails the moment this grip is torn down — the
        // next selection render destroys and rebuilds the handle children.
        rot.alpha = 0
        const fadeStart = performance.now()
        const fadeIn = (now: number) => {
          if (rot.destroyed) return
          rot.alpha = Math.min((now - fadeStart) / 140, 1)
          if (rot.alpha < 1) requestAnimationFrame(fadeIn)
        }
        requestAnimationFrame(fadeIn)
        rot.on('pointerover', () => { if (!rotatingRef.current) rot.scale.set(1.12) })
        rot.on('pointerout', () => { if (!rotatingRef.current) rot.scale.set(1) })

        // Grab-and-drag: rotation = clockwise angle from straight-up (north) to
        // the pointer, measured around the token CENTER, so the token's top
        // faces wherever you drag. Listen on window so the drag survives the
        // pointer leaving the small grip hitArea (mirrors the resize grip).
        rot.on('pointerdown', (e: PIXI.FederatedPointerEvent) => {
          e.stopPropagation()
          const handleLayer = selectionHandlesRef.current
          if (!handleLayer) return
          rotatingRef.current = true
          rot.scale.set(1)
          let latestRotation = soleToken.rotation ?? 0

          const onRotateMove = (ev: PointerEvent) => {
            // Map the screen pointer into the selectionHandles layer's local
            // space (same transform the grips live in) so the angle tracks the
            // cursor under any pan/zoom.
            const local = handleLayer.toLocal(new PIXI.Point(ev.clientX, ev.clientY))
            // atan2(dx, -dy): 0° when the pointer is straight up, increasing
            // clockwise (matches the token's CW rotation convention).
            const deg = ((Math.round(Math.atan2(local.x - cx, -(local.y - cy)) * (180 / Math.PI)) % 360) + 360) % 360
            latestRotation = deg
            const { tokens: lt2, setTokens: setLt2 } = useVTTStore.getState()
            setLt2(lt2.map((t) => (t.id === soleToken.id ? { ...t, rotation: deg } : t)))
          }
          const onRotateUp = () => {
            window.removeEventListener('pointermove', onRotateMove)
            window.removeEventListener('pointerup', onRotateUp)
            rotatingRef.current = false
            // Persist the final rotation; the server validates + broadcasts
            // token_updated, replicating it to every other client.
            socketService.updateToken(soleToken.id, { rotation: latestRotation })
          }
          window.addEventListener('pointermove', onRotateMove)
          window.addEventListener('pointerup', onRotateUp)
        })
        handles.addChild(rot)

        // --- Resize grip (bottom-right): same dark disc + white diagonal-resize
        // glyph (Figma 3218:1345 "Arrow External Right" — a down-right corner
        // arrow). Grab-and-drag scales the token GRADUALLY (continuously) rather
        // than in integer steps: pointermove rescales live; pointerup persists.
        // Tokens are square, so width === height (scaled equally).
        const res = new PIXI.Container()
        res.x = cx + r + 1
        res.y = cy + r + 1
        res.eventMode = 'static'
        res.cursor = 'nwse-resize'
        res.hitArea = new PIXI.Circle(0, 0, 11)
        const resDisc = new PIXI.Graphics()
        resDisc.beginFill(HANDLE_DARK, 0.95)
        resDisc.drawCircle(0, 0, 8)
        resDisc.endFill()
        // Diagonal-resize glyph: a down-right corner arrow (L-bracket + barb),
        // white, matching the Figma "Arrow External Right" vector (a diagonal
        // line from top-left to bottom-right with an elbow at bottom-left).
        const resIcon = new PIXI.Graphics()
        resIcon.lineStyle({ width: 1.4, color: 0xffffff, cap: PIXI.LINE_CAP.ROUND, join: PIXI.LINE_JOIN.ROUND })
        // diagonal stroke (top-left -> bottom-right)
        resIcon.moveTo(-2.7, -2.7)
        resIcon.lineTo(2.7, 2.7)
        // elbow bracket at the bottom-right corner forming the arrowhead
        resIcon.moveTo(2.7, -0.3)
        resIcon.lineTo(2.7, 2.7)
        resIcon.lineTo(-0.3, 2.7)
        res.addChild(resDisc)
        res.addChild(resIcon)
        // Fade in to match the rotate grip's reveal.
        res.alpha = 0
        const resFadeStart = performance.now()
        const resFadeIn = (now: number) => {
          if (res.destroyed) return
          res.alpha = Math.min((now - resFadeStart) / 140, 1)
          if (res.alpha < 1) requestAnimationFrame(resFadeIn)
        }
        requestAnimationFrame(resFadeIn)
        res.on('pointerover', () => { if (!resizingRef.current) res.scale.set(1.12) })
        res.on('pointerout', () => { if (!resizingRef.current) res.scale.set(1) })

        // Grab-and-drag: compute new fractional size from the pointer's distance
        // to the token CENTER (the grip sits at the bottom-right corner, so the
        // half-diagonal of a square token of size N grid-squares is
        // N * gridSize * sqrt(2) / 2 from center). Listen on window so the drag
        // survives the pointer leaving the small grip hitArea.
        res.on('pointerdown', (e: PIXI.FederatedPointerEvent) => {
          e.stopPropagation()
          const handleLayer = selectionHandlesRef.current
          if (!handleLayer) return
          resizingRef.current = true
          res.scale.set(1)
          const HALF_DIAG = Math.SQRT2 / 2 // (sqrt(2)/2) — corner distance per unit size
          let latestSize = soleToken.width

          const onResizeMove = (ev: PointerEvent) => {
            // Map the screen pointer into the selectionHandles layer's local
            // space (same transform the grips live in) so size tracks the cursor
            // under any pan/zoom.
            const local = handleLayer.toLocal(new PIXI.Point(ev.clientX, ev.clientY))
            const dist = Math.hypot(local.x - cx, local.y - cy)
            // dist = size * gridSize * HALF_DIAG  ->  size = dist / (gridSize * HALF_DIAG)
            const raw = dist / (map.gridSize * HALF_DIAG)
            const size = Math.min(20, Math.max(0.25, raw))
            latestSize = size
            const { tokens: lt2, setTokens: setLt2 } = useVTTStore.getState()
            setLt2(lt2.map((t) => (t.id === soleToken.id ? { ...t, width: size, height: size } : t)))
          }
          const onResizeUp = () => {
            window.removeEventListener('pointermove', onResizeMove)
            window.removeEventListener('pointerup', onResizeUp)
            resizingRef.current = false
            // Persist the final size; the server validates + broadcasts
            // token_updated, replicating the rescale to every other client.
            socketService.updateToken(soleToken.id, { width: latestSize, height: latestSize })
          }
          window.addEventListener('pointermove', onResizeMove)
          window.addEventListener('pointerup', onResizeUp)
        })
        handles.addChild(res)

        // --- Lightning / status grip (right edge, below rotate): same dark
        // #19103D disc + white glyph, no border, same fade-in. Matches Figma
        // 3242-425's control cluster — rotate/lightning/arrow stacked on the
        // token's right edge, one ~28px chip-step apart. Tap opens the
        // TokenStatusPopover anchored at the grip's screen position.
        const GRIP_STEP = 28 // Figma cluster vertical spacing between grips
        const bolt = new PIXI.Container()
        bolt.x = cx + r + 1
        bolt.y = cy - r - 1 + GRIP_STEP
        bolt.eventMode = 'static'
        bolt.cursor = 'pointer'
        bolt.hitArea = new PIXI.Circle(0, 0, 11)
        const boltDisc = new PIXI.Graphics()
        boltDisc.beginFill(HANDLE_DARK, 0.95)
        boltDisc.drawCircle(0, 0, 8)
        boltDisc.endFill()
        // Real Figma lightning vector (node 3218:1343) as a white texture; the
        // 8-unit viewBox is rasterised at 32px then drawn at 11px so the stroke
        // stays crisp on the ~16px disc.
        const boltIcon = new PIXI.Sprite(lightningGripTexture())
        boltIcon.anchor.set(0.5)
        boltIcon.width = 11
        boltIcon.height = 11
        bolt.addChild(boltDisc)
        bolt.addChild(boltIcon)
        bolt.alpha = 0
        const boltFadeStart = performance.now()
        const boltFadeIn = (now: number) => {
          if (bolt.destroyed) return
          bolt.alpha = Math.min((now - boltFadeStart) / 140, 1)
          if (bolt.alpha < 1) requestAnimationFrame(boltFadeIn)
        }
        requestAnimationFrame(boltFadeIn)
        bolt.on('pointerover', () => bolt.scale.set(1.12))
        bolt.on('pointerout', () => bolt.scale.set(1))
        bolt.on('pointertap', (e) => {
          e.stopPropagation()
          // Anchor the DOM popover at the tap's screen (client) position so it
          // lines up with the on-canvas grip. PIXI federated events expose the
          // native pointer event via nativeEvent; fall back to the global coord
          // mapped through the canvas rect if it's unavailable.
          const native = e.nativeEvent as PointerEvent | MouseEvent | undefined
          let sx: number
          let sy: number
          if (native && 'clientX' in native) {
            sx = native.clientX
            sy = native.clientY
          } else {
            const rect = canvasRef.current?.getBoundingClientRect()
            sx = (rect?.left ?? 0) + e.global.x
            sy = (rect?.top ?? 0) + e.global.y
          }
          // Keep the popover on-screen: it's ~160px wide / ~120px tall. If the
          // grip sits near the right/bottom edge, flip the anchor inward.
          const POPOVER_W = 160
          const POPOVER_H = 120
          const margin = 8
          let px = sx + 12
          let py = sy + 12
          if (px + POPOVER_W + margin > window.innerWidth) px = sx - POPOVER_W - 12
          if (py + POPOVER_H + margin > window.innerHeight) py = sy - POPOVER_H - 12
          px = Math.max(margin, px)
          py = Math.max(margin, py)
          setStatusPopover((prev) =>
            prev && prev.tokenId === soleToken.id
              ? null // tapping the grip again closes it
              : { tokenId: soleToken.id, position: { x: px, y: py } }
          )
        })
        handles.addChild(bolt)
      }
    }

    // Sync HP pill visibility across all tokens to match selection + hover,
    // keeping the viewer's own (persistent) pills visible regardless.
    tokenHpPillsRef.current.forEach((pill, tokenId) => {
      if (!pill) return
      const isSelected = selectedSet.has(tokenId)
      const isHovered = hoveredTokenIdsRef.current.has(tokenId)
      const isPersistent = persistentHpPillIdsRef.current.has(tokenId)
      pill.visible = isSelected || isHovered || isPersistent
    })
  }, [selectedTokenIds, tokens, renderSelection, canMoveToken, map.gridSize])

  /**
   * Render saved barriers (Phase 3)
   */
  useEffect(() => {
    const barrierLayer = barrierLayerRef.current
    if (!barrierLayer) return

    // Barriers are always available, independent of dynamic LoS. Render
    // whenever the GM hasn't hidden them via the showBarriers toggle.
    const showBarriers = map.showBarriers !== false // default true

    if (!showBarriers) {
      barrierLayer.removeChildren()
      barrierGraphicsRef.current.clear()
      return
    }

    barrierLayer.removeChildren()
    barrierGraphicsRef.current.clear()

    // Draw each saved barrier
    barriers.forEach((barrier) => {
      if (barrier.points.length < 2) return

      const barrierGraphic = new PIXI.Graphics()
      const isSelected = selectedBarrierId === barrier.id

      // Determine barrier color based on what it blocks
      // Vision only: red, Movement only: blue, Both: purple
      const blocksVision = barrier.blocksVision !== false // default true
      const blocksMovement = barrier.blocksMovement === true // default false
      let barrierColor = 0xff4444 // default red (vision only)
      if (blocksVision && blocksMovement) {
        barrierColor = 0x9b59b6 // purple (blocks both)
      } else if (blocksMovement && !blocksVision) {
        barrierColor = 0x3498db // blue (movement only)
      }

      // Open polylines (closed === false) are walls — a stroked line with no
      // closing edge and no fill. Legacy closed polygons keep the filled-area look.
      const isOpen = barrier.closed === false
      const off = isOpen ? 0 : map.gridSize / 2 // open walls snap to corners; closed are cell-centered
      const firstPixel = gridToPixel(barrier.points[0].x, barrier.points[0].y, map.gridSize)
      const cx0 = firstPixel.x + off
      const cy0 = firstPixel.y + off

      // Fill (closed polygons only).
      if (!isOpen) {
        barrierGraphic.beginFill(barrierColor, isSelected ? 0.3 : 0.15) // Brighter if selected
        barrierGraphic.moveTo(cx0, cy0)
        for (let i = 1; i < barrier.points.length; i++) {
          const pixel = gridToPixel(barrier.points[i].x, barrier.points[i].y, map.gridSize)
          barrierGraphic.lineTo(pixel.x + off, pixel.y + off)
        }
        barrierGraphic.lineTo(cx0, cy0) // close the polygon
        barrierGraphic.endFill()
      }

      // Border stroke (always).
      barrierGraphic.lineStyle({
        width: isSelected ? 4 : 3,
        color: isSelected ? 0xffff00 : barrierColor, // Yellow if selected, otherwise match barrier color
        alpha: isSelected ? 1.0 : 0.8
      })
      barrierGraphic.moveTo(cx0, cy0)
      for (let i = 1; i < barrier.points.length; i++) {
        const pixel = gridToPixel(barrier.points[i].x, barrier.points[i].y, map.gridSize)
        barrierGraphic.lineTo(pixel.x + off, pixel.y + off)
      }
      if (!isOpen) {
        barrierGraphic.lineTo(cx0, cy0) // close the border
      }

      // Make barrier interactive in select mode
      if (toolMode === 'select') {
        barrierGraphic.eventMode = 'static'
        barrierGraphic.cursor = 'pointer'
        barrierGraphic.hitArea = new PIXI.Polygon(
          barrier.points.flatMap(point => {
            const pixel = gridToPixel(point.x, point.y, map.gridSize)
            return [pixel.x + off, pixel.y + off]
          })
        )

        barrierGraphic.on('click', (event) => {
          event.stopPropagation()
          selectBarrier(barrier.id)
          console.log(`[VTTCanvas] Barrier ${barrier.id} selected`)
        })

        barrierGraphic.on('pointerover', () => {
          barrierGraphic.alpha = 0.8
        })

        barrierGraphic.on('pointerout', () => {
          barrierGraphic.alpha = 1.0
        })
      }

      barrierLayer.addChild(barrierGraphic)
      barrierGraphicsRef.current.set(barrier.id, barrierGraphic)
    })

    console.log(`[VTTCanvas] Rendered ${barriers.length} barriers`)
  }, [barriers, map.gridSize, map.showBarriers, selectedBarrierId, toolMode, selectBarrier])

  /**
   * Auto-complete barrier when switching out of barrier mode (Phase 3)
   */
  useEffect(() => {
    // When switching OUT of barrier mode, complete any in-progress barrier
    if (toolMode !== 'barrier' && barrierVerticesRef.current.length >= 2) {
      console.log('[VTTCanvas] Switching out of barrier mode, auto-completing wall')

      const barrier = {
        id: `barrier_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        mapId: map.id,
        points: [...barrierVerticesRef.current],
        closed: false, // new walls are open polylines (no closing edge)
      }

      addBarrier(barrier)
      console.log('[VTTCanvas] Barrier auto-completed:', barrier)

      // Send to server via WebSocket
      if (onBarrierCreate) {
        onBarrierCreate(barrier.mapId, barrier.points, barrier.closed)
      }

      // Clear preview
      barrierVerticesRef.current = []
      const barrierLayer = barrierLayerRef.current
      if (barrierLayer) {
        barrierLayer.clear()
      }
    }

    // Always clear barrier vertices when leaving barrier mode (even if < 3)
    if (toolMode !== 'barrier' && barrierVerticesRef.current.length > 0) {
      console.log('[VTTCanvas] Clearing incomplete barrier')
      barrierVerticesRef.current = []
      const barrierLayer = barrierLayerRef.current
      if (barrierLayer) {
        barrierLayer.clear()
      }
    }
  }, [toolMode, map.id, addBarrier, onBarrierCreate])

  /**
   * Reset fog brush state when switching out of fog mode (Phase 4)
   */
  useEffect(() => {
    // When switching OUT of fog mode, reset brush state
    if (toolMode !== 'fog') {
      const fogState = fogBrushStateRef.current
      if (fogState.isDrawing) {
        console.log('[VTTCanvas] Switching out of fog mode, resetting brush state')
        fogState.isDrawing = false
        fogState.accumulatedCells.clear()
        fogState.lastGridX = null
        fogState.lastGridY = null
      }

      // Clear brush preview
      const brushPreviewLayer = brushPreviewLayerRef.current
      if (brushPreviewLayer) {
        brushPreviewLayer.clear()
      }
    }
  }, [toolMode])

  /**
   * Exit "see as token" when the viewed token is no longer the single
   * selected token. The toggle lives in the bottom-left selection HUD, so
   * deselecting (or multi-selecting) drops the GM back to their own view.
   * Guarded so an active view for the currently selected token is left
   * untouched.
   */
  useEffect(() => {
    if (!previewingVisionFor) return
    if (
      selectedTokenIds.length === 1 &&
      selectedTokenIds[0] === previewingVisionFor
    )
      return
    setPreviewingVisionFor(null)
  }, [selectedTokenIds, previewingVisionFor, setPreviewingVisionFor])

  /**
   * Render darkness layer with vision cutout (Phase 3b)
   */
  const renderDarkness = useCallback((
    darknessLayer: PIXI.Graphics,
    visibleRegions: Array<Array<{ x: number; y: number }>>,
    mapWidth: number,
    mapHeight: number,
    darkAlpha: number,
  ) => {
    darknessLayer.clear()

    // Draw darkness covering the map
    darknessLayer.beginFill(0x000000, darkAlpha)
    darknessLayer.drawRect(0, 0, mapWidth, mapHeight)
    darknessLayer.endFill()

    // Cut out each visible region as its own hole. Regions are disjoint (the
    // caller unions overlapping lights first), so multiple holes don't XOR.
    const regions = visibleRegions.filter((r) => r.length >= 3)
    if (regions.length === 0) return

    darknessLayer.beginHole()
    for (const region of regions) {
      // Clamp points to map bounds to prevent rendering issues
      const clamped = region.map(point => ({
        x: Math.max(0, Math.min(mapWidth, point.x)),
        y: Math.max(0, Math.min(mapHeight, point.y))
      }))
      darknessLayer.moveTo(clamped[0].x, clamped[0].y)
      for (let i = 1; i < clamped.length; i++) {
        darknessLayer.lineTo(clamped[i].x, clamped[i].y)
      }
      darknessLayer.closePath()
    }
    darknessLayer.endHole()
  }, [])

  /**
   * Calculate and render vision/darkness (Phase 3b + dynamic lighting v1)
   * Recalculates when tokens move, barriers change, vision mode changes, or
   * lighting state changes.
   *
   * Visibility model:
   *   - lightingEnabled OFF + losEnabled OFF → clear (full light, current behaviour)
   *   - lightingEnabled OFF + losEnabled ON  → LoS occlusion only (existing behaviour)
   *   - lightingEnabled ON  + losEnabled OFF → dark unless lit (light polygon = visible area)
   *   - lightingEnabled ON  + losEnabled ON  → intersection(vision, light polygons)
   *   GM always sees everything.
   */
  useEffect(() => {
    const darknessLayer = darknessLayerRef.current
    if (!darknessLayer) return
    // Clear the v2 band/tint layers upfront so no return path below leaves stale
    // shading; they're redrawn only when lighting is on.
    dimLayerRef.current?.clear()
    lightTintLayerRef.current?.clear()
    if (darkvisionGreyRef.current) darkvisionGreyRef.current.visible = false

    const losEnabled = map.losEnabled === true
    const lightingEnabled = map.lightingEnabled === true
    const mapWidthPx = map.width * map.gridSize
    const mapHeightPx = map.height * map.gridSize

    // Neither feature active → no darkness
    if (!losEnabled && !lightingEnabled) {
      darknessLayer.clear()
      setVisionPolygon(null)
      return
    }

    // "See as token" ("sample token vision"): a GM views from one token's
    // perspective. Treated exactly like a player below (full, light-respecting
    // view) with that token's vision standing in for party vision.
    const isPreview = !!previewingVisionFor

    // GM authoring view (NOT previewing) with lighting off → sees everything.
    // With lighting ON the GM gets a dimmed preview so light placement is WYSIWYG.
    if (userRole === 'gm' && !isPreview && !lightingEnabled) {
      darknessLayer.clear()
      return
    }

    // asGM = GM authoring (dimmed, LoS-agnostic). Players, observers, and a GM
    // previewing a token all render the full player-accurate view.
    const asGM = userRole === 'gm' && !isPreview

    // ── Async: compute everything we need, then render ───────────────────────
    import('@/utils/raycasting').then(async ({ calculateTokenVision }) => {
      const { calculatePartyVision, shouldCalculateVision, unionVisionPolygonsMulti, intersectPolygons } =
        await import('@/utils/visionCalculation')

      // 1. Per-light polygons, split into BRIGHT (inner) and DIM (outer) bands.
      //    Two raycasts per source so the darkness can render three tiers below:
      //    bright (clear), dim (half-lit), unlit (dark). Default lights migrate
      //    with dim == bright, so they read as a single lit disc (v1 behaviour).
      //    1×1 footprint centers the lit area on the cell, matching the icon.
      const brightPolys: Array<Array<{ x: number; y: number }>> = []
      const dimPolys: Array<Array<{ x: number; y: number }>> = []
      if (lightingEnabled) {
        for (const ls of lightSources) {
          const bright = ls.brightRadius ?? ls.radius
          const dim = Math.max(ls.dimRadius ?? ls.radius, bright)
          const bp = calculateTokenVision(ls.x, ls.y, 1, 1, bright, barriers, map.gridSize)
          if (bp.length >= 3) brightPolys.push(bp)
          const dp =
            dim > bright
              ? calculateTokenVision(ls.x, ls.y, 1, 1, dim, barriers, map.gridSize)
              : bp
          if (dp.length >= 3) dimPolys.push(dp)
        }

        // Token torch lights (torchActive && lightRadius > 0). Single band for now.
        for (const t of tokens) {
          if (t.torchActive && t.lightRadius && t.lightRadius > 0) {
            const poly = calculateTokenVision(t.x, t.y, t.width, t.height, t.lightRadius, barriers, map.gridSize)
            if (poly.length >= 3) {
              brightPolys.push(poly)
              dimPolys.push(poly)
            }
          }
        }
      }

      // 2. Compute vision polygon (LoS). Preview → the single viewed token's
      //    vision; otherwise the whole party's union.
      let visPolygon: Array<{ x: number; y: number }> | null = null
      if (isPreview) {
        const viewed = tokens.find((t) => t.id === previewingVisionFor)
        if (!viewed) {
          darknessLayer.clear()
          setViewAsVisionPolygon(null)
          return
        }
        visPolygon = losEnabled
          ? calculateTokenVision(viewed.x, viewed.y, viewed.width, viewed.height, viewed.visionRadius || 0, barriers, map.gridSize)
          : null
        setViewAsVisionPolygon(visPolygon)
      } else if (losEnabled) {
        if (!visionNeedsRecalculation && visionPolygon !== null) {
          visPolygon = visionPolygon
        } else if (shouldCalculateVision(map.losEnabled, tokens)) {
          visPolygon = calculatePartyVision(tokens, barriers, map.gridSize)
          setVisionPolygon(visPolygon)
        }
      }

      // 3. Resolve a polygon list into visible REGIONS (preserves disjoint
      //    lit areas), applying the same LoS/lighting gating for any band.
      const regionsFor = (
        polys: Array<Array<{ x: number; y: number }>>,
      ): Array<Array<{ x: number; y: number }>> => {
        if (polys.length === 0) return []
        if (asGM) return unionVisionPolygonsMulti(polys)
        if (losEnabled && lightingEnabled) {
          const out: Array<Array<{ x: number; y: number }>> = []
          if (visPolygon) {
            for (const region of unionVisionPolygonsMulti(polys)) {
              const clipped = intersectPolygons(visPolygon, region)
              if (clipped && clipped.length >= 3) out.push(clipped)
            }
          }
          return out
        }
        if (lightingEnabled) return unionVisionPolygonsMulti(polys)
        return []
      }

      // allVisible = outer (dim) extent → darkness holes. brightRegions → the
      // dim-layer holes (everything outside bright gets the mid shade).
      let allVisible: Array<Array<{ x: number; y: number }>> = []
      let brightRegions: Array<Array<{ x: number; y: number }>> = []
      if (lightingEnabled) {
        allVisible = regionsFor(dimPolys)
        brightRegions = regionsFor(brightPolys)
      } else if (losEnabled) {
        if (visPolygon && visPolygon.length >= 3) allVisible = [visPolygon]
      }

      // 3b. Vision types (lighting only). Low-light promotes the viewer's dim
      //     band to bright; darkvision lets a token see its full LoS polygon
      //     regardless of light — lit areas stay normal colour, the UNLIT part of
      //     the LoS is revealed as the desaturated map (see the dvGrey sprite).
      //     Party view aggregates player tokens; "see as" uses the viewed token.
      const litExtent = allVisible // the genuinely-lit regions, before darkvision
      const darkvisionPolys: Array<Array<{ x: number; y: number }>> = []
      if (lightingEnabled) {
        const visionTokens = isPreview
          ? tokens.filter((t) => t.id === previewingVisionFor)
          : asGM
            ? []
            : tokens.filter(
                (t) =>
                  t.ownerUserId &&
                  !t.ownerUserId.toLowerCase().includes('gm') &&
                  (t.visionRadius ?? 0) > 0,
              )
        let lowLightActive = false
        for (const t of visionTokens) {
          if (t.lowLightVision) lowLightActive = true
          if (t.darkvision) {
            const p = calculateTokenVision(t.x, t.y, t.width, t.height, t.visionRadius || 0, barriers, map.gridSize)
            if (p.length >= 3) darkvisionPolys.push(p)
          }
        }
        // Low-light: the dim band reads as bright → no dim shading.
        if (lowLightActive) brightRegions = allVisible
        // Darkvision: seen fully regardless of light (and never dim-shaded). MUST
        // union — darkvision overlaps the lit regions, and renderDarkness cuts all
        // holes in one beginHole/endHole, so overlapping (non-disjoint) holes XOR
        // and cancel. Union merges them into disjoint regions first.
        if (darkvisionPolys.length > 0) {
          allVisible = unionVisionPolygonsMulti([...allVisible, ...darkvisionPolys])
          brightRegions = unionVisionPolygonsMulti([...brightRegions, ...darkvisionPolys])
        }
      }

      // Darkvision greyscale reveal: show the desaturated map in the UNLIT part
      // of the darkvision LoS (darkvision polys minus the lit extent). Lit areas
      // fall through to the colour sprite below, so they stay normal.
      const dvGrey = darkvisionGreyRef.current
      const dvMask = darkvisionGreyMaskRef.current
      if (dvGrey && dvMask) {
        if (darkvisionPolys.length > 0) {
          dvMask.clear()
          dvMask.beginFill(0xffffff)
          for (const poly of darkvisionPolys) {
            if (poly.length < 3) continue
            dvMask.moveTo(poly[0].x, poly[0].y)
            for (let i = 1; i < poly.length; i++) dvMask.lineTo(poly[i].x, poly[i].y)
            dvMask.closePath()
          }
          // Punch out the lit regions so colour shows there, greyscale only in dark.
          dvMask.beginHole()
          for (const poly of litExtent) {
            if (poly.length < 3) continue
            dvMask.moveTo(poly[0].x, poly[0].y)
            for (let i = 1; i < poly.length; i++) dvMask.lineTo(poly[i].x, poly[i].y)
            dvMask.closePath()
          }
          dvMask.endHole()
          dvMask.endFill()
          dvGrey.visible = true
        } else {
          dvGrey.visible = false
        }
      }

      // 4. Opacity. GM authoring is dimmed (0.4) so the whole map stays editable.
      //    For players (and the GM previewing a token), unlit areas are FULLY
      //    hidden when lighting is on (1.0) — you can't see what isn't lit — and
      //    fall back to the legacy LoS dim (0.7) when only LoS is active.
      const darkAlpha = asGM ? 0.4 : lightingEnabled ? 1.0 : 0.7
      renderDarkness(darknessLayer, allVisible, mapWidthPx, mapHeightPx, darkAlpha)

      // 5. Dim band: shade everything EXCEPT the bright regions by a mid alpha.
      //    Over the dim ring → half-lit; over already-dark unlit → no-op. Skipped
      //    when no light has a dim band wider than its bright (brightRegions
      //    covers the whole lit extent), so v1-style lights show no extra shade.
      const dimLayer = dimLayerRef.current
      if (dimLayer && lightingEnabled && allVisible.length > 0) {
        const DIM_SHADE_ALPHA = asGM ? 0.2 : 0.5
        renderDarkness(dimLayer, brightRegions, mapWidthPx, mapHeightPx, DIM_SHADE_ALPHA)
      }

      // 6. Colour tint + falloff (additive, below the darkness layers so the
      //    darkness still handles occlusion). White lights are neutral (no wash);
      //    coloured lights tint their bright disc, and the dim ring in proportion
      //    to falloff%. Per-light because colours differ; only non-white lights
      //    pay the extra raycasts.
      const tintLayer = lightTintLayerRef.current
      if (tintLayer && lightingEnabled) {
        const TINT_BRIGHT_ALPHA = 0.3
        const hexToInt = (hex: string) =>
          parseInt(hex.replace('#', '').slice(0, 6), 16)
        const fillPoly = (
          poly: Array<{ x: number; y: number }>,
          color: number,
          alpha: number,
        ) => {
          if (alpha <= 0 || poly.length < 3) return
          tintLayer.beginFill(color, alpha)
          tintLayer.moveTo(poly[0].x, poly[0].y)
          for (let i = 1; i < poly.length; i++) tintLayer.lineTo(poly[i].x, poly[i].y)
          tintLayer.closePath()
          tintLayer.endFill()
        }
        for (const ls of lightSources) {
          if (!ls.color || ls.color.toLowerCase() === '#ffffff') continue
          const color = hexToInt(ls.color)
          if (Number.isNaN(color)) continue
          const bright = ls.brightRadius ?? ls.radius
          const dim = Math.max(ls.dimRadius ?? ls.radius, bright)
          // Tint covers the WHOLE lit area. Falloff governs the fade from the
          // bright edge to the dim edge: 0 = uniform full colour across both
          // bands; 100 = colour concentrated in the bright core, fading to none
          // at the dim edge. The dim fill lays the base tint over the whole
          // extent; the bright fill tops the core back up to full (additive).
          if (dim > bright) {
            const dimAlpha = TINT_BRIGHT_ALPHA * (1 - (ls.falloff ?? 0) / 100)
            const dp = calculateTokenVision(ls.x, ls.y, 1, 1, dim, barriers, map.gridSize)
            fillPoly(dp, color, dimAlpha)
            const bp = calculateTokenVision(ls.x, ls.y, 1, 1, bright, barriers, map.gridSize)
            fillPoly(bp, color, TINT_BRIGHT_ALPHA - dimAlpha)
          } else {
            const bp = calculateTokenVision(ls.x, ls.y, 1, 1, bright, barriers, map.gridSize)
            fillPoly(bp, color, TINT_BRIGHT_ALPHA)
          }
        }
      }
    })
  }, [tokens, barriers, lightSources, map.losEnabled, map.lightingEnabled, map.gridSize, map.width, map.height, visionNeedsRecalculation, visionPolygon, setVisionPolygon, setViewAsVisionPolygon, userRole, previewingVisionFor, renderDarkness])

  /**
   * Render standalone light sources as interactive GM objects (dynamic lighting).
   * Each light is a sun-glyph icon with a radius ring — behaves like a token for
   * the GM: tap to select, drag to move (persisted via light_source_update). No
   * status indicators, no scaling. Players never see these; they see only the
   * lighting effect the darkness layer produces. Shown to the GM when lighting is
   * on, or whenever the light tool is active (so freshly-placed lights appear).
   */
  useEffect(() => {
    const layer = lightSourceLayerRef.current
    if (!layer) return
    layer.removeChildren().forEach((c) => c.destroy({ children: true }))

    if (userRole !== 'gm') return
    if (!map.lightingEnabled && toolMode !== 'light') return

    // Interactive in the light/select tools; inert (display-only) otherwise.
    const interactive = toolMode === 'light' || toolMode === 'select'
    const gs = map.gridSize

    for (const ls of lightSources) {
      const obj = new PIXI.Container()
      obj.x = (ls.x + 0.5) * gs
      obj.y = (ls.y + 0.5) * gs

      // Radius rings — bright (solid inner) + dim (fainter outer). Brighter when
      // this light is the selected one. Dim ring only drawn when wider than bright.
      const isSel = ls.id === selectedLightId
      const brightR = ls.brightRadius ?? ls.radius
      const dimR = Math.max(ls.dimRadius ?? ls.radius, brightR)
      const ring = new PIXI.Graphics()
      if (dimR > brightR) {
        ring.lineStyle(1, 0xffd24a, isSel ? 0.4 : 0.22)
        ring.drawCircle(0, 0, dimR * gs)
      }
      ring.lineStyle(isSel ? 2 : 1, 0xffd24a, isSel ? 0.7 : 0.4)
      ring.drawCircle(0, 0, brightR * gs)
      obj.addChild(ring)

      // Selection halo around the icon.
      if (isSel) {
        const halo = new PIXI.Graphics()
        halo.lineStyle(2, SEL_GOLD, 0.95)
        halo.drawCircle(0, 0, gs * 0.42)
        obj.addChild(halo)
      }

      // Icon: dark disc + sun glyph.
      const disc = new PIXI.Graphics()
      disc.lineStyle(2, 0xffd24a, 1)
      disc.beginFill(0x1a1430, 0.92)
      disc.drawCircle(0, 0, gs * 0.3)
      disc.endFill()
      obj.addChild(disc)
      const glyph = new PIXI.Text('☀', { fontSize: Math.max(12, gs * 0.34), fill: 0xffd24a })
      glyph.anchor.set(0.5)
      obj.addChild(glyph)

      if (interactive) {
        obj.eventMode = 'static'
        obj.cursor = 'grab'
        obj.hitArea = new PIXI.Circle(0, 0, gs * 0.34)

        obj.on('pointerdown', (e: PIXI.FederatedPointerEvent) => {
          // Stop the stage placement handler from spawning a NEW light under us,
          // and the marquee/pan from starting.
          e.stopPropagation()
          lightPointerDownRef.current = true
          setSelectedLightId(ls.id)
          obj.cursor = 'grabbing'

          const app = appRef.current
          const canvas = app?.view as HTMLCanvasElement | undefined
          const res = app?.renderer.resolution ?? 1
          const toWorld = (clientX: number, clientY: number) => {
            if (!canvas) return { x: obj.x, y: obj.y }
            return screenToWorld(clientToScreen(canvas, clientX, clientY, res), viewportRef.current)
          }

          const onMove = (ev: PointerEvent) => {
            const w = toWorld(ev.clientX, ev.clientY)
            obj.x = w.x
            obj.y = w.y
          }
          const onUp = (ev: PointerEvent) => {
            window.removeEventListener('pointermove', onMove)
            window.removeEventListener('pointerup', onUp)
            lightPointerDownRef.current = false
            const w = toWorld(ev.clientX, ev.clientY)
            const gx = Math.max(0, Math.min(map.width - 1, Math.floor(w.x / gs)))
            const gy = Math.max(0, Math.min(map.height - 1, Math.floor(w.y / gs)))
            // Only persist a real move (a plain tap just selects).
            if (gx !== ls.x || gy !== ls.y) {
              updateLightSourceInStore(ls.id, { x: gx, y: gy }) // optimistic
              socketService.updateLightSource(ls.id, { x: gx, y: gy })
            } else {
              // Snap back to the cell center in case of a tiny drag.
              obj.x = (ls.x + 0.5) * gs
              obj.y = (ls.y + 0.5) * gs
            }
          }
          window.addEventListener('pointermove', onMove)
          window.addEventListener('pointerup', onUp)
        })
      } else {
        obj.eventMode = 'none'
      }

      layer.addChild(obj)
    }
  }, [lightSources, selectedLightId, setSelectedLightId, updateLightSourceInStore, map.gridSize, map.width, map.height, map.lightingEnabled, userRole, toolMode])

  /**
   * Render fog of war (Phase 4)
   * Shows black overlay covering entire map, with revealed cells cut out
   */
  useEffect(() => {
    const fogLayer = fogLayerRef.current
    if (!fogLayer) return

    fogLayer.clear()

    // Fog-of-war is always available now — it is its own layer that renders
    // regardless of dynamic LoS and stacks on top of LoS-occluded darkness.
    // In see-as-token mode the GM views as a player, so fog renders opaque
    // (the effective role, not the real one).
    const isGM = effectiveRole === 'gm'

    // GM always sees fog as a light-gray authoring overlay so they can edit the
    // map underneath. For players the opacity is governed by the map's render
    // mode: 'darkness' (default) is fully opaque black that truly hides content;
    // 'dimmed' leaves terrain faintly readable through the fog.
    const renderMode = map.renderMode ?? 'darkness'
    const fogAlpha = isGM ? 0.3 : renderMode === 'dimmed' ? 0.85 : 1
    const fogColor = isGM ? 0x808080 : 0x000000 // Gray for GM, black for players

    // Draw fog in parts - skip revealed cells
    // This is more performant than masking for grid-based fog
    for (let gridY = 0; gridY < map.height; gridY++) {
      for (let gridX = 0; gridX < map.width; gridX++) {
        const key = `${gridX},${gridY}`
        const isRevealed = fog.get(key) === true

        if (!isRevealed) {
          // Draw fog for this cell
          const pixelX = gridX * map.gridSize
          const pixelY = gridY * map.gridSize
          fogLayer.beginFill(fogColor, fogAlpha)
          fogLayer.drawRect(pixelX, pixelY, map.gridSize, map.gridSize)
          fogLayer.endFill()
        }
      }
    }

    console.log(`[VTTCanvas] Rendered fog with ${fog.size} revealed cells (GM mode: ${isGM})`)
  }, [fog, map.gridSize, map.width, map.height, effectiveRole, map.renderMode])

  /**
   * Ruler: clear state on active-map change. Spec section "Map-switch behavior":
   * different maps may have different gridSize, so we MUST drop coords and
   * cancel any pending fade timeout when the map id changes.
   */
  useEffect(() => {
    ruler.cancel()
    // Remote rulers are in world coords for the previous map; a new map may
    // have a different grid, so drop them all on switch.
    clearRemoteRulers()
    // Ping markers are world-coord pulses for the previous map; drop them too.
    clearPings()
    // Intentionally only depend on map.id; ruler.cancel/clearRemoteRulers are
    // stable per render but adding them would re-fire on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map.id])

  /**
   * Ruler: keyboard toggle. R enters ruler mode, Esc exits.
   * Ignores key events while typing in inputs/textareas.
   *
   * Reads ruler state via ref so the listener is attached only once
   * (avoids tear-down/re-attach on every state change).
   */
  const rulerKeyRef = useRef({ isActive: ruler.isActive, setActive: ruler.setActive })
  rulerKeyRef.current = { isActive: ruler.isActive, setActive: ruler.setActive }
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      if (target) {
        const tag = target.tagName
        if (tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable) {
          return
        }
      }
      const key = event.key.toLowerCase()
      if (key === RULER_TOGGLE_KEY) {
        event.preventDefault()
        rulerKeyRef.current.setActive(!rulerKeyRef.current.isActive)
        return
      }
      if (event.key === 'Escape' && rulerKeyRef.current.isActive) {
        event.preventDefault()
        rulerKeyRef.current.setActive(false)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  /**
   * Ruler: poll viewportRef (which is a ref, not state) into local state
   * while the ruler is active so the SVG overlay re-positions on pan/zoom.
   * We only run this loop while active to avoid useless rAF churn.
   */
  useEffect(() => {
    if (!ruler.isActive && !ruler.start) return

    let raf = 0
    const tick = () => {
      const v = viewportRef.current
      setViewportSnapshot(prev => {
        if (prev.scale === v.scale && prev.x === v.x && prev.y === v.y) return prev
        return { scale: v.scale, x: v.x, y: v.y }
      })
      const canvas = appRef.current?.view as HTMLCanvasElement | undefined
      if (canvas) {
        const rect = canvas.getBoundingClientRect()
        setCanvasSize(prev => {
          if (prev.width === rect.width && prev.height === rect.height) return prev
          return { width: rect.width, height: rect.height }
        })
      }
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [ruler.isActive, ruler.start])

  /**
   * Ruler: keep a ref mirror of isDragging + the ruler callbacks so the
   * pointer-event useEffect doesn't tear down + rebuild on every render
   * (which would drop in-flight events). The effect only re-runs when
   * `ruler.isActive` flips or grid params change.
   */
  const rulerStateRef = useRef({
    isDragging: ruler.isDragging,
    beginDrag: ruler.beginDrag,
    updateDrag: ruler.updateDrag,
    endDrag: ruler.endDrag,
  })
  rulerStateRef.current = {
    isDragging: ruler.isDragging,
    beginDrag: ruler.beginDrag,
    updateDrag: ruler.updateDrag,
    endDrag: ruler.endDrag,
  }

  // Mirror the live measurement shape into a ref so the broadcast closure
  // (created inside the ruler effect, not re-run when only the shape
  // changes) always emits the currently-selected template.
  const rulerShapeRef = useRef(ruler.shape)
  rulerShapeRef.current = ruler.shape

  /**
   * Ruler: native pointer-event interception when ruler mode is active.
   *
   * We attach to the canvas DOM element with `capture: true` so we run BEFORE
   * PIXI's stage handlers and can call stopPropagation to prevent the existing
   * fog-brush / barrier / token-drag handlers from firing.
   *
   * CRITICAL — coordinate transform (spec section "Coordinate transform"):
   * the canvas uses PIXI viewport pan + zoom (viewportRef.current = {scale, x, y}).
   * Raw event.clientX/Y is screen space. We convert to canvas-local pixels
   * (subtract canvas bounding rect), then apply the inverse viewport transform
   * to get WORLD coords (the same space token positions live in). All ruler
   * math uses world coords, so the gridSize division is valid at any zoom.
   */
  useEffect(() => {
    if (!ruler.isActive) return
    const canvas = appRef.current?.view as HTMLCanvasElement | undefined
    if (!canvas) return

    const toWorld = (event: PointerEvent | MouseEvent): RulerCoord => {
      // Map CSS px → Pixi SCREEN space (the space viewportRef lives in). On a
      // CSS-scaled canvas (mobile) raw `clientX - rect.left` would diverge from
      // event.global by the CSS→render factor and the ruler would draw offset;
      // clientToScreen applies that factor (a no-op at 1:1 on desktop).
      const a = appRef.current
      const resolution = a?.renderer.resolution ?? 1
      const local = clientToScreen(canvas, event.clientX, event.clientY, resolution)
      // Inverse viewport transform: world = (local - viewportPos) / scale.
      // viewportRef holds the same values applied to app.stage.position/scale,
      // so this is the exact inverse of the forward transform PIXI applies.
      const v = viewportRef.current
      return {
        x: (local.x - v.x) / v.scale,
        y: (local.y - v.y) / v.scale,
      }
    }

    const maybeSnap = (coord: RulerCoord, event: { shiftKey: boolean }): RulerCoord => {
      if (event.shiftKey) return coord // Shift disables snap per spec.
      return snapToGridCenter(coord, map.gridSize, map.gridOffsetX || 0, map.gridOffsetY || 0)
    }

    // Throttle live ruler broadcasts to ~20/sec so a fast drag doesn't
    // flood the socket. begin/end always emit; intermediate moves are
    // rate-limited. `start` is captured at begin and held for the drag.
    let emitStart: RulerCoord | null = null
    let lastEmitMs = 0
    const RULER_EMIT_MS = 50

    const broadcast = (start: RulerCoord, end: RulerCoord, active: boolean) => {
      socketService.emitRulerUpdate({
        mapId: map.id,
        start,
        end,
        active,
        shape: rulerShapeRef.current ?? 'line',
      })
    }

    const onDown = (event: PointerEvent) => {
      // Left button only (mirror existing token-drag conventions).
      if (event.button !== 0) return
      // Guard: only intercept when ruler is actually active. Without this, a
      // brief window exists between ruler deactivation and React effect cleanup
      // where the listener is still attached but ruler mode is already off —
      // swallowing the click before Pixi's token-drag handler can fire.
      if (!rulerKeyRef.current.isActive) return
      event.preventDefault()
      event.stopPropagation()
      const world = maybeSnap(toWorld(event), event)
      rulerStateRef.current.beginDrag(world)
      emitStart = world
      lastEmitMs = Date.now()
      broadcast(world, world, true)
    }

    const onMove = (event: PointerEvent) => {
      if (!rulerStateRef.current.isDragging) return
      event.preventDefault()
      event.stopPropagation()
      const world = maybeSnap(toWorld(event), event)
      rulerStateRef.current.updateDrag(world)
      const now = Date.now()
      if (emitStart && now - lastEmitMs >= RULER_EMIT_MS) {
        lastEmitMs = now
        broadcast(emitStart, world, true)
      }
    }

    const onUp = (event: PointerEvent) => {
      if (!rulerStateRef.current.isDragging) return
      event.preventDefault()
      event.stopPropagation()
      const world = maybeSnap(toWorld(event), event)
      rulerStateRef.current.endDrag(world)
      // Final inactive frame clears the ruler on every peer. The local
      // ruler still fades on its own timer (useRuler), independent of this.
      if (emitStart) broadcast(emitStart, world, false)
      emitStart = null
    }

    // Attach with capture so we beat PIXI's stage handlers.
    canvas.addEventListener('pointerdown', onDown, { capture: true })
    // Move/up on window so we still get events if cursor leaves canvas mid-drag.
    window.addEventListener('pointermove', onMove, { capture: true })
    window.addEventListener('pointerup', onUp, { capture: true })

    return () => {
      canvas.removeEventListener('pointerdown', onDown, { capture: true } as EventListenerOptions)
      window.removeEventListener('pointermove', onMove, { capture: true } as EventListenerOptions)
      window.removeEventListener('pointerup', onUp, { capture: true } as EventListenerOptions)
    }
  }, [ruler.isActive, map.id, map.gridSize, map.gridOffsetX, map.gridOffsetY])

  // Compute whether the ruler overlay should be visible.
  const rulerVisible = ruler.start !== null && ruler.end !== null

  // Programmatic zoom for the on-canvas zoom buttons. Zooms around the
  // canvas centre (cursor-anchored math, like the wheel handler) and keeps
  // viewportRef + the SVG overlay in sync.
  const zoomViewport = (factor: number) => {
    const app = appRef.current
    if (!app) return
    const v = viewportRef.current
    const newScale = Math.max(0.5, Math.min(3, v.scale * factor))
    if (newScale === v.scale) return
    const ratio = newScale / v.scale
    const cx = app.screen.width / 2
    const cy = app.screen.height / 2
    const newX = cx - (cx - v.x) * ratio
    const newY = cy - (cy - v.y) * ratio
    viewportRef.current = { scale: newScale, x: newX, y: newY }
    app.stage.scale.set(newScale)
    app.stage.position.set(newX, newY)
    setViewportSnapshot({ scale: newScale, x: newX, y: newY })
  }
  // Reset returns to scale 1 and RE-CENTERS the map within the viewport
  // (facet #1) rather than snapping to the top-left (0,0). When the map is
  // smaller than the container this puts it dead-center; when larger it's
  // centered with equal overflow on each edge.
  const resetViewport = () => {
    const app = appRef.current
    if (app) {
      const m = mapRef.current
      const offset = computeCenterOffset(
        app.screen.width,
        app.screen.height,
        m.width * m.gridSize,
        m.height * m.gridSize,
        1,
      )
      viewportRef.current = { scale: 1, x: offset.x, y: offset.y }
      app.stage.scale.set(1)
      app.stage.position.set(offset.x, offset.y)
      setViewportSnapshot({ scale: 1, x: offset.x, y: offset.y })
    } else {
      viewportRef.current = { scale: 1, x: 0, y: 0 }
      setViewportSnapshot({ scale: 1, x: 0, y: 0 })
    }
  }

  // Selected-barrier delete affordance. Keyboard Delete/Backspace already
  // removes a selected barrier (see the keydown handler above); this surfaces
  // the same action as a discoverable on-canvas control. GM-gated to match
  // the rest of the barrier tooling (barriers are a GM-only feature).
  const selectedBarrier =
    selectedBarrierId != null
      ? barriers.find((b) => b.id === selectedBarrierId) ?? null
      : null

  // Anchor the control just above the barrier's top-most vertex, in the same
  // panned/zoomed container frame the RulerOverlay uses (world pixels are the
  // canvas-local stage transform: container = world * scale + viewportPos).
  const barrierControlAnchor = (() => {
    if (!selectedBarrier || selectedBarrier.points.length === 0) return null
    let topVertex = selectedBarrier.points[0]
    for (const p of selectedBarrier.points) {
      if (p.y < topVertex.y) topVertex = p
    }
    const worldPixel = gridToPixel(topVertex.x, topVertex.y, map.gridSize)
    const worldX = worldPixel.x + map.gridSize / 2
    const worldY = worldPixel.y + map.gridSize / 2
    return {
      left: worldX * viewportSnapshot.scale + viewportSnapshot.x,
      top: worldY * viewportSnapshot.scale + viewportSnapshot.y,
    }
  })()

  // Reuses the exact keyboard-delete path (removeBarrier + clear selection +
  // onBarrierDelete websocket emit). Do not add a parallel deletion path.
  const handleDeleteSelectedBarrier = () => {
    const id = useVTTStore.getState().selectedBarrierId
    if (!id) return
    removeBarrier(id)
    selectBarrier(null)
    if (onBarrierDeleteRef.current) {
      onBarrierDeleteRef.current(id)
    }
  }

  return (
    // CONTAINER-AS-VIEWPORT: the outer wrapper fills the .canvas-area cell
    // (100% × 100%) instead of sizing itself to the map. The map no longer
    // dictates the layout box — the canvas is a stable viewport and the map
    // pans/zooms inside it.
    <div
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        minHeight: 0,
        // Stop the browser from claiming touch gestures (pinch/pan) over the
        // tabletop so a two-finger pinch reaches the canvas's own zoom handler
        // (handleTouchStart/Move) instead of scrolling/zooming the page.
        touchAction: 'none',
      }}
    >
      <Toolbar
        rulerKeyHint={RULER_TOGGLE_KEY.toUpperCase()}
        userRole={userRole}
      />
      {/* Stable viewport container. Fills the remaining space below the
          toolbar; the ResizeObserver in the init effect drives the renderer
          size off THIS element's box, and the zoom controls below are
          absolutely positioned within it (bottom-right) so they stay put while
          the map pans/zooms — they are NOT children of the transformed PIXI
          stage. */}
      <div
        ref={dropRegionRef}
        style={{
          position: 'relative',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          touchAction: 'none',
          // #494: the frame border/radius/shadow live on THIS wrapper, NOT on the
          // measured canvas container below. A border on the measured element made
          // its content-box 4px shorter than its border-box, so the resize sources
          // (clientHeight vs ResizeObserver borderBoxSize) disagreed and the
          // renderer/canvas ended up sized off a box taller than the visible
          // (overflow-clipped) content — leaving the bottom rows unresponsive to
          // pointer input. With no border on the measured box, every measurement
          // source agrees and the pointer→world mapping covers the whole map.
          border: '2px solid #34495e',
          borderRadius: '4px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
        }}
      >
        <div
          ref={canvasRef}
          style={{
            position: 'absolute',
            inset: 0,
            touchAction: 'none',
            // #494: no border here — this is the measured box (ResizeObserver +
            // clientWidth/Height). Keeping it borderless makes content-box ==
            // border-box so every resize source agrees. Frame moved to the parent.
            cursor: ruler.isActive
              ? 'crosshair'
              : toolMode === 'barrier'
                ? 'crosshair'
                : toolMode === 'fog'
                  ? 'cell'
                  : 'default',
            overflow: 'hidden',
          }}
        />
        {/* Zoom controls \u2014 cross-role. Same pill chrome as the tool cluster,
            absolutely anchored to the bottom-right of the stable viewport
            CONTAINER (facet #2). These live in the DOM overlay, NOT on the
            transformed PIXI stage, so panning/zooming the map never moves or
            hides them. */}
        <div
          style={{
            position: 'absolute',
            bottom: 12,
            right: 12,
            zIndex: 30,
          }}
        >
          <div className="ac-tool-cluster">
            <IconButton
              size="sm"
              variant="ghost"
              title="Zoom in"
              aria-label="Zoom in"
              onClick={() => zoomViewport(1.2)}
            >
              <svg
                width={16}
                height={16}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="7" />
                <line x1="11" y1="8" x2="11" y2="14" />
                <line x1="8" y1="11" x2="14" y2="11" />
                <line x1="16.5" y1="16.5" x2="21" y2="21" />
              </svg>
            </IconButton>
            <IconButton
              size="sm"
              variant="ghost"
              title="Zoom out"
              aria-label="Zoom out"
              onClick={() => zoomViewport(1 / 1.2)}
            >
              <svg
                width={16}
                height={16}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="7" />
                <line x1="8" y1="11" x2="14" y2="11" />
                <line x1="16.5" y1="16.5" x2="21" y2="21" />
              </svg>
            </IconButton>
            <div className="divider" />
            <IconButton
              size="sm"
              variant="ghost"
              title="Reset zoom"
              aria-label="Reset zoom"
              onClick={resetViewport}
            >
              <svg
                width={16}
                height={16}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M3 12a9 9 0 1 0 2.6-6.3L3 8" />
                <path d="M3 3v5h5" />
              </svg>
            </IconButton>
          </div>
        </div>
        <RulerOverlay
          start={ruler.start}
          end={ruler.end}
          gridSize={map.gridSize}
          viewportScale={viewportSnapshot.scale}
          viewportX={viewportSnapshot.x}
          viewportY={viewportSnapshot.y}
          canvasWidth={canvasSize.width || map.width * map.gridSize}
          canvasHeight={canvasSize.height || map.height * map.gridSize}
          visible={rulerVisible}
          shape={measurementShape}
        />
        {/* Remote participants' live rulers (transient socket relay). One
            overlay per peer, colored + labeled by user, scoped to this map. */}
        {Array.from(remoteRulers.values())
          .filter((r) => r.mapId === map.id)
          .map((r) => (
            <RulerOverlay
              key={r.userId}
              start={r.start}
              end={r.end}
              gridSize={map.gridSize}
              viewportScale={viewportSnapshot.scale}
              viewportX={viewportSnapshot.x}
              viewportY={viewportSnapshot.y}
              canvasWidth={canvasSize.width || map.width * map.gridSize}
              canvasHeight={canvasSize.height || map.height * map.gridSize}
              visible={true}
              color={rulerColorForUser(r.userId)}
              label={r.displayName}
              shape={r.shape}
            />
          ))}
        {/* Selected-barrier delete control. Floats above the barrier's top
            vertex and tracks pan/zoom via viewportSnapshot. Reuses the same
            removeBarrier + onBarrierDelete path as the keyboard Delete
            shortcut. GM-gated like the rest of the barrier tooling. */}
        {canUseBarrierTool(userRole) && barrierControlAnchor && (
          <div
            style={{
              position: 'absolute',
              left: barrierControlAnchor.left,
              top: barrierControlAnchor.top,
              transform: 'translate(-50%, calc(-100% - 8px))',
              zIndex: 40,
              pointerEvents: 'auto',
            }}
          >
            <div className="ac-tool-cluster">
              <IconButton
                size="sm"
                variant="destructive"
                title="Delete barrier (or press Delete)"
                aria-label="Delete selected barrier"
                onClick={handleDeleteSelectedBarrier}
              >
                <svg
                  width={16}
                  height={16}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M3 6h18" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                  <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
              </IconButton>
            </div>
          </div>
        )}
        {/* On-canvas HP editor (issue #135). Lives inside the viewport box so
            its canvas-pixel anchor lines up with the clicked HP pill. */}
        {hpEditState && (
          <HpEditPopover
            position={hpEditState.position}
            current={hpEditState.current}
            max={hpEditState.max}
            onSubmit={(change) => onHpEdit?.(hpEditState.characterId, change, hpEditState.tokenId)}
            onClose={() => setHpEditState(null)}
          />
        )}
      </div>

      {/* Token tooltip on right-click */}
      {tooltipState && (
        <TokenTooltip
          token={tooltipState.token}
          position={tooltipState.position}
          onClose={() => setTooltipState(null)}
          isGM={userRole === 'gm'}
          canEditToken={canMoveToken(tooltipState.token)}
          onDisconnect={
            onTokenDisconnect
              ? (tokenId) => {
                  onTokenDisconnect(tokenId)
                  setTooltipState(null)
                }
              : undefined
          }
          onHpBarVisibilityChange={handleTokenHpBarVisibilityChange}
          onVisibilityChange={handleTokenVisibilityChange}
          onSizeChange={handleTokenSizeChange}
          onRotationChange={handleTokenRotationChange}
          onStatusesChange={handleTokenStatusesChange}
          onFollowChange={handleTokenFollowChange}
          onLightChange={handleTokenLightChange}
          onVisionTypeChange={handleTokenVisionTypeChange}
          onClassificationChange={onClassificationChange}
          otherTokens={tokens
            .filter((t) => t.id !== tooltipState.token.id)
            .map((t) => ({ id: t.id, name: t.name }))}
          characterData={tooltipState.token.characterId && getCharacterData
            ? getCharacterData(tooltipState.token.characterId)
            : null}
        />
      )}

      {/* On-token status picker (lightning grip). Anchored at the grip's screen
          position; multi-select, closes on outside-click. */}
      {statusPopover && (() => {
        const popToken = tokens.find((t) => t.id === statusPopover.tokenId)
        if (!popToken) return null
        return (
          <TokenStatusPopover
            position={statusPopover.position}
            activeStatuses={normalizeStatuses(popToken.statuses)}
            onToggle={(key) => {
              const cur = normalizeStatuses(popToken.statuses)
              const next = cur.includes(key)
                ? cur.filter((k) => k !== key)
                : [...cur, key]
              handleTokenStatusesChange(popToken.id, next)
            }}
            onClose={() => setStatusPopover(null)}
          />
        )
      })()}
    </div>
  )
}

/**
 * Draw grid lines on the canvas (Phase 6: with grid configuration)
 *
 * @param graphics - PixiJS Graphics object to draw on
 * @param width - Map width in grid squares
 * @param height - Map height in grid squares
 * @param gridSize - Size of each grid square in pixels
 * @param gridColor - Hex color for grid lines (default: #000000)
 * @param gridOpacity - Opacity 0-1 (default: 0.3)
 * @param gridOffsetX - Horizontal offset in pixels (default: 0)
 * @param gridOffsetY - Vertical offset in pixels (default: 0)
 */
function applyBackgroundTransform(sprite: PIXI.Sprite, map: { width: number; gridSize: number; height: number; backgroundOffsetX?: number; backgroundOffsetY?: number; backgroundScaleX?: number; backgroundScaleY?: number; backgroundFit?: 'stretch' | 'contain' }) {
  const baseW = map.width * map.gridSize
  const baseH = map.height * map.gridSize
  const scaleX = map.backgroundScaleX ?? 1
  const scaleY = map.backgroundScaleY ?? 1
  const offsetX = map.backgroundOffsetX ?? 0
  const offsetY = map.backgroundOffsetY ?? 0

  if ((map.backgroundFit ?? 'stretch') === 'contain') {
    // Preserve the image's native aspect ratio: scale it uniformly so it fits
    // inside the grid bounds (letterboxing the shorter axis), then center it.
    // backgroundScale* act as additional user multipliers and backgroundOffset*
    // as nudges, layered on top of the fit — same controls, different base.
    const texW = sprite.texture?.width || baseW
    const texH = sprite.texture?.height || baseH
    const fit = Math.min(baseW / texW, baseH / texH)
    const drawW = texW * fit * scaleX
    const drawH = texH * fit * scaleY
    sprite.width = drawW
    sprite.height = drawH
    sprite.x = (baseW - drawW) / 2 + offsetX
    sprite.y = (baseH - drawH) / 2 + offsetY
    return
  }

  // 'stretch' (default): fill the grid bounds exactly, distorting aspect if the
  // image and map proportions differ.
  sprite.width = baseW * scaleX
  sprite.height = baseH * scaleY
  sprite.x = offsetX
  sprite.y = offsetY
}

/**
 * Repaint the grid-bounds mask and the play-area border to the current grid
 * size. The mask is a filled rect (clips the color background to the play
 * area so anything beyond it shows the greyscale underlay); the border is a
 * stroke at the same rect so the edge of the canvas is obvious.
 */
function paintGridBounds(
  mask: PIXI.Graphics,
  border: PIXI.Graphics,
  baseW: number,
  baseH: number
) {
  mask.clear()
  mask.beginFill(0xffffff).drawRect(0, 0, baseW, baseH).endFill()

  border.clear()
  // Inner-aligned stroke so the line stays inside the play area at any zoom.
  border.lineStyle({ width: 3, color: 0xffffff, alpha: 0.85, alignment: 0 })
  border.drawRect(0, 0, baseW, baseH)
}

function drawGrid(
  graphics: PIXI.Graphics,
  width: number,
  height: number,
  gridSize: number,
  gridColor: string = '#000000',
  gridOpacity: number = 0.3,
  gridOffsetX: number = 0,
  gridOffsetY: number = 0
) {
  // Convert hex color to number
  const colorNum = parseInt(gridColor.replace('#', ''), 16)
  graphics.lineStyle({ width: 1, color: colorNum, alpha: gridOpacity })

  // Apply offsets
  const offsetX = gridOffsetX
  const offsetY = gridOffsetY

  // Draw vertical lines
  for (let x = 0; x <= width; x++) {
    graphics.moveTo(x * gridSize + offsetX, offsetY)
    graphics.lineTo(x * gridSize + offsetX, height * gridSize + offsetY)
  }

  // Draw horizontal lines
  for (let y = 0; y <= height; y++) {
    graphics.moveTo(offsetX, y * gridSize + offsetY)
    graphics.lineTo(width * gridSize + offsetX, y * gridSize + offsetY)
  }
}
